import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import {
  firstValue,
  normalizeBoothKey,
  hallNameKey,
  zoneKeyOf,
  expandHallZoneMaster,
  attachRowsToBooths,
  rollupPossession,
  NO_POSSESSION_RECORD,
  POSSESSION_ORDER,
  buildDeliverableIndex,
  deliverablesForTask,
  otherAreaHallKeys,
} from "./trackerData";
import VenueView from "./VenueView";
import NotifyModal from "./NotifyModal";
import AreaDetailPanel from "./AreaDetailPanel";
import { toast } from "react-toastify";
import { VENUE_HALLS, VENUE_VIEWBOX, venueZoneShape } from "./venueHalls";
import { areaHallsForLayout } from "./layoutAreas";
import "./SvgCanvas.css";

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    'Completed': '#10B981',
    'InProgress': '#3B82F6',
    'Pending': '#FBBF24',
    'Delayed': '#EF4444',
    // White, as an unallocated stall reads on the plan (its fill is near clear).
    'Unallocated': '#FFFFFF',
    // Stall possession (data4) - its own scale, shown only in that mode.
    'NDC Pending': '#FACC15',        // yellow
    'NDC Issued': '#86EFAC',         // light green
    'Possession Pending': '#F97316', // orange
    'Stall Possession': '#10B981',   // dark green
    'No Show': '#EF4444',
    'No Record': '#E5E7EB',
  };
  return colors[status] || '#9CA3AF';
};

const STATUS_LEGEND = [
  { status: 'Completed', color: '#10B981' },
  { status: 'InProgress', color: '#3B82F6' },
  { status: 'Pending', color: '#FBBF24' },
  { status: 'Delayed', color: '#EF4444' },
  { status: 'Unallocated', color: '#FFFFFF' },
];

// Legend for the Stall Possession data filter, in the order the process runs.
const POSSESSION_LEGEND = [
  { status: 'NDC Pending', color: '#FACC15' },
  { status: 'NDC Issued', color: '#86EFAC' },
  { status: 'Possession Pending', color: '#F97316' },
  { status: 'Stall Possession', color: '#10B981' },
  { status: 'No Show', color: '#EF4444' },
  { status: 'No Record', color: '#E5E7EB' },
];

const DIMMED_DOT_COLOR = '#D1D5DB';

// Zoom a layout opens at (and Reset returns to), at most: 1.99 = 199% on the
// toolbar. A layout whose booths would not fit at that zoom opens at the zoom
// that fits them, with DEFAULT_FIT_MARGIN of breathing room (applyDefaultView).
const DEFAULT_ZOOM = 1.99;
const DEFAULT_FIT_MARGIN = 0.95;

// Full screen: this component covers the viewport at z 1000, below popups
// (react-modal overlays, bootstrap modals and toasts are 9999 app-wide). The
// app's fixed chrome, also 9999, is hidden under this body class (SvgCanvas.css).
const FULLSCREEN_BODY_CLASS = "layout-view-fullscreen";
const FULLSCREEN_ROOT_STYLE = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  backgroundColor: "#ffffff",
  padding: "12px",
  overflow: "auto",
};

// Popup status badges take the legend's colour (getStatusColor), so a badge
// and the legend always agree. Light fills - Pending, Unallocated, No Record -
// get dark text; white on them is unreadable.
const statusBadgeStyle = (status) => {
  const bg = getStatusColor(status);
  const n = parseInt(bg.slice(1), 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return { backgroundColor: bg, color: lum > 0.6 ? '#1f2937' : '#ffffff' };
};

// An exhibitor order's status - one rule for the map and the popup.
const isTrue = (v) => v === 1 || v === true;
const orderStatusOf = (r) => {
  if (isTrue(r.IsVerifyByHallManager) && isTrue(r.IsVerifyByOrganizer)) return "Completed";
  if (r.WorkStatusID === 2 || r.WorkStatusID === true || r.Status === "InProgress" || isTrue(r.IsVerifyByHallManager)) {
    return "InProgress";
  }
  return "Pending";
};

// Stable fallback, so memos keyed on a missing result set do not rerun every render.
const NO_ROWS = [];

// One size for every toolbar control, so the row reads as a single bar.
const TOOLBAR_SELECT_STYLE = { minWidth: '125px', maxWidth: '160px', height: '32px', fontSize: '13px', paddingTop: 0, paddingBottom: 0 };

const DATA_FILTER_OPTIONS = [
  { key: 'service_partner', label: 'Service Partner Task', icon: 'ri-tools-line' },
  { key: 'exhibitor_orders', label: 'Exhibitor Orders', icon: 'ri-shopping-bag-3-line' },
  { key: 'daily_routine', label: 'Daily Routine Task', icon: 'ri-calendar-check-line' },
  // Single select: a different status scale (data4), so it never mixes with
  // the task sources above.
  { key: 'stall_possession', label: 'Stall Possession', icon: 'ri-key-2-line', exclusive: true },
];
const EXCLUSIVE_DATA_FILTERS = DATA_FILTER_OPTIONS.filter((o) => o.exclusive).map((o) => o.key);
const DEFAULT_TASK_SOURCES = ["service_partner", "exhibitor_orders", "daily_routine"];

// Data sources a view can use (views not listed use all). Every view offers
// every source now; Stall View used to be limited to orders + possession.
// Stall Possession is its own mode everywhere.
const VIEW_DATA_SOURCES = {};

// Venue View and Venue Zone View draw the venue drawing, not the hall plan.
const isVenueLike = (view) => view === "venue" || view === "venueZone";

// ─── Status rule: the worst task decides ────────────────────────────────
// A booth, a zone and a hall all read as the highest-priority status among
// their tasks: any Delayed task makes the whole thing Delayed; otherwise any
// task in progress makes it InProgress; otherwise any incomplete (Pending)
// task makes it Pending; only when every task is done is it Completed.
const TASK_STATUS_PRIORITY = ["Delayed", "InProgress", "Pending", "Completed"];
const rollupTaskStatus = (statuses) =>
  TASK_STATUS_PRIORITY.find((s) => statuses.includes(s)) || (statuses.length > 0 ? "Pending" : null);

// StallTaskStatus may come as a name or as a status id.
const STALL_TASK_STATUS = {
  1: "Completed",
  2: "InProgress",
  3: "Pending",
  4: "Delayed",
  5: null, // unallocated - drawn dimmed
};
// One service partner task's status.
const servicePartnerRowStatus = (r) => {
  const raw = firstValue(r.StallTaskStatus);
  if (typeof raw === "string" && raw) return raw;
  if (typeof raw === "number" && STALL_TASK_STATUS[raw]) return STALL_TASK_STATUS[raw];
  return "Pending";
};

// One daily routine row's status - the same rule Hall View rolls up.
const dailyRoutineRowStatus = (r) =>
  r.HallStatus ||
  (r.TotalCompCount === r.TotalCount && r.TotalCount > 0 ? "Completed" : r.TotalInPCount > 0 ? "InProgress" : "Pending");

// Hall (hallNameKey) -> the statuses of its daily routine rows.
const dailyRoutineStatusesByHall = (rows) => {
  const byHall = new Map();
  (rows || []).forEach((r) => {
    const hk = hallNameKey(r.HallName || r.Hall);
    if (!hk) return;
    if (!byHall.has(hk)) byHall.set(hk, []);
    byHall.get(hk).push(dailyRoutineRowStatus(r));
  });
  return byHall;
};


// One place that decides a dot's colour, so the map and the popup can never
// disagree: a hall with no tracker row for the current service is drawn grey
// rather than given a status it does not have.
const paintBoothDot = (circle, status, dimmed) => {
  circle.setAttribute(
    'fill',
    dimmed ? DIMMED_DOT_COLOR : status ? getStatusColor(status) : '#4f46e5'
  );
};

// Border-wise stall highlighting with properly visible fill opacity
const paintBoothArea = (rect, status, dimmed) => {
  const color = dimmed ? DIMMED_DOT_COLOR : status ? getStatusColor(status) : '#4f46e5';
  rect.setAttribute('fill', color);
  rect.setAttribute('fill-opacity', dimmed ? '0.08' : '0.22');
  rect.setAttribute('stroke', color);
  rect.setAttribute('stroke-opacity', dimmed ? 0.35 : 0.95);
  rect.setAttribute('stroke-width', '1.5');
  rect.setAttribute('stroke-linejoin', 'round');
  rect.setAttribute('vector-effect', 'non-scaling-stroke');
};

// ─── Memoized SVG Component ─────────────────────────────────────────
// This prevents React from re-parsing the massive HTML string when SvgCanvas re-renders
const MemoizedSvg = React.memo(React.forwardRef(({ svgContent }, ref) => (
  <div
    ref={ref}
    style={{
      transformOrigin: "0 0",
      width: "100%",
      height: "100%",
    }}
    dangerouslySetInnerHTML={{ __html: svgContent }}
  />
)), (prev, next) => prev.svgContent === next.svgContent);

// "Hall 1" stays "Hall 1", a bare "1" becomes "Hall 1" - names from the SP
// already carry the word, so a fixed prefix read "Hall Hall 1".
const withPrefix = (word, value) => {
  const v = String(value ?? "").trim();
  if (!v) return `${word} --`;
  return v.toLowerCase().startsWith(word.toLowerCase()) ? v : `${word} ${v}`;
};

const normalizeId = (id) => String(id ?? "").trim().toLowerCase();

// sp_get_hallviewauditTracker selects two columns with the same name (the
// exhibitor's and the vendor's), and the driver collapses each pair into an
// array: CompanyName: ["Acme Looms", "Grade Electricals"], MobileNo likewise.
// Index 0 is the exhibitor, index 1 the vendor. Scalars come back from the
// first recordset, where only the vendor column is selected.
// sp_get_hallviewauditTracker now names the exhibitor's and the vendor's columns
// apart, so nothing collapses into an array. The array unwrapping below is kept
// so the screen still reads a server that has not been updated yet.
const exhibitorField = (row, named, legacy) =>
  row[named] != null ? row[named] : firstValue(row[legacy]);
const vendorField = (row, named, legacy) =>
  row[named] != null ? row[named] : lastValue(row[legacy]);
const lastValue = (v) => {
  if (!Array.isArray(v)) return v;
  for (let i = v.length - 1; i >= 0; i--) {
    if (v[i] != null && v[i] !== "") return v[i];
  }
  return undefined;
};




// normalizeBoothKey (the drawing/database join key) lives in trackerData.js.

const buildSvgIndex = (svgElement) => {
  const elementsByClip = new Map();
  const clipPathDefsById = new Map();
  const elementsByBoothName = new Map();
  const textElementsByContent = new Map();

  try {
    svgElement.querySelectorAll("clipPath[id]").forEach((cp) => {
      const id = normalizeId(cp.getAttribute("id"));
      if (id) clipPathDefsById.set(id, cp);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("[data-booth-name]").forEach((el) => {
      const name = normalizeId(el.getAttribute("data-booth-name"));
      if (!name) return;
      if (!elementsByBoothName.has(name)) elementsByBoothName.set(name, []);
      elementsByBoothName.get(name).push(el);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("text, tspan").forEach((el) => {
      const txt = normalizeId(el.textContent);
      if (!txt) return;
      if (!textElementsByContent.has(txt)) textElementsByContent.set(txt, []);
      textElementsByContent.get(txt).push(el);
    });
  } catch (e) { }

  const refRegex = /url\(\s*["']?#([^"')]+)["']?\s*\)/i;
  try {
    const candidates = svgElement.querySelectorAll('[clip-path], [style*="clip-path"], [style*="clip-Path"]');
    candidates.forEach((el) => {
      try {
        let raw = el.getAttribute("clip-path");
        if (!raw) {
          const styleAttr = el.getAttribute("style") || "";
          const m = styleAttr.match(/clip-path\s*:\s*([^;]+)/i);
          raw = m ? m[1] : null;
        }
        if (!raw) return;
        const match = raw.match(refRegex);
        if (!match) return;
        const id = normalizeId(match[1]);
        if (!id) return;
        if (!elementsByClip.has(id)) elementsByClip.set(id, []);
        elementsByClip.get(id).push(el);
      } catch (e) { }
    });
  } catch (e) { }

  return { elementsByClip, clipPathDefsById, elementsByBoothName, textElementsByContent };
};

// ─── Layout extraction from the drawing itself ──────────────────────────
// The layout is a PDF export: booth codes live in <text> nodes that carry
// fill-opacity="0" and sit invisibly on top of the vector glyphs. They are the
// only trustworthy booth anchors in the file - the API's ClipId values are
// sequential placeholders that mostly do not exist in the SVG, and the handful
// that do are full-canvas layer clips.
const BOOTH_LABEL_RE = /^H(\d+)\s*-\s*([A-Za-z0-9]+)$/i;
const HALL_TITLE_RE = /^HALLS?\s+(\d+)$/i;
// Groups we inject ourselves - never re-read them as layout content.
const OVERLAY_IDS = "#booth-focus, #booth-nodes, #booth-hits, #route-path";

const cleanText = (el) => (el.textContent || "").replace(/\s+/g, " ").trim();

// "H1-A10" must sort after "H1-A9", not between "H1-A1" and "H1-A2".
const naturalCompare = (a, b) => {
  const ax = String(a).match(/(\d+|\D+)/g) || [];
  const bx = String(b).match(/(\d+|\D+)/g) || [];
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const av = ax[i];
    const bv = bx[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (/^\d/.test(av) && /^\d/.test(bv)) {
      const diff = Number(av) - Number(bv);
      if (diff !== 0) return diff;
    } else if (av !== bv) {
      return av < bv ? -1 : 1;
    }
  }
  return 0;
};

const isOverlayNode = (el) => {
  try {
    return !!(el.closest && el.closest(OVERLAY_IDS));
  } catch (e) {
    return false;
  }
};

const extractSvgLayoutData = (svgElement) => {
  const elementsByBooth = new Map();
  const hallTitles = new Map();
  const boothsByName = new Map();

  let textNodes = [];
  try {
    textNodes = Array.from(svgElement.querySelectorAll("text"));
  } catch (e) {
    return { halls: [], booths: [], elementsByBooth };
  }

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const m = cleanText(el).match(HALL_TITLE_RE);
    if (m) hallTitles.set(m[1], `Hall ${m[1]}`);
  });

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const raw = cleanText(el);
    const m = raw.match(BOOTH_LABEL_RE);
    if (!m) return;

    const hallNo = m[1];
    const name = raw.toUpperCase().replace(/\s*-\s*/, "-");
    const hallId = `H${hallNo}`;

    if (!elementsByBooth.has(name)) elementsByBooth.set(name, []);
    elementsByBooth.get(name).push(el);

    if (!boothsByName.has(name)) {
      boothsByName.set(name, {
        BoothID: name,
        BoothName: name,
        HallID: hallId,
        HallName: hallTitles.get(hallNo) || `Hall ${hallNo}`,
        Block: (m[2].match(/^[A-Za-z]+/) || [""])[0].toUpperCase(),
      });
    }

    // Let the existing hover/click delegation find these. The nodes are
    // unpainted, so they also need pointer-events turned on (done in CSS).
    el.setAttribute("data-booth-name", name);
    el.setAttribute("data-hall-id", hallId);
  });

  const booths = Array.from(boothsByName.values()).sort((a, b) =>
    naturalCompare(a.BoothName, b.BoothName)
  );

  const hallMap = new Map();
  booths.forEach((b) => {
    if (!hallMap.has(b.HallID)) {
      hallMap.set(b.HallID, { HallID: b.HallID, HallName: b.HallName, BoothCount: 0 });
    }
    hallMap.get(b.HallID).BoothCount++;
  });
  const halls = Array.from(hallMap.values()).sort((a, b) =>
    naturalCompare(a.HallID, b.HallID)
  );

  return { halls, booths, elementsByBooth };
};

// Matrix from an element's own user space into the <svg> root's USER space,
// i.e. viewBox units - the space our overlays and the nav grid are expressed in.
//
// getCTM() is deliberately not used: it maps to the nearest *viewport*, which
// includes the viewBox->viewport transform. That is identity only while the svg
// renders at its intrinsic size; once it scales to its container the results
// come back in CSS pixels and every overlay lands in the wrong place. Composing
// root.getScreenCTM()^-1 with el.getScreenCTM() cancels the viewport transform
// (and any ancestor CSS transform) and leaves pure user units.
const matrixToSvgUserSpace = (el) => {
  try {
    const root = el.ownerSVGElement;
    if (root && typeof root.getScreenCTM === "function" && typeof el.getScreenCTM === "function") {
      const rootCTM = root.getScreenCTM();
      const elCTM = el.getScreenCTM();
      if (rootCTM && elCTM) return rootCTM.inverse().multiply(elCTM);
    }
  } catch (e) { }
  try {
    return typeof el.getCTM === "function" ? el.getCTM() : null;
  } catch (e) {
    return null;
  }
};

// getBBox() reports the element's own user space; map it into root user space.
const elementBoxInSvgSpace = (el) => {
  try {
    const bbox = el.getBBox();
    if (!(bbox.width > 0 || bbox.height > 0)) return null;
    const ctm = matrixToSvgUserSpace(el);
    if (!ctm) {
      return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
    }
    const pts = [
      [bbox.x, bbox.y],
      [bbox.x + bbox.width, bbox.y],
      [bbox.x, bbox.y + bbox.height],
      [bbox.x + bbox.width, bbox.y + bbox.height],
    ].map(([x, y]) => ({
      x: ctm.a * x + ctm.c * y + ctm.e,
      y: ctm.b * x + ctm.d * y + ctm.f,
    }));
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  } catch (e) {
    return null;
  }
};

const unionBoxes = (boxes) => {
  const valid = boxes.filter(Boolean);
  if (valid.length === 0) return null;
  const minX = Math.min(...valid.map((b) => b.x));
  const minY = Math.min(...valid.map((b) => b.y));
  const maxX = Math.max(...valid.map((b) => b.x + b.width));
  const maxY = Math.max(...valid.map((b) => b.y + b.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

// Each drawn hall's box for Hall / Zone View: the box around its stalls, then
// the halls that stand side by side (overlapping vertically by more than half)
// given one top and one bottom. A hall's stalls rarely touch its walls - Hall 1
// has stalls on its back wall, Halls 3 / 5 / 7 do not - so per-hall boxes came
// out at different heights for halls that share the same floor.
// Only halls of similar height are aligned - the overlap must cover most of
// the TALLER one. Measured against the shorter, Layout 3's tall Hall 16 column
// "overlapped" each of Halls 17 / 15 / 14 stacked beside it and stretched all
// four to the full sheet height.
//
// pad grows each box first (Hall View keeps its edge off the outer stalls'
// borders), so the overlap and gap passes below see the boxes as drawn.
const HALL_GAP_CLOSE = 40; // units; a corridor narrower than this between two halls is shared out
// A hall is not always a rectangle (Layout 3: Hall 17 is an L - its top-right
// corner is outside the hall). An empty corner this big is cut out of the box.
const HALL_NOTCH_MIN_SIDE = 0.1;  // of the box's width / height, each side
const HALL_NOTCH_MIN_AREA = 0.03; // of the box's area

// The largest rectangle in each corner of box that holds none of the stall
// boxes, kept when it is big enough to be a real corner of the floor rather
// than the slack around the outer stalls.
const hallNotchesFor = (box, stallBoxes) => {
  const notches = [];
  const W = box.width;
  const H = box.height;
  [[1, 1], [-1, 1], [1, -1], [-1, -1]].forEach(([sx, sy]) => {
    // Corner-relative coordinates: u grows away from the corner's vertical
    // side, v away from its horizontal side.
    const ox = sx > 0 ? box.x : box.x + W;
    const oy = sy > 0 ? box.y : box.y + H;
    const rel = stallBoxes.map((b) => {
      const u = sx > 0 ? [b.x - ox, b.x + b.width - ox] : [ox - (b.x + b.width), ox - b.x];
      const v = sy > 0 ? [b.y - oy, b.y + b.height - oy] : [oy - (b.y + b.height), oy - b.y];
      return { u0: Math.max(0, u[0]), v0: Math.max(0, v[0]) };
    });
    let best = null;
    const candidates = Array.from(new Set(rel.map((r) => r.u0).concat(W))).filter((u) => u > 0);
    candidates.forEach((U) => {
      let V = H;
      rel.forEach((r) => { if (r.u0 < U && r.v0 < V) V = r.v0; });
      if (V <= 0) return;
      if (!best || U * V > best.U * best.V) best = { U, V };
    });
    if (!best) return;
    const { U, V } = best;
    if (U >= W - 0.5 && V >= H - 0.5) return; // no stalls at all
    if (U < W * HALL_NOTCH_MIN_SIDE || V < H * HALL_NOTCH_MIN_SIDE || U * V < W * H * HALL_NOTCH_MIN_AREA) return;
    notches.push({
      x: sx > 0 ? box.x : box.x + W - U,
      y: sy > 0 ? box.y : box.y + H - V,
      width: U,
      height: V,
    });
  });
  return notches;
};

// SVG path of rect minus the notches that reach into it, traced on the uneven
// grid their edges make (cellsToLoops). Null when nothing is cut away.
const rectMinusNotchesPath = (rect, notches) => {
  const cuts = (notches || []).filter((n) => overlapArea(n, rect) > 0.25);
  if (cuts.length === 0) return null;
  const within = (v, lo, hi) => v > lo && v < hi;
  const xs = Array.from(new Set([rect.x, rect.x + rect.width].concat(
    cuts.flatMap((n) => [n.x, n.x + n.width]).filter((v) => within(v, rect.x, rect.x + rect.width))
  ))).sort((a, b) => a - b);
  const ys = Array.from(new Set([rect.y, rect.y + rect.height].concat(
    cuts.flatMap((n) => [n.y, n.y + n.height]).filter((v) => within(v, rect.y, rect.y + rect.height))
  ))).sort((a, b) => a - b);
  const cols = xs.length - 1;
  const cells = [];
  for (let r = 0; r < ys.length - 1; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const cx = (xs[c] + xs[c + 1]) / 2;
      const cy = (ys[r] + ys[r + 1]) / 2;
      const cut = cuts.some((n) => cx > n.x && cx < n.x + n.width && cy > n.y && cy < n.y + n.height);
      if (!cut) cells.push(r * cols + c);
    }
  }
  if (cells.length === 0) return null;
  return cellsToLoops(cells, cols)
    .map((loop) => `M${loop.map(([c, r]) => `${xs[c]},${ys[r]}`).join("L")}Z`)
    .join("");
};

const hallBoxesFor = (halls, booths, getBoothBox, pad = 0) => {
  const boxes = new Map();
  const stallBoxes = new Map();
  (halls || []).forEach((h) => {
    const own = booths.filter((b) => b.HallID === h.HallID).map((b) => getBoothBox(b.BoothName)).filter(Boolean)
      .map((b) => ({ x: b.x - pad, y: b.y - pad, width: b.width + pad * 2, height: b.height + pad * 2 }));
    const box = unionBoxes(own);
    if (box) {
      boxes.set(h.HallID, box);
      stallBoxes.set(h.HallID, own);
    }
  });
  const aligned = new Map();
  boxes.forEach((b, id) => {
    let top = b.y;
    let bottom = b.y + b.height;
    boxes.forEach((o) => {
      const overlap = Math.min(b.y + b.height, o.y + o.height) - Math.max(b.y, o.y);
      if (overlap > 0.5 * Math.max(b.height, o.height)) {
        top = Math.min(top, o.y);
        bottom = Math.max(bottom, o.y + o.height);
      }
    });
    aligned.set(id, { x: b.x, y: top, width: b.width, height: bottom - top });
  });

  const list = Array.from(aligned.values());

  // Halls whose floors interlock (Layout 3: Hall 17's strip runs on past the
  // top of Hall 16's column) have overlapping stall boxes. Two status
  // rectangles drawn over each other read as one muddle, so each overlap is
  // split down its middle across its thinner side and the halls meet edge to
  // edge instead.
  const splitOverlaps = () => {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const p = list[i];
        const q = list[j];
        const ox = Math.min(p.x + p.width, q.x + q.width) - Math.max(p.x, q.x);
        const oy = Math.min(p.y + p.height, q.y + q.height) - Math.max(p.y, q.y);
        if (ox <= 0 || oy <= 0) continue;
        if (ox <= oy) {
          const [l, r] = p.x <= q.x ? [p, q] : [q, p];
          const mid = Math.max(l.x, r.x) + ox / 2;
          const rRight = r.x + r.width;
          l.width = Math.min(l.width, mid - l.x);
          r.x = Math.max(r.x, mid);
          r.width = rRight - r.x;
        } else {
          const [t, u] = p.y <= q.y ? [p, q] : [q, p];
          const mid = Math.max(t.y, u.y) + oy / 2;
          const uBottom = u.y + u.height;
          t.height = Math.min(t.height, mid - t.y);
          u.y = Math.max(u.y, mid);
          u.height = uBottom - u.y;
        }
      }
    }
  };
  splitOverlaps();

  // Neighbouring halls with a narrow corridor between them (Layout 3: Halls
  // 15 / 14 and Hall 16, Hall 17 and Hall 15) left that corridor unmarked down
  // the middle of the sheet. Each such gap is closed: both halls move to its
  // middle, or - when one of them would then run into a third hall - the
  // other one goes all the way across. Narrowest gaps first.
  const span = (p, q, axis) => (axis === "x"
    ? Math.min(p.y + p.height, q.y + q.height) - Math.max(p.y, q.y)
    : Math.min(p.x + p.width, q.x + q.width) - Math.max(p.x, q.x));
  const clash = (box, except) => list.some((o) => !except.includes(o) &&
    Math.min(box.x + box.width, o.x + o.width) - Math.max(box.x, o.x) > 0.5 &&
    Math.min(box.y + box.height, o.y + o.height) - Math.max(box.y, o.y) > 0.5);
  const gaps = [];
  list.forEach((p) => list.forEach((q) => {
    if (p === q) return;
    const gx = q.x - (p.x + p.width); // q right of p
    if (gx > 0 && gx <= HALL_GAP_CLOSE && span(p, q, "x") > 0) gaps.push({ p, q, axis: "x", gap: gx });
    const gy = q.y - (p.y + p.height); // q below p
    if (gy > 0 && gy <= HALL_GAP_CLOSE && span(p, q, "y") > 0) gaps.push({ p, q, axis: "y", gap: gy });
  }));
  gaps.sort((g1, g2) => g1.gap - g2.gap).forEach(({ p, q, axis }) => {
    const pos = axis === "x" ? "x" : "y";
    const size = axis === "x" ? "width" : "height";
    const pEnd = p[pos] + p[size];
    const gap = q[pos] - pEnd;
    if (gap <= 0) return; // closed meanwhile
    // The corridor itself must be empty - never stretch across another hall.
    const corridor = axis === "x"
      ? { x: pEnd, y: Math.max(p.y, q.y), width: gap, height: span(p, q, "x") }
      : { x: Math.max(p.x, q.x), y: pEnd, width: span(p, q, "y"), height: gap };
    if (clash(corridor, [p, q])) return;
    const grownP = (to) => ({ ...p, [size]: to - p[pos] });
    const grownQ = (to) => ({ ...q, [pos]: to, [size]: q[pos] + q[size] - to });
    const mid = pEnd + gap / 2;
    const options = [[mid, mid], [q[pos], q[pos]], [pEnd, pEnd]];
    const fit = options.find(([pTo, qTo]) => !clash(grownP(pTo), [p, q]) && !clash(grownQ(qTo), [p, q]));
    if (!fit) return;
    const qEnd = q[pos] + q[size];
    p[size] = fit[0] - p[pos];
    q[pos] = fit[1];
    q[size] = qEnd - q[pos];
  });
  splitOverlaps();

  // Empty corners cut out of the final box: hallBox.notches (for clipping
  // zone cells) and hallBox.path (the hall's outline; null for a rectangle).
  aligned.forEach((box, id) => {
    box.notches = hallNotchesFor(box, stallBoxes.get(id) || []);
    box.path = rectMinusNotchesPath(box, box.notches);
  });
  return aligned;
};

// Zone View cells. A zone's stalls are not a clean region of the drawing -
// big stands straddle the hall's midlines, so boxes drawn around each zone's
// stalls overlap. Instead the hall's box is cut into equal cells, one per zone,
// and each zone gets the cell nearest its OWN stalls' area-weighted centre.
//
// The grid is chosen the same way: every split (1 x n, 2 x .., n x 1) is
// tried and the one whose cell centres sit closest to their zones' centres
// wins. Picking the squarest grid instead cut a tall hall top / bottom even
// when its zones run left / right (Hall 5: the J column is Registration Area,
// the H column Zone 1). Scoring by stall area inside each cell failed the other
// way: Hall 1's centre stands straddle the vertical midline, so any left /
// right split cut them in half and its quadrant zones came out as bands.
const overlapArea = (a, b) =>
  Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));

const gridCells = (box, cols, rows) => {
  const w = box.width / cols;
  const h = box.height / rows;
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      cells.push({ x: box.x + c * w, y: box.y + r * h, width: w, height: h });
    }
  }
  return cells;
};

// The assignment of zones to cells that puts zone centres closest to cell
// centres, distances measured in cell widths / heights - measured in hall
// units, a grid with spare cells always won just by having smaller cells
// (ties go to the most stall area inside the cell). Exhaustive for a handful
// of zones; beyond that, greedy.
const assignZonesToCells = (zones, cells, hallBox) => {
  const hallArea = Math.max(hallBox.width * hallBox.height, 1);
  const score = (z, c) => {
    const dx = (z.centre.x - (c.x + c.width / 2)) / Math.max(c.width, 1);
    const dy = (z.centre.y - (c.y + c.height / 2)) / Math.max(c.height, 1);
    const area = z.boxes.reduce((sum, bx) => sum + overlapArea(bx, c), 0) / hallArea;
    return -(dx * dx + dy * dy) + area * 1e-3;
  };
  if (zones.length > 7) {
    const free = cells.slice();
    const picked = zones.map((z) => {
      let bi = 0;
      free.forEach((c, i) => { if (score(z, c) > score(z, free[bi])) bi = i; });
      return free.splice(bi, 1)[0];
    });
    return { cells: picked, score: zones.reduce((t, z, i) => t + score(z, picked[i]), 0) };
  }
  let best = null;
  const taken = new Array(cells.length).fill(false);
  const pick = [];
  const walk = (i, total) => {
    if (i === zones.length) {
      if (!best || total > best.score) best = { score: total, pick: pick.slice() };
      return;
    }
    for (let c = 0; c < cells.length; c++) {
      if (taken[c]) continue;
      taken[c] = true;
      pick.push(c);
      walk(i + 1, total + score(zones[i], cells[c]));
      pick.pop();
      taken[c] = false;
    }
  };
  walk(0, 0);
  return best ? { cells: best.pick.map((c) => cells[c]), score: best.score } : { cells: [], score: -Infinity };
};

// Cost of each spare cell, in the same squared-cell-units as a zone's offset.
// Without it four zones picked 3 x 2 (smaller cells sit closer to any centre);
// at 1 five zones in a clear 3 + 2 layout collapsed into one row.
const ZONE_EMPTY_CELL_PENALTY = 0.5;

// zones: [{ boxes, centre }] -> the cell for each zone, in the same order.
const layoutZoneCells = (hallBox, zones) => {
  const n = zones.length;
  if (!hallBox || n === 0) return [];
  let best = null;
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols);
    if (cols * rows - n >= cols) continue; // a whole empty row
    const fit = assignZonesToCells(zones, gridCells(hallBox, cols, rows), hallBox);
    fit.score -= ZONE_EMPTY_CELL_PENALTY * (cols * rows - n);
    const skew = Math.abs(Math.log(hallBox.width / cols / (hallBox.height / rows)));
    if (!best || fit.score > best.score + 1e-6 || (Math.abs(fit.score - best.score) <= 1e-6 && skew < best.skew)) {
      best = { ...fit, skew };
    }
  }
  return best ? best.cells : [];
};

// The stall's own border, read off the drawing.
//
// There is no stall rectangle to look up. The layout is a PDF export and the
// borders are DASHED, so each one reaches the file as a few dozen unconnected
// dash segments - no element's box is a stall, which is why matching shapes by
// enclosure picks up whatever larger panel happens to contain the label.
//
// The border is still in the file, just as ink rather than as an object. And
// the nav grid already proved what that ink forms: rasterise it and every stall
// closes into a sealed pocket (150/150 - see LAYOUT_VIEW_CONTEXT.md). So the
// stall's exact extent is the pocket its label sits in. Flood-fill from the
// label, take the bounds of the region that comes back, and that is the border.
//
// Two details make it work at a cell fine enough to trace a border with:
//
//   dilate    at 0.75 units a dash GAP is wider than a cell, so a raw fill
//             leaks straight out into the aisle. Growing the ink by
//             OUTLINE_DILATE cells bridges gaps up to 3 units and seals the
//             stall - the same sealing the nav grid gets for free from its
//             coarse 2-unit cell.
//   snap      that dilation would then swell every box by a fixed margin, so
//             each edge of the filled region is walked back out to the first
//             row/column carrying real, undilated ink. The box lands on the
//             drawn line rather than on the dilation, which holds the error at
//             half a unit whatever the dash pattern.
//
// Rasterised at OUTLINE_CELL and kept for the overlay only; routing is untouched.
const OUTLINE_CELL = 0.75;         // SVG user units per cell
const OUTLINE_MAX_AREA = 0.03;     // a pocket bigger than this share of the sheet is the aisle, not a stall
const OUTLINE_DILATE = 2;          // cells of ink growth, to bridge the gaps between dashes
const OUTLINE_SNAP_REACH = 5;      // how far back out to hunt for the real line (OUTLINE_DILATE + 3)
const OUTLINE_SIZE_TOLERANCE = 0.2; // how far a trace may stand off the drawing's own scale
const OUTLINE_SIMPLIFY = 1.6;       // cells of slack when straightening a rasterised diagonal
const ZONE_CLUB_MAX_RADIUS = 60;    // cells (45 units); how far a zone will reach to join its stalls up - cheap now that closing is scoped to the zone's own bounding box

// Traces a set of grid cells into SVG path loops by cancelling shared edges:
// an edge between two cells of the same region appears twice, in opposite
// directions, and drops out - what survives is the boundary. Whatever shape the
// flood fill found comes out exactly, so a pentagon, an L or a stall with a
// pillar in it are no harder than a rectangle, which is just the simple case.
//
// A hole traces as its own loop with the opposite winding, so evenodd fill
// renders it correctly without any extra work.
const cellsToLoops = (cells, cols) => {
  const edges = new Map();
  const key = (a, b, c, d) => `${a},${b}:${c},${d}`;
  const add = (x1, y1, x2, y2) => {
    const rev = key(x2, y2, x1, y1);
    if (edges.has(rev)) { edges.delete(rev); return; } // interior edge, cancels
    edges.set(key(x1, y1, x2, y2), [x1, y1, x2, y2]);
  };
  cells.forEach((i) => {
    const r = (i / cols) | 0;
    const c = i - r * cols;
    add(c, r, c + 1, r);
    add(c + 1, r, c + 1, r + 1);
    add(c + 1, r + 1, c, r + 1);
    add(c, r + 1, c, r);
  });

  const from = new Map();
  edges.forEach(([x1, y1, x2, y2]) => {
    const k = `${x1},${y1}`;
    if (!from.has(k)) from.set(k, []);
    from.get(k).push([x2, y2]);
  });

  const loops = [];
  const used = new Set();
  edges.forEach(([x1, y1, x2, y2]) => {
    const start = key(x1, y1, x2, y2);
    if (used.has(start)) return;
    used.add(start);
    const pts = [[x1, y1]];
    let px = x1, py = y1, cx = x2, cy = y2;
    while (!(cx === x1 && cy === y1)) {
      pts.push([cx, cy]);
      const outs = from.get(`${cx},${cy}`) || [];
      const dx = cx - px, dy = cy - py;
      let next = null;
      for (let i = 0; i < outs.length; i += 1) {
        const [nx, ny] = outs[i];
        if (used.has(key(cx, cy, nx, ny))) continue;
        // Prefer carrying straight on, so a pinch point is not cut across.
        if (nx - cx === dx && ny - cy === dy) { next = outs[i]; break; }
        if (!next) next = outs[i];
      }
      if (!next) break;
      used.add(key(cx, cy, next[0], next[1]));
      px = cx; py = cy; cx = next[0]; cy = next[1];
    }
    if (pts.length >= 3) loops.push(pts);
  });
  return loops;
};

// Drop the vertices that sit on the line between their neighbours. A rasterised
// diagonal arrives as a staircase of single-cell steps; this collapses it back
// to the one straight edge the drawing actually has, while a true right angle
// stands too far off the chord to be dropped.
const simplifyLoop = (pts, tol) => {
  if (pts.length < 4) return pts;
  const dist2 = (p, a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = dx * dx + dy * dy;
    if (!L) return (p[0] - a[0]) ** 2 + (p[1] - a[1]) ** 2;
    let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L;
    t = Math.max(0, Math.min(1, t));
    return (p[0] - a[0] - t * dx) ** 2 + (p[1] - a[1] - t * dy) ** 2;
  };
  const keep = new Array(pts.length).fill(false);
  keep[0] = true;
  keep[pts.length - 1] = true;
  const walk = (i, j) => {
    let best = -1;
    let bestD = tol * tol;
    for (let k = i + 1; k < j; k += 1) {
      const d = dist2(pts[k], pts[i], pts[j]);
      if (d > bestD) { bestD = d; best = k; }
    }
    if (best < 0) return;
    keep[best] = true;
    walk(i, best);
    walk(best, j);
  };
  walk(0, pts.length - 1);
  return pts.filter((_, i) => keep[i]);
};

// Clubs a scattered set of cells into one region by closing the gaps between
// them - grow the region, then shrink it back by the same amount. The growth
// bridges anything narrower than twice the radius; the shrink puts the outer
// boundary back where it started, so the outline still follows the shape of
// the group rather than ballooning around it.
//
// Two things keep this affordable at the canvas's real resolution (roughly a
// million cells for a full layout):
//
//   scoped   growth only ever touches the zone's own bounding box plus a
//            margin for the radius, never the whole canvas - a zone tucked
//            into one corner of a hall does not pay for the empty half of
//            the drawing.
//   cumulative   the region is grown one step at a time and checked after
//            each step, so the search stops the moment the pieces join
//            instead of re-running the whole grow+shrink cycle at every
//            radius from 1 up. The gap between two blocks across an aisle
//            typically closes within 5-15 steps; the shrink then undoes
//            exactly that many steps, once.
const clubCells = (cells, grid, maxRadius) => {
  if (!cells || cells.length === 0) return cells;

  let minC = grid.cols, maxC = -1, minR = grid.rows, maxR = -1;
  cells.forEach((i) => {
    const r = (i / grid.cols) | 0;
    const c = i - r * grid.cols;
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
  });

  const pad = maxRadius + 1;
  const c0 = Math.max(0, minC - pad);
  const r0 = Math.max(0, minR - pad);
  const c1 = Math.min(grid.cols - 1, maxC + pad);
  const r1 = Math.min(grid.rows - 1, maxR + pad);
  const subCols = c1 - c0 + 1;
  const subRows = r1 - r0 + 1;
  const n = subCols * subRows;

  const toSub = (i) => {
    const r = (i / grid.cols) | 0;
    const c = i - r * grid.cols;
    return (r - r0) * subCols + (c - c0);
  };
  const toGlobal = (j) => {
    const r = (j / subCols) | 0;
    const c = j - r * subCols;
    return (r + r0) * grid.cols + (c + c0);
  };

  let g = new Uint8Array(n);
  cells.forEach((i) => { g[toSub(i)] = 1; });

  const step = (src, grow) => {
    const out = new Uint8Array(n);
    for (let r = 0; r < subRows; r += 1) {
      for (let c = 0; c < subCols; c += 1) {
        const i = r * subCols + c;
        const right = c + 1 < subCols ? src[i + 1] : 0;
        const left = c > 0 ? src[i - 1] : 0;
        const down = r + 1 < subRows ? src[i + subCols] : 0;
        const up = r > 0 ? src[i - subCols] : 0;
        out[i] = grow
          ? (src[i] || right || left || down || up ? 1 : 0)
          : (src[i] && right && left && down && up ? 1 : 0);
      }
    }
    return out;
  };

  // Connected-component count, with an early exit the moment a second one
  // shows up - this runs after every growth step, so it never needs to be
  // exact past "more than one".
  const moreThanOneRegion = (arr) => {
    const seen = new Uint8Array(n);
    let comps = 0;
    for (let i = 0; i < n; i += 1) {
      if (!arr[i] || seen[i]) continue;
      comps += 1;
      if (comps > 1) return true;
      const stack = [i];
      seen[i] = 1;
      while (stack.length) {
        const cur = stack.pop();
        const r = (cur / subCols) | 0;
        const c = cur - r * subCols;
        if (c + 1 < subCols && arr[cur + 1] && !seen[cur + 1]) { seen[cur + 1] = 1; stack.push(cur + 1); }
        if (c > 0 && arr[cur - 1] && !seen[cur - 1]) { seen[cur - 1] = 1; stack.push(cur - 1); }
        if (r + 1 < subRows && arr[cur + subCols] && !seen[cur + subCols]) { seen[cur + subCols] = 1; stack.push(cur + subCols); }
        if (r > 0 && arr[cur - subCols] && !seen[cur - subCols]) { seen[cur - subCols] = 1; stack.push(cur - subCols); }
      }
    }
    return false;
  };

  let steps = 0;
  while (moreThanOneRegion(g) && steps < maxRadius) {
    g = step(g, true);
    steps += 1;
  }
  const stillSeparate = moreThanOneRegion(g);
  for (let k = 0; k < steps; k += 1) g = step(g, false);

  const out = [];
  for (let j = 0; j < n; j += 1) if (g[j]) out.push(toGlobal(j));

  // Closing bridges anything within reach; a gap wider than the cap is left as
  // more than one region. "One entire zone" cannot mean "usually one piece",
  // so the guaranteed fallback is a single convex fill over everything picked.
  if (stillSeparate) return hullFillCells(cells, grid);
  return out;
};

// A single convex region covering every given cell - the guaranteed fallback
// when closing cannot bridge a gap within its radius cap. It always comes back
// as exactly one region, which is the one promise a zone highlight has to keep;
// it can cover more than the stalls alone when two clusters sit far apart, the
// same trade closing already makes on a smaller scale.
const hullFillCells = (cells, grid) => {
  if (!cells || cells.length === 0) return cells;
  const pts = cells.map((i) => {
    const r = (i / grid.cols) | 0;
    return [i - r * grid.cols, r];
  });
  const sorted = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  sorted.forEach((p) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  });
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  const hull = lower.concat(upper);
  if (hull.length < 3) return cells;

  let minR = grid.rows;
  let maxR = -1;
  let minC = grid.cols;
  let maxC = -1;
  hull.forEach(([c, r]) => {
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
  });

  const out = [];
  for (let r = minR; r <= maxR; r += 1) {
    const xs = [];
    for (let i = 0; i < hull.length; i += 1) {
      const [x1, y1] = hull[i];
      const [x2, y2] = hull[(i + 1) % hull.length];
      if ((y1 <= r && y2 > r) || (y2 <= r && y1 > r)) {
        xs.push(x1 + ((r - y1) / (y2 - y1)) * (x2 - x1));
      }
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const c0 = Math.max(minC, Math.ceil(xs[k]));
      const c1 = Math.min(maxC, Math.floor(xs[k + 1]));
      for (let c = c0; c <= c1; c += 1) out.push(r * grid.cols + c);
    }
  }
  return out.length ? out : cells;
};

// Cell region -> an SVG path in user units.// Cell region -> an SVG path in user units.
const cellsToPath = (cells, grid) => {
  if (!cells || cells.length === 0) return null;
  const loops = cellsToLoops(cells, grid.cols);
  if (loops.length === 0) return null;
  const parts = [];
  loops.forEach((loop) => {
    const pts = simplifyLoop(loop, OUTLINE_SIMPLIFY);
    if (pts.length < 3) return;
    const at = (pt) => `${(grid.ox + pt[0] * grid.cell).toFixed(2)} ${(grid.oy + pt[1] * grid.cell).toFixed(2)}`;
    parts.push(`M${at(pts[0])}` + pts.slice(1).map((pt) => `L${at(pt)}`).join("") + "Z");
  });
  return parts.length ? parts.join("") : null;
};

// Grows a cell set by n steps. A traced pocket stops OUTLINE_DILATE cells short
// of the drawn line (the dilation that sealed it); growing it back by the same
// amount puts a non-rectangular outline on the border instead of inside it.
const growCells = (cells, grid, n) => {
  let current = new Set(cells);
  for (let k = 0; k < n; k += 1) {
    const next = new Set(current);
    current.forEach((i) => {
      const r = (i / grid.cols) | 0;
      const c = i - r * grid.cols;
      if (c + 1 < grid.cols) next.add(i + 1);
      if (c > 0) next.add(i - 1);
      if (r + 1 < grid.rows) next.add(i + grid.cols);
      if (r > 0) next.add(i - grid.cols);
    });
    current = next;
  }
  return Array.from(current);
};

// Below this share of its bounding box, a pocket is drawn as its traced shape
// rather than as a rectangle.
const OUTLINE_RECT_FILL = 0.85;

const findBoothOutlines = (svgElement, elementsByBooth) => {
  const outlines = new Map();
  if (!svgElement || !elementsByBooth || elementsByBooth.size === 0) return outlines;

  const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
  const ox = (vb && vb.x) || 0;
  const oy = (vb && vb.y) || 0;
  const vw = (vb && vb.width) || 792;
  const vh = (vb && vb.height) || 612;

  const cols = Math.max(1, Math.ceil(vw / OUTLINE_CELL));
  const rows = Math.max(1, Math.ceil(vh / OUTLINE_CELL));
  const ink = new Uint8Array(cols * rows);

  const ctmCache = new Map();
  const ctmOf = (el) => {
    const parent = el.parentNode;
    let base = ctmCache.get(parent);
    if (base === undefined) {
      base = parent ? matrixToSvgUserSpace(parent) : null;
      ctmCache.set(parent, base);
    }
    const own = parseTransformAttr(el.getAttribute("transform"));
    if (own === null) return matrixToSvgUserSpace(el);
    return base ? matMul(base, own) : own;
  };

  const mark = (x0, y0, x1, y1) => {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (OUTLINE_CELL * 0.4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const c = Math.floor((x0 + (x1 - x0) * t - ox) / OUTLINE_CELL);
      const r = Math.floor((y0 + (y1 - y0) * t - oy) / OUTLINE_CELL);
      if (c >= 0 && r >= 0 && c < cols && r < rows) ink[r * cols + c] = 1;
    }
  };

  let paths = [];
  try {
    paths = Array.from(svgElement.querySelectorAll("path"));
  } catch (e) {
    return outlines;
  }
  paths.forEach((el) => {
    if (isOverlayNode(el) || inTextLayer(el)) return;
    const d = el.getAttribute("d");
    if (!d) return;
    const m = ctmOf(el);
    const segs = pathSegments(d).map(([x0, y0, x1, y1]) => (m
      ? [m.a * x0 + m.c * y0 + m.e, m.b * x0 + m.d * y0 + m.f, m.a * x1 + m.c * y1 + m.e, m.b * x1 + m.d * y1 + m.f]
      : [x0, y0, x1, y1]));
    // Lettering is not a wall, whatever layer it is in (see isGlyphLike).
    if (isGlyphLike(el, segs)) return;
    segs.forEach(([p0x, p0y, p1x, p1y]) => mark(p0x, p0y, p1x, p1y));
  });

  let blocked = ink;
  for (let pass = 0; pass < OUTLINE_DILATE; pass += 1) {
    const grown = new Uint8Array(cols * rows);
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const i = r * cols + c;
        if (!blocked[i]) continue;
        grown[i] = 1;
        if (c + 1 < cols) grown[i + 1] = 1;
        if (c > 0) grown[i - 1] = 1;
        if (r + 1 < rows) grown[i + cols] = 1;
        if (r > 0) grown[i - cols] = 1;
      }
    }
    blocked = grown;
  }

  const stamp = new Int32Array(cols * rows);
  const maxCells = Math.floor((cols * rows) * OUTLINE_MAX_AREA);
  const stack = [];

  const freeCellNear = (c0, r0) => {
    for (let ring = 0; ring <= 6; ring += 1) {
      for (let dr = -ring; dr <= ring; dr += 1) {
        for (let dc = -ring; dc <= ring; dc += 1) {
          if (ring > 0 && Math.abs(dr) !== ring && Math.abs(dc) !== ring) continue;
          const c = c0 + dc, r = r0 + dr;
          if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
          if (!blocked[r * cols + c]) return r * cols + c;
        }
      }
    }
    return -1;
  };

  const pocketBox = (seed, id) => {
    let minC = cols, maxC = -1, minR = rows, maxR = -1, size = 0;
    const visited = [];
    stack.length = 0;
    stack.push(seed);
    stamp[seed] = id;
    while (stack.length) {
      const cur = stack.pop();
      visited.push(cur);
      size += 1;
      if (size > maxCells) return null;
      const r = (cur / cols) | 0;
      const c = cur - r * cols;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (c + 1 < cols && !blocked[cur + 1] && stamp[cur + 1] !== id) { stamp[cur + 1] = id; stack.push(cur + 1); }
      if (c - 1 >= 0 && !blocked[cur - 1] && stamp[cur - 1] !== id) { stamp[cur - 1] = id; stack.push(cur - 1); }
      if (r + 1 < rows && !blocked[cur + cols] && stamp[cur + cols] !== id) { stamp[cur + cols] = id; stack.push(cur + cols); }
      if (r - 1 >= 0 && !blocked[cur - cols] && stamp[cur - cols] !== id) { stamp[cur - cols] = id; stack.push(cur - cols); }
    }
    if (maxC < minC || maxR < minR) return null;

    const colHasInk = (c) => {
      for (let r = minR; r <= maxR; r += 1) if (ink[r * cols + c]) return true;
      return false;
    };
    const rowHasInk = (r) => {
      for (let c = minC; c <= maxC; c += 1) if (ink[r * cols + c]) return true;
      return false;
    };
    let left = minC - 1;
    let right = maxC + 1;
    let top = minR - 1;
    let bottom = maxR + 1;
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (minC - d >= 0 && colHasInk(minC - d)) { left = minC - d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (maxC + d < cols && colHasInk(maxC + d)) { right = maxC + d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (minR - d >= 0 && rowHasInk(minR - d)) { top = minR - d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (maxR + d < rows && rowHasInk(maxR + d)) { bottom = maxR + d; break; }
    }

    return {
      x: ox + (left + 0.5) * OUTLINE_CELL,
      y: oy + (top + 0.5) * OUTLINE_CELL,
      width: (right - left) * OUTLINE_CELL,
      height: (bottom - top) * OUTLINE_CELL,
      cells: visited,
      // Share of its own bounding box the pocket fills: ~1 for a rectangle,
      // clearly less for an L, a pentagon or a stall around a pillar.
      fill: visited.length / ((maxC - minC + 1) * (maxR - minR + 1)),
    };
  };

  const printedSizes = [];
  try {
    Array.from(svgElement.querySelectorAll("text")).forEach((el) => {
      if (isOverlayNode(el)) return;
      const m = String(el.textContent || "")
        .replace(/\s+/g, "")
        // "9x6(54)" - or "9x6" alone: the 08.09.2026 plan prints the area as
        // a separate text, which left all but one size note unread there.
        .match(/^(\d+(?:\.\d+)?)[xX×](\d+(?:\.\d+)?)(?:\((\d+(?:\.\d+)?)\))?$/);
      if (!m) return;
      const box = elementBoxInSvgSpace(el);
      if (!box) return;
      printedSizes.push({
        area: Number(m[3]) || Number(m[1]) * Number(m[2]),
        w: Number(m[1]),
        h: Number(m[2]),
        cx: box.x + box.width / 2,
        cy: box.y + box.height / 2,
        box,
      });
    });
  } catch (e) { }

  const printedFor = (box) => {
    let best = null;
    let bestD = Infinity;
    for (let i = 0; i < printedSizes.length; i += 1) {
      const d = printedSizes[i];
      if (d.cx < box.x || d.cx > box.x + box.width) continue;
      if (d.cy < box.y || d.cy > box.y + box.height) continue;
      const dist = Math.hypot(d.cx - (box.x + box.width / 2), d.cy - (box.y + box.height / 2));
      if (dist < bestD) { bestD = dist; best = d; }
    }
    return best;
  };

  // Share of a straight run that carries drawn ink, allowing a cell either side
  // so a line that falls between two cells still counts. Dashed borders score
  // around their dash duty; open floor scores near zero.
  const inkAt = (c, r) => c >= 0 && r >= 0 && c < cols && r < rows && ink[r * cols + c] === 1;
  const inkShare = (horizontal, fixedAt, from, to) => {
    const f = Math.round((fixedAt - (horizontal ? oy : ox)) / OUTLINE_CELL - 0.5);
    const a = Math.floor((from - (horizontal ? ox : oy)) / OUTLINE_CELL);
    const b = Math.floor((to - (horizontal ? ox : oy)) / OUTLINE_CELL);
    if (b <= a) return 0;
    let hits = 0;
    for (let k = a; k <= b; k += 1) {
      const hit = horizontal
        ? inkAt(k, f - 1) || inkAt(k, f) || inkAt(k, f + 1)
        : inkAt(f - 1, k) || inkAt(f, k) || inkAt(f + 1, k);
      if (hit) hits += 1;
    }
    return hits / (b - a + 1);
  };

  // Place a stall of its printed size (w x h units, either orientation) where
  // its four edges sit best on the drawing's ink, keeping the booth label and
  // the size note inside it. The note is printed in a corner of the stall, not
  // at its centre, so centring on the note puts the box off the stall.
  const fitPrintedRect = (label, note, pw, ph, K) => {
    // Must hold the label's and the note's centres - not their whole boxes,
    // which overflow a narrow stall and would force the fit to a wrong, wider
    // position that happens to contain all the text.
    const point = (x, y) => ({ x: x - 0.5, y: y - 0.5, width: 1, height: 1 });
    const inner = unionBoxes([
      point(label.x + label.width / 2, label.y + label.height / 2),
      point(note.cx, note.cy),
    ]);
    if (!inner) return null;
    let best = null;
    [[pw * K, ph * K], [ph * K, pw * K]].forEach(([w, h]) => {
      const x0 = inner.x + inner.width - w - 1;
      const x1 = inner.x + 1;
      const y0 = inner.y + inner.height - h - 1;
      const y1 = inner.y + 1;
      if (x1 < x0 || y1 < y0) return; // the content is wider than the stall
      for (let x = x0; x <= x1; x += OUTLINE_CELL) {
        const left = inkShare(false, x, y0, y1 + h);
        const right = inkShare(false, x + w, y0, y1 + h);
        if (left + right < 0.3) continue; // no wall anywhere along either side
        for (let y = y0; y <= y1; y += OUTLINE_CELL) {
          const score =
            inkShare(true, y, x, x + w) + inkShare(true, y + h, x, x + w) +
            inkShare(false, x, y, y + h) + inkShare(false, x + w, y, y + h);
          if (!best || score > best.score) best = { score, x, y, width: w, height: h };
        }
      }
    });
    // Four edges averaging under a fifth ink is not a border - do not invent one.
    if (!best || best.score < 0.8) return null;
    return { x: best.x, y: best.y, width: best.width, height: best.height };
  };

  let id = 0;
  const stats = [];
  const cellsByBooth = new Map();
  const notesIn = (box) => printedSizes.filter((p) =>
    p.cx >= box.x && p.cx <= box.x + box.width && p.cy >= box.y && p.cy <= box.y + box.height
  ).length;
  // The label each booth was finally placed from - one occurrence, never the
  // union of several (see below). getBoothRawBox falls back to it.
  const labelByBooth = new Map();

  elementsByBooth.forEach((els, name) => {
    // A code can be printed more than once (H3-F5 sits both in its narrow
    // column and as a larger caption beside it). Tracing from the union of the
    // occurrences seeded the fill between them and produced one box spanning
    // both, so each occurrence is traced on its own and the best pocket kept:
    // one holding exactly one size note, then the smallest.
    const labels = els.map(elementBoxInSvgSpace).filter(Boolean);
    if (labels.length === 0) return;
    let chosen = null;
    let failure = null;
    labels.forEach((label) => {
      id += 1;
      const c0 = Math.floor((label.x + label.width / 2 - ox) / OUTLINE_CELL);
      const r0 = Math.floor((label.y + label.height / 2 - oy) / OUTLINE_CELL);
      const seed = freeCellNear(c0, r0);
      if (seed < 0) { failure = failure || "no free cell at label"; return; }
      const candidate = pocketBox(seed, id);
      if (!candidate) { failure = failure || "escaped to aisle"; return; }
      // The label's centre, not its whole box: in the narrow 3 m columns the
      // code is wider than the stall and overflows its walls, so requiring the
      // full label rejected every correct pocket there.
      const lcx = label.x + label.width / 2;
      const lcy = label.y + label.height / 2;
      const holds =
        lcx >= candidate.x - 1 && lcx <= candidate.x + candidate.width + 1 &&
        lcy >= candidate.y - 1 && lcy <= candidate.y + candidate.height + 1;
      if (!holds) { failure = failure || "lost its label"; return; }
      const notes = notesIn(candidate);
      const rank = notes === 1 ? 0 : notes === 0 ? 1 : 2;
      const area = candidate.width * candidate.height;
      if (!chosen || rank < chosen.rank || (rank === chosen.rank && area < chosen.area)) {
        chosen = { label, box: candidate, rank, area };
      }
    });
    if (!chosen) {
      labelByBooth.set(name, labels[0]);
      stats.push({ booth: name, result: failure || "not traced", label: labels[0] });
      return;
    }
    const label = chosen.label;
    const box = chosen.box;
    labelByBooth.set(name, label);
    outlines.set(name, box);
    cellsByBooth.set(name, box.cells);
    const printed = printedFor(box);
    stats.push({
      booth: name,
      result: `${box.width.toFixed(1)} x ${box.height.toFixed(1)}`,
      printed: printed ? `${printed.w}x${printed.h}(${printed.area})` : null,
      scale: printed && printed.area > 0
        ? Math.sqrt((box.width * box.height) / printed.area)
        : null,
      label,
    });
  });

  // A stall carries one printed size. A pocket holding two or more size notes
  // has leaked through a break in the dashes into its neighbours (the narrow
  // 3x6 columns are the usual case), so its box spans several stalls. Reject it
  // here, before its scale can skew the per-hall median, and let the fit pass
  // below place the stall from its own note instead.
  stats.forEach((r) => {
    const box = outlines.get(r.booth);
    if (!box) return;
    const n = notesIn(box);
    if (n < 2) return;
    outlines.delete(r.booth);
    cellsByBooth.delete(r.booth);
    r.result = `leaked (holds ${n} size notes)`;
    r.scale = null;
  });

  // A pocket holding none of its own size note was usually cut short by a
  // line that is not a wall: a dashed zone / boundary line drawn straight
  // across the stall, on a layer whose name says nothing (Halls 9-12: a red
  // dash in "Layout" at y ~675 across the bottom row - the stalls run on to
  // their wall at ~687 and each "9x6" note sits below the line). The part of
  // the stall past the line is a pocket of its own, holding the note. The two
  // are joined when that pocket sits right beside this one, holds no booth
  // label and exactly this one size note - so a real neighbour is never
  // swallowed, and nothing depends on layer names or colours.
  const JOIN_GAP = 4; // units between the two pockets (the line and its dilation)
  const insideBox = (p, b) => p.cx >= b.x && p.cx <= b.x + b.width && p.cy >= b.y && p.cy <= b.y + b.height;
  const labelPoints = stats
    .filter((r) => r.label)
    .map((r) => ({ booth: r.booth, cx: r.label.x + r.label.width / 2, cy: r.label.y + r.label.height / 2 }));
  stats.forEach((r) => {
    const box = outlines.get(r.booth);
    if (!box || !r.label || notesIn(box) > 0) return;
    const me = labelPoints.find((p) => p.booth === r.booth);
    const owned = (p) => Array.from(outlines.entries()).some(([other, b]) => other !== r.booth && insideBox(p, b));
    const near = printedSizes
      .filter((p) => !owned(p))
      .sort((a, b) => Math.hypot(a.cx - me.cx, a.cy - me.cy) - Math.hypot(b.cx - me.cx, b.cy - me.cy))
      .slice(0, 3);
    for (let i = 0; i < near.length; i += 1) {
      const note = near[i];
      const seed = freeCellNear(
        Math.floor((note.cx - ox) / OUTLINE_CELL),
        Math.floor((note.cy - oy) / OUTLINE_CELL)
      );
      if (seed < 0) continue;
      id += 1;
      const other = pocketBox(seed, id);
      if (!other || notesIn(other) !== 1 || labelPoints.some((p) => insideBox(p, other))) continue;
      const gapV = Math.max(other.y - (box.y + box.height), box.y - (other.y + other.height));
      const overlapX = Math.min(box.x + box.width, other.x + other.width) - Math.max(box.x, other.x);
      const gapH = Math.max(other.x - (box.x + box.width), box.x - (other.x + other.width));
      const overlapY = Math.min(box.y + box.height, other.y + other.height) - Math.max(box.y, other.y);
      let joined = null;
      if (gapV <= JOIN_GAP && overlapX > 0.5 * Math.min(box.width, other.width)) {
        // Above / below each other: the stall is the other pocket's columns.
        const top = Math.min(box.y, other.y);
        joined = { x: other.x, y: top, width: other.width, height: Math.max(box.y + box.height, other.y + other.height) - top };
      } else if (gapH <= JOIN_GAP && overlapY > 0.5 * Math.min(box.height, other.height)) {
        const left = Math.min(box.x, other.x);
        joined = { x: left, y: other.y, width: Math.max(box.x + box.width, other.x + other.width) - left, height: other.height };
      }
      if (!joined || !insideBox(me, joined)) continue;
      outlines.set(r.booth, { ...joined, fill: 1 });
      cellsByBooth.delete(r.booth);
      r.result += ` -> joined with the pocket of its ${note.w}x${note.h} note across a non-wall line`;
      break;
    }
  });

  const hallOf = (name) => {
    const m = String(name).match(/^H(\d+)/i);
    return m ? m[1] : "?";
  };
  const median = (arr) => {
    const sorted = arr.slice().sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  };

  const scalesByHall = new Map();
  stats.forEach((r) => {
    if (!(r.scale > 0)) return;
    const hall = hallOf(r.booth);
    if (!scalesByHall.has(hall)) scalesByHall.set(hall, []);
    scalesByHall.get(hall).push(r.scale);
  });
  const allScales = stats.map((r) => r.scale).filter((v) => v > 0);
  const globalK = allScales.length >= 4 ? median(allScales) : null;

  if (globalK || scalesByHall.size > 0) {
    stats.forEach((r) => {
      if (!r.scale || !r.printed) return;
      const hall = hallOf(r.booth);
      const hallScales = scalesByHall.get(hall) || [];
      const K = hallScales.length >= 4 ? median(hallScales) : globalK;
      if (!K) return;

      const off = Math.abs(r.scale - K) / K;
      if (off <= OUTLINE_SIZE_TOLERANCE) return;

      const box = outlines.get(r.booth);
      const printed = box && printedFor(box);
      if (!box || !printed) return;

      const fixed = fitPrintedRect(r.label, printed, printed.w, printed.h, K);
      if (!fixed) { r.result += " (off-size, no border fits, kept)"; return; }

      r.result += ` -> rebuilt ${fixed.width.toFixed(1)} x ${fixed.height.toFixed(1)} on ink`;
      outlines.set(r.booth, fixed);
      cellsByBooth.delete(r.booth);
    });
  }

  // Several booth codes can share one stall (a combined stand). A code whose
  // label sits inside a stall already placed takes that stall, rather than
  // being fitted or guessed on its own and drawn as a second, overlapping box.
  const labelOf = new Map();
  stats.forEach((r) => { if (r.label) labelOf.set(r.booth, r.label); });
  const adoptEnclosing = () => {
    const placed = Array.from(outlines.entries());
    labelOf.forEach((l, name) => {
      if (outlines.has(name)) return;
      const cx = l.x + l.width / 2;
      const cy = l.y + l.height / 2;
      let host = null;
      placed.forEach(([other, b]) => {
        if (cx < b.x || cx > b.x + b.width || cy < b.y || cy > b.y + b.height) return;
        // The tightest enclosing stall, if boxes happen to nest.
        if (!host || b.width * b.height < host[1].width * host[1].height) host = [other, b];
      });
      if (!host) return;
      outlines.set(name, host[1]);
      if (cellsByBooth.has(host[0])) cellsByBooth.set(name, cellsByBooth.get(host[0]));
      const r = stats.find((x) => x.booth === name);
      if (r) r.result += ` -> shares ${host[0]}'s stall`;
    });
  };
  adoptEnclosing();

  // Booths whose pocket could not be traced at all. If a size note sits close
  // enough to be inside the same stall - and is not already inside a stall that
  // did trace - fit that printed size onto the ink instead of guessing from the
  // label alone.
  if (globalK) {
    const traced = Array.from(outlines.values());
    stats.forEach((r) => {
      if (outlines.has(r.booth) || !r.label) return;
      const hallScales = scalesByHall.get(hallOf(r.booth)) || [];
      const K = hallScales.length >= 4 ? median(hallScales) : globalK;
      const lx = r.label.x + r.label.width / 2;
      const ly = r.label.y + r.label.height / 2;
      let note = null;
      let noteD = Infinity;
      printedSizes.forEach((p) => {
        const reach = Math.hypot(p.w, p.h) * K;
        const d = Math.hypot(p.cx - lx, p.cy - ly);
        if (d > reach || d >= noteD) return;
        const owned = traced.some((b) =>
          p.cx >= b.x && p.cx <= b.x + b.width && p.cy >= b.y && p.cy <= b.y + b.height
        );
        if (owned) return;
        note = p;
        noteD = d;
      });
      if (!note) return;
      const fitted = fitPrintedRect(r.label, note, note.w, note.h, K);
      if (!fitted) return;
      r.result += ` -> fitted ${note.w}x${note.h} on ink`;
      outlines.set(r.booth, fitted);
      traced.push(fitted);
    });
  }
  adoptEnclosing();

  // Last resort for a booth nothing above could place: walk outward from the
  // label's CENTRE to the first drawn line on each side. Walking from the
  // label's edges failed in the narrow 3 m columns: the code ("H1-B15") is
  // wider than the stall, so its text overlaps the side wall, the walk started
  // beyond that wall and stopped at the next one out. The centre is always in
  // the stall. Label text is not ink, so walking through it is harmless.
  const CLAMP_REACH = 20; // units
  const labelCentres = [];
  labelOf.forEach((l, name) => labelCentres.push({ name, x: l.x + l.width / 2, y: l.y + l.height / 2 }));
  stats.forEach((r) => {
    if (outlines.has(r.booth) || !r.label) return;
    const l = r.label;
    const cx = l.x + l.width / 2;
    const cy = l.y + l.height / 2;
    const walk = (horizontal, start, dir, from, to) => {
      for (let d = OUTLINE_CELL; d <= CLAMP_REACH; d += OUTLINE_CELL) {
        const at = start + dir * d;
        let share = inkShare(horizontal, at, from, to);
        if (share < 0.3) continue;
        // The probe tolerates a cell either side, so it fires a cell before
        // the line. Step on to where the line is strongest.
        let best = at;
        for (let k = 1; k <= 2; k += 1) {
          const next = at + dir * k * OUTLINE_CELL;
          const s2 = inkShare(horizontal, next, from, to);
          // A tie one cell on is the line's centre: the probe's tolerance makes
          // it read equally strong from one cell either side.
          if (s2 > share || (k === 1 && s2 === share)) { share = s2; best = next; }
        }
        return best;
      }
      return null;
    };
    // Side walls are probed over a short run around the centre - long enough
    // to span a dash gap, short enough not to catch a neighbour's corner.
    const left = walk(false, cx, -1, cy - 3, cy + 3);
    const right = walk(false, cx, 1, cy - 3, cy + 3);
    if (left == null || right == null) { r.result += " (no side walls near label)"; return; }
    const top = walk(true, cy, -1, left + 0.5, right - 0.5);
    const bottom = walk(true, cy, 1, left + 0.5, right - 0.5);
    if (top == null || bottom == null) { r.result += " (no end walls near label)"; return; }
    const box = { x: left, y: top, width: right - left, height: bottom - top };

    // A box that holds another stall's label or a second size note has
    // crossed a wall it did not see. Better no box than a wrong one.
    const strangers = labelCentres.filter((c) =>
      c.name !== r.booth &&
      c.x > box.x && c.x < box.x + box.width && c.y > box.y && c.y < box.y + box.height
    ).length;
    if (strangers > 0 || notesIn(box) > 1 || box.width < 2 || box.height < 2) {
      r.result += ` (walled box rejected: ${strangers} other labels, ${notesIn(box)} notes)`;
      return;
    }
    outlines.set(r.booth, box);
    r.result += " -> walled from label centre";
  });
  adoptEnclosing();

  try {
    window.__boothOutlines = {
      cell: OUTLINE_CELL,
      grid: `${cols} x ${rows}`,
      traced: outlines.size,
      fellBack: elementsByBooth.size - outlines.size,
      booths: stats,
    };
  } catch (e) { }

  outlines.grid = { cols, rows, ox, oy, cell: OUTLINE_CELL };
  outlines.cells = cellsByBooth;
  outlines.labels = labelByBooth;
  return outlines;
};

// Fallback for a booth whose outline could not be found. Grows the label box
// into a booth-cell-sized target so the focus ring reads as the stall rather
// than as the text.
const padBox = (box, padX = 7, padY = 7, minW = 24, minH = 15) => {
  if (!box) return null;
  let { x, y, width, height } = box;
  x -= padX; y -= padY; width += padX * 2; height += padY * 2;
  if (width < minW) { x -= (minW - width) / 2; width = minW; }
  if (height < minH) { y -= (minH - height) / 2; height = minH; }
  return { x, y, width, height };
};

// ─── Walkable navigation grid ───────────────────────────────────────────
// Routing cannot use a booth-to-booth graph: every edge in one is a straight
// line, so the shortest path cuts diagonally through the stalls. Nor can it use
// booth label boxes as obstacles - a label is a ~10x4 unit sliver sitting inside
// a stall that may be 30x25, leaving most of the stall walkable.
//
// Instead the drawing's own line work is rasterised as walls. Every stall in
// this layout is drawn as a closed (if dashed) outline, so once the ink is
// blocked each stall becomes a sealed pocket and the largest remaining free
// region *is* the aisle network. Routing is confined to that region, which
// makes passing through a stall structurally impossible rather than merely
// expensive. Measured on the ELECRAMA layout: 150/150 stalls sealed, 10/10
// sample routes entirely within the aisles.

const NAV_CELL = 2;        // SVG user units per cell
const MIN_INK_SEG = 1.5;   // shorter strokes are glyph outlines, not structure
const TURN_COST = 2.5;     // without this the optimal path is a staircase
const HUG_COST = 1.5;      // keeps the line off the walls, down the aisle centre

// Layers holding text rendered as outlines. Their glyph strokes would pepper
// the aisles with phantom obstacles - and, inside a stall, wall off the text
// from the booth fill, so the traced outline hugs the lettering (holes around
// every word, pockets cut short where a name crosses the stall). The
// 08.09.2026 revision of the Halls 1-7 plan added exhibitor names in
// "Company Name": 1,984 glyph paths that did exactly that.
const TEXT_LAYERS = new Set(["Stall No", "Stall Size", "Area", "Company Name"]);
// Any later text layer under a similar name is text too.
const TEXT_LAYER_RE = /company|exhibitor|name|logo|text|label/i;
// Zone annotation is not structure either: the red dashed zone outline
// ("ZONE-BOUNDARY") and the zone colour fills ("Zone 1") are drawn straight
// across stalls. Read as walls they cut those stalls short - on the
// 08.09.2026 plan H1-B2 / H1-A1 traced 43 x 22 against a printed 9x6
// (~43 x 28), and the H1-A7 / H1-A6 stand (printed 6x18, ~28 x 84) split in
// two. Without them every one of the 150 booths traces with exactly its own
// size note inside.
const NON_WALL_LAYER_RE = /zone|boundary/i;

// 4-directional only: a diagonal step could slip through the corner gap where
// two stalls touch.
const NAV_DIRS = [
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
];

class MinHeap {
  constructor() { this.items = []; this.costs = []; }
  get size() { return this.items.length; }
  push(item, cost) {
    this.items.push(item); this.costs.push(cost);
    let i = this.items.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.costs[p] <= this.costs[i]) break;
      this.swap(p, i); i = p;
    }
  }
  pop() {
    const top = this.items[0];
    const li = this.items.pop();
    const lc = this.costs.pop();
    if (this.items.length > 0) {
      this.items[0] = li; this.costs[0] = lc;
      let i = 0;
      for (; ;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let s = i;
        if (l < this.items.length && this.costs[l] < this.costs[s]) s = l;
        if (r < this.items.length && this.costs[r] < this.costs[s]) s = r;
        if (s === i) break;
        this.swap(s, i); i = s;
      }
    }
    return top;
  }
  swap(a, b) {
    const ti = this.items[a]; this.items[a] = this.items[b]; this.items[b] = ti;
    const tc = this.costs[a]; this.costs[a] = this.costs[b]; this.costs[b] = tc;
  }
}

const matMul = (m, n) => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

const IDENTITY = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

// Only the forms this exporter emits, plus the common shorthands.
const parseTransformAttr = (str) => {
  if (!str) return IDENTITY;
  let out = IDENTITY;
  const re = /(matrix|translate|scale)\(([^)]*)\)/g;
  let m;
  let matched = false;
  while ((m = re.exec(str)) !== null) {
    const v = m[2].trim().split(/[\s,]+/).map(Number);
    if (v.some(Number.isNaN)) return null;
    let next = null;
    if (m[1] === "matrix" && v.length >= 6) {
      next = { a: v[0], b: v[1], c: v[2], d: v[3], e: v[4], f: v[5] };
    } else if (m[1] === "translate") {
      next = { a: 1, b: 0, c: 0, d: 1, e: v[0] || 0, f: v.length > 1 ? v[1] : 0 };
    } else if (m[1] === "scale") {
      next = { a: v[0] || 1, b: 0, c: 0, d: v.length > 1 ? v[1] : (v[0] || 1), e: 0, f: 0 };
    }
    if (!next) return null;
    out = matMul(out, next);
    matched = true;
  }
  return matched ? out : (str.trim() ? null : IDENTITY);
};

// Straight-line vertices of a path. Curves are only used for glyphs and arrow
// heads in this drawing, so treating their endpoints as vertices is enough.
const pathSegments = (d) => {
  const cmds = d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!cmds) return [];
  const segs = [];
  let cx = 0, cy = 0, sx = 0, sy = 0, prev = null;
  const emit = (x, y) => { if (prev) segs.push([prev.x, prev.y, x, y]); prev = { x, y }; };
  for (const c of cmds) {
    const op = c[0];
    const a = c.slice(1).match(/-?\d*\.?\d+(?:e-?\d+)?/gi);
    const n = a ? a.map(Number) : [];
    if (op === "M" || op === "m") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "M") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        if (i === 0) { prev = null; sx = cx; sy = cy; }
        emit(cx, cy);
      }
    } else if (op === "L" || op === "l") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "L") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        emit(cx, cy);
      }
    } else if (op === "H" || op === "h") {
      for (const v of n) { cx = op === "H" ? v : cx + v; emit(cx, cy); }
    } else if (op === "V" || op === "v") {
      for (const v of n) { cy = op === "V" ? v : cy + v; emit(cx, cy); }
    } else if (op === "Z" || op === "z") {
      emit(sx, sy);
    } else {
      prev = null; // curve: break the run rather than guess at its shape
    }
  }
  return segs;
};

// Text exported as outlines - and small solid markers - is FILLED and never
// stroked; every wall in these drawings is a stroked line. So a path with no
// stroke whose extent is glyph-sized is lettering, whatever layer it sits in.
// Layer names alone are not enough: a plan exported another way may name its
// layers differently (or not at all), and then an exhibitor name inside a stall
// walls itself off from the booth fill - the traced outline hugs the words.
// Measured on the 08.09.2026 Halls 1-7 plan: every glyph layer (Company Name,
// Stall No, Area, Stall Size) is unstroked with sides under 7.1 units; 127 of
// 150 booths then trace as clean rectangles, the same as excluding by name.
const GLYPH_MAX_SIDE = 8; // SVG user units
const strokeCache = new WeakMap();
const isStroked = (el) => {
  if (!el || !el.getAttribute) return false;
  if (strokeCache.has(el)) return strokeCache.get(el);
  let v = el.getAttribute("stroke");
  if (v == null) {
    const m = String(el.getAttribute("style") || "").match(/(?:^|;)\s*stroke\s*:\s*([^;]+)/);
    if (m) v = m[1];
  }
  // A stroke set by a stylesheet class is invisible to the attributes; ask the
  // browser, or a classed wall's short dashes would read as unstroked glyphs.
  if (v == null && el.getAttribute("class") && typeof window !== "undefined" && window.getComputedStyle) {
    try { v = window.getComputedStyle(el).stroke; } catch (e) { v = null; }
    if (v === "") v = null;
  }
  const tag = el.tagName ? el.tagName.toLowerCase() : "";
  const out = v != null
    ? String(v).trim() !== "none"
    : tag === "svg" ? false : isStroked(el.parentNode);
  strokeCache.set(el, out);
  return out;
};
// segs: the path's segments already in root user space.
const isGlyphLike = (el, segs) => {
  if (isStroked(el)) return false;
  if (segs.length === 0) return true;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  segs.forEach(([x0, y0, x1, y1]) => {
    minX = Math.min(minX, x0, x1); maxX = Math.max(maxX, x0, x1);
    minY = Math.min(minY, y0, y1); maxY = Math.max(maxY, y0, y1);
  });
  return Math.max(maxX - minX, maxY - minY) < GLYPH_MAX_SIDE;
};

const inTextLayer = (el) => {
  for (let p = el.parentNode; p && p.getAttribute; p = p.parentNode) {
    const label = p.getAttribute("inkscape:label");
    if (label && (TEXT_LAYERS.has(label) || TEXT_LAYER_RE.test(label) || NON_WALL_LAYER_RE.test(label))) return true;
    if (p.tagName && p.tagName.toLowerCase() === "svg") break;
  }
  return false;
};

// Rasterise the drawing's line work into a blocked-cell grid.
const buildNavGrid = (svgElement, viewBox) => {
  const { x: ox, y: oy, width: vw, height: vh } = viewBox;
  const cols = Math.max(1, Math.ceil(vw / NAV_CELL));
  const rows = Math.max(1, Math.ceil(vh / NAV_CELL));
  const blocked = new Uint8Array(cols * rows);

  const ctmCache = new Map();
  const ctmOf = (el) => {
    // Matrix(path) = Matrix(parent) x transform(path), all in root USER space -
    // see matrixToSvgUserSpace: getCTM() would return viewport pixels once the
    // svg scales to its container, which would misplace every wall. Caching per
    // parent means a handful of matrix reads instead of one per path.
    const parent = el.parentNode;
    let base = ctmCache.get(parent);
    if (base === undefined) {
      base = parent ? matrixToSvgUserSpace(parent) : null;
      ctmCache.set(parent, base);
    }
    const own = parseTransformAttr(el.getAttribute("transform"));
    if (own === null) {
      return matrixToSvgUserSpace(el);
    }
    return base ? matMul(base, own) : own;
  };

  let paths = [];
  try {
    paths = Array.from(svgElement.querySelectorAll("path"));
  } catch (e) {
    paths = [];
  }

  const mark = (x0, y0, x1, y1) => {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (NAV_CELL * 0.4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const c = Math.floor((x0 + (x1 - x0) * t - ox) / NAV_CELL);
      const r = Math.floor((y0 + (y1 - y0) * t - oy) / NAV_CELL);
      if (c >= 0 && r >= 0 && c < cols && r < rows) blocked[r * cols + c] = 1;
    }
  };

  paths.forEach((el) => {
    if (isOverlayNode(el) || inTextLayer(el)) return;
    const d = el.getAttribute("d");
    if (!d) return;
    const m = ctmOf(el);
    const segs = pathSegments(d).map(([x0, y0, x1, y1]) => (m
      ? [m.a * x0 + m.c * y0 + m.e, m.b * x0 + m.d * y0 + m.f, m.a * x1 + m.c * y1 + m.e, m.b * x1 + m.d * y1 + m.f]
      : [x0, y0, x1, y1]));
    if (isGlyphLike(el, segs)) return;
    segs.forEach(([p0x, p0y, p1x, p1y]) => {
      if (Math.hypot(p1x - p0x, p1y - p0y) < MIN_INK_SEG) return;
      mark(p0x, p0y, p1x, p1y);
    });
  });

  // Flood fill the free space. Stalls are sealed pockets; the biggest region
  // left over is the aisle network, and routing never leaves it.
  const comp = new Int32Array(cols * rows).fill(-1);
  const sizes = [];
  const stack = [];
  for (let seed = 0; seed < cols * rows; seed++) {
    if (blocked[seed] || comp[seed] !== -1) continue;
    const id = sizes.length;
    let size = 0;
    stack.length = 0;
    stack.push(seed);
    comp[seed] = id;
    while (stack.length) {
      const cur = stack.pop();
      size++;
      const r = (cur / cols) | 0;
      const c = cur - r * cols;
      if (c + 1 < cols && !blocked[cur + 1] && comp[cur + 1] === -1) { comp[cur + 1] = id; stack.push(cur + 1); }
      if (c - 1 >= 0 && !blocked[cur - 1] && comp[cur - 1] === -1) { comp[cur - 1] = id; stack.push(cur - 1); }
      if (r + 1 < rows && !blocked[cur + cols] && comp[cur + cols] === -1) { comp[cur + cols] = id; stack.push(cur + cols); }
      if (r - 1 >= 0 && !blocked[cur - cols] && comp[cur - cols] === -1) { comp[cur - cols] = id; stack.push(cur - cols); }
    }
    sizes.push(size);
  }
  let main = -1;
  let best = -1;
  sizes.forEach((s, i) => { if (s > best) { best = s; main = i; } });

  // Cells adjacent to a wall cost more, so routes run down aisle centres.
  const hugging = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (comp[i] !== main) continue;
      for (let d = 0; d < NAV_DIRS.length && !hugging[i]; d++) {
        const nr = r + NAV_DIRS[d].dy;
        const nc = c + NAV_DIRS[d].dx;
        if (nr < 0 || nc < 0 || nr >= rows || nc >= cols || comp[nr * cols + nc] !== main) hugging[i] = 1;
      }
    }
  }

  return { cols, rows, blocked, comp, main, hugging, ox, oy, aisleCells: best };
};

// A booth centre sits inside its own sealed stall, so step out to the closest
// cell that is actually on the aisle network - the stall's opening.
const nearestAisleCell = (grid, x, y, maxRadius = 60) => {
  const { cols, rows, comp, main } = grid;
  const col0 = Math.floor((x - grid.ox) / NAV_CELL);
  const row0 = Math.floor((y - grid.oy) / NAV_CELL);
  for (let radius = 0; radius <= maxRadius; radius++) {
    let best = null;
    let bestD = Infinity;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (radius > 0 && Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue;
        const c = col0 + dc;
        const r = row0 + dr;
        if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
        if (comp[r * cols + c] !== main) continue;
        const d = dr * dr + dc * dc;
        if (d < bestD) { bestD = d; best = { col: c, row: r }; }
      }
    }
    if (best) return best;
  }
  return null;
};

// Dijkstra over (cell, heading), confined to the aisle network. Heading is part
// of the state so a turn can be charged for.
const dijkstraOnGrid = (grid, start, goal) => {
  const { cols, rows, comp, main, hugging } = grid;
  const stateCount = cols * rows * 4;

  const dist = new Float64Array(stateCount).fill(Infinity);
  const prev = new Int32Array(stateCount).fill(-1);
  const settled = new Uint8Array(stateCount);

  const startCell = start.row * cols + start.col;
  const goalCell = goal.row * cols + goal.col;
  if (comp[startCell] !== main || comp[goalCell] !== main) return null;

  const heap = new MinHeap();
  for (let d = 0; d < 4; d++) {
    dist[startCell * 4 + d] = 0;
    heap.push(startCell * 4 + d, 0);
  }

  let goalState = -1;
  while (heap.size > 0) {
    const state = heap.pop();
    if (settled[state]) continue;
    settled[state] = 1;

    const cell = state >> 2;
    const dir = state & 3;
    if (cell === goalCell) { goalState = state; break; }

    const row = (cell / cols) | 0;
    const col = cell - row * cols;

    for (let nd = 0; nd < 4; nd++) {
      const nc = col + NAV_DIRS[nd].dx;
      const nr = row + NAV_DIRS[nd].dy;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const ncell = nr * cols + nc;
      if (comp[ncell] !== main) continue;

      const nstate = ncell * 4 + nd;
      if (settled[nstate]) continue;

      const step = 1 + (hugging[ncell] ? HUG_COST : 0) + (nd === dir ? 0 : TURN_COST);
      const alt = dist[state] + step;
      if (alt < dist[nstate]) {
        dist[nstate] = alt;
        prev[nstate] = state;
        heap.push(nstate, alt);
      }
    }
  }

  if (goalState < 0) return null;

  const cells = [];
  for (let s = goalState; s !== -1; s = prev[s]) {
    const cell = s >> 2;
    if (cells.length === 0 || cells[cells.length - 1] !== cell) cells.push(cell);
    if (prev[s] === -1) break;
  }
  cells.reverse();
  return cells.map((cell) => {
    const row = (cell / cols) | 0;
    return { col: cell - row * cols, row };
  });
};

// Collapse collinear runs so the drawn polyline has corners, not 400 points.
const simplifyCells = (cells) => {
  if (!cells || cells.length < 3) return cells || [];
  const out = [cells[0]];
  for (let i = 1; i < cells.length - 1; i++) {
    const a = out[out.length - 1];
    const b = cells[i];
    const c = cells[i + 1];
    const straight = (b.col - a.col) * (c.row - b.row) === (b.row - a.row) * (c.col - b.col);
    if (!straight) out.push(b);
  }
  out.push(cells[cells.length - 1]);
  return out;
};

const cellToPoint = (grid, cell) => ({
  x: grid.ox + (cell.col + 0.5) * NAV_CELL,
  y: grid.oy + (cell.row + 0.5) * NAV_CELL,
});

const isOnsiteAgencyService = (name) => String(name || "").trim().toLowerCase() === "onsite agency";

// One staff member line: name, designation, phases, mobile, email.
const renderStaffMember = (m) => (
  <div key={m.uid} className="small text-muted pt-1 border-top mt-1" style={{ fontSize: '11px' }}>
    <div className="d-flex justify-content-between align-items-center">
      <span className="text-dark fw-semibold">
        <i className="ri-user-3-line me-1"></i>{m.name || "--"}
        {m.designation ? <span className="fw-normal text-muted"> · {m.designation}</span> : null}
      </span>
      <span className="d-inline-flex gap-1 flex-wrap justify-content-end">
        {m.phases.map((ph) => (
          <span key={ph} className="badge bg-light text-dark border" style={{ fontSize: '9.5px' }}>{ph}</span>
        ))}
      </span>
    </div>
    {m.mobile && <div><i className="ri-phone-line me-1"></i>{m.mobile}</div>}
    {m.email && <div className="text-break"><i className="ri-mail-line me-1"></i>{m.email}</div>}
  </div>
);

// A hall's service partner team (result set 0's TeamMembers), one card per
// service. Shared by the booth popup's own section and the team modal.
const renderTeamCards = (team, emptyText) => {
  if (!team || team.length === 0) {
    return <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">{emptyText}</div>;
  }
  const byService = new Map();
  team.forEach((m) => {
    if (!byService.has(m.service)) byService.set(m.service, []);
    byService.get(m.service).push(m);
  });
  return Array.from(byService.entries()).map(([service, members]) => (
    <div key={service} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
      <div className="p-2">
        <div className="fw-semibold text-dark mb-1" style={{ fontSize: '12px' }}>{service}</div>
        {members.map(renderStaffMember)}
      </div>
    </div>
  ));
};

// A hall's Onsite Agency (agency contact, hall managers, team) for its modal.
const renderOnsiteAgency = (agencies, hallName) => {
  if (!agencies || agencies.length === 0) {
    return (
      <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
        No Onsite Agency mapped to {hallName || "this hall"}.
      </div>
    );
  }
  const block = (title, icon, members, emptyText) => (
    <div className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
      <div className="p-2">
        <div className="fw-semibold text-dark mb-1 d-flex justify-content-between" style={{ fontSize: '12px' }}>
          <span><i className={`${icon} me-1 text-primary`}></i>{title}</span>
          {members.length > 0 && <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>{members.length}</span>}
        </div>
        {members.length > 0
          ? members.map(renderStaffMember)
          : <div className="small text-muted fst-italic" style={{ fontSize: '11px' }}>{emptyText}</div>}
      </div>
    </div>
  );
  return agencies.map((a) => (
    <div key={a.vendorID ?? a.company} className="mb-2">
      <div className="border rounded p-2 mb-2" style={{ backgroundColor: '#f0fdfa', fontSize: '11.5px' }}>
        <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
          <i className="ri-building-4-line me-1"></i>{a.company}
        </div>
        {(a.contactPerson || a.contactMobile || a.contactEmail) && (
          <div className="text-muted mt-1">
            {a.contactPerson && <div><i className="ri-user-3-line me-1"></i>{a.contactPerson}</div>}
            {a.contactMobile && <div><i className="ri-phone-line me-1"></i>{a.contactMobile}</div>}
            {a.contactEmail && <div className="text-break"><i className="ri-mail-line me-1"></i>{a.contactEmail}</div>}
          </div>
        )}
      </div>
      {block(`Hall Managers · ${hallName || "this hall"}`, "ri-shield-user-line", a.hallManagers, `No hall manager mapped to ${hallName || "this hall"}.`)}
      {block("Team", "ri-team-line", a.team, "No other team members.")}
    </div>
  ));
};

const SvgCanvas = ({
  layout,
  viewFilter = "stall", // "stall" | "zone" | "hall"
  dataFilters = ["service_partner", "exhibitor_orders", "daily_routine"],
  onViewFilterChange,
  onDataFilterChange,
  hallZoneMasterRows = [],
  servicePartnerRows = [],
  exhibitorOrderRows = [],
  dailyRoutineRows = [],
  // Result set 4 (data4): stall possession, one row per exhibitor per stall.
  stallPossessionRows,
  // Service partner deliverables + checklist sub points (sp_get_ExhVendorTaskDetails).
  deliverableRows,
  // Stall -> { hall, zone, zoneId } from data2's stall master (buildStallHomes).
  stallHomes,
  filterHallId,
  filterBoothId,
  onFilterHallChange,
  onFilterBoothChange,
  onSvgDataExtracted,
  zones = [],
  selectedZoneId = "",
  onZoneChange,
  // A stall clicked directly on the Zone Status map: sets the hall AND the
  // zone together (see layoutView.js's handleZoneStallFocus for why plain
  // onZoneChange alone is not enough here).
  onZoneStallFocus,
  isZoneLoading = false,
  zoneStalls = [],
  statusCounts = {},
  isStatusLoading = false,
  taskRows = [],
  exhibitorRows = [],
  isTaskLoading = false,
  taskLoadError = null,
  services = [],
  selectedServiceId = "",
  onServiceChange,
  // Verification state per deliverable, keyed by ExhVendorPlanDtlID. Only the
  // Organizer can move it, the same rule the vendor task screen applies
  verificationById = {},
  onOpenTaskDetail,
  // A daily routine row clicked in a popup -> its update screen (edit mode).
  onOpenDailyRoutine,
  onOpenManualForm,
  // ({ name, role, booth, mobile, email, channels, message }) => void. Not
  // passed yet - there is no send endpoint - so Send only confirms for now.
  onSendNotification,
  // ── Several layouts stacked (layoutView renders one SvgCanvas per layout) ──
  // compact: no title / toolbar / legend - only the layout's name above its map;
  // the first layout's toolbar drives all of them.
  compact = false,
  // Toolbar lists across ALL layouts (default: this drawing's own).
  hallOptions = null,
  boothOptions = null,
  // The selection shown in the toolbar, which may be a hall / booth of another
  // layout (filterHallId / filterBoothId are only ever this layout's own).
  toolbarHallId,
  toolbarBoothId,
  // Legend counts summed over all layouts, and this layout's own counts out.
  legendCounts = null,
  onStatusCountsChange,
  // The other layouts, rendered inside this one's root so Full Screen holds them.
  children,
  // The hall list carries a "layout:<id>" entry per layout (the whole drawing)
  // in place of "All Halls" - one drawing never holds every hall.
  hideAllHallsOption = false,
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipContentRef = useRef(null);
  const tooltipDotRef = useRef(null);

  // Multi-mode filter local states (supporting controlled & uncontrolled)
  const [internalViewFilter, setInternalViewFilter] = useState("stall");
  const [internalDataFilters, setInternalDataFilters] = useState(["service_partner", "exhibitor_orders", "daily_routine"]);
  const [isDataFilterOpen, setIsDataFilterOpen] = useState(false);
  const dataFilterRef = useRef(null);

  // Close the data filter on a click outside it or on Escape, like a native select.
  useEffect(() => {
    if (!isDataFilterOpen) return undefined;
    const onPointer = (e) => {
      if (dataFilterRef.current && !dataFilterRef.current.contains(e.target)) setIsDataFilterOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setIsDataFilterOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [isDataFilterOpen]);

  // Halls/booths read out of the drawing. These drive the dropdowns.
  const [svgData, setSvgData] = useState({ halls: [], booths: [] });

  // Other areas (halls with no booths: banquet / conference / food court) -
  // flagged IsOtherArea in result set 0, or drawn as an area (layoutAreas.js).
  // They have no stalls or zones, so they are shown in Hall View only.
  const areaHallKeys = useMemo(() => {
    const keys = otherAreaHallKeys(hallZoneMasterRows);
    svgData.halls.forEach((h) => { if (h.isOtherArea) keys.add(hallNameKey(h.HallName)); });
    return keys;
  }, [hallZoneMasterRows, svgData.halls]);
  const isOtherAreaHall = useCallback(
    (h) => !!h && (!!h.isOtherArea || areaHallKeys.has(hallNameKey(h.HallName))),
    [areaHallKeys]
  );
  // Service partner team members per hall, from result set 0's TeamMembers
  // (JSON from the SP: staff flagged as hall managers on the work orders, with
  // the halls they are mapped to). hall key -> [{ service, phase, name, ... }].
  const teamByHall = useMemo(() => {
    const map = new Map();
    (hallZoneMasterRows || []).forEach((h) => {
      let list = h.TeamMembers;
      if (typeof list === "string") {
        try { list = JSON.parse(list); } catch (e) { list = null; }
      }
      if (!Array.isArray(list) || list.length === 0) return;
      const key = hallNameKey(h.HallName);
      if (!map.has(key)) map.set(key, []);
      // The same person is a separate staff record in each phase (PreSetup,
      // Event, Dismantling), so they arrive once per phase. One entry per
      // person per service, carrying every phase they work in.
      const members = map.get(key);
      const PHASE_ORDER = ["PreSetup", "Event", "Dismantling"];
      list.forEach((m) => {
        // The Onsite Agency is shown on its own (onsiteAgencyByHall), not as a
        // service partner.
        if (isOnsiteAgencyService(m.ServiceName)) return;
        const person = String(m.PersonName || "").trim().toLowerCase();
        const uid = [m.ServiceID, person, String(m.MobileNumber || "").trim() || String(m.EmailID || "").trim().toLowerCase()].join("|");
        let entry = members.find((x) => x.uid === uid);
        if (!entry) {
          entry = {
            uid,
            vendorID: m.VendorID,
            serviceID: m.ServiceID,
            service: m.ServiceName || "Service",
            phases: [],
            name: m.PersonName,
            designation: m.Designation,
            mobile: m.MobileNumber,
            email: m.EmailID,
          };
          members.push(entry);
        }
        if (m.Phase && !entry.phases.includes(m.Phase)) {
          entry.phases.push(m.Phase);
          entry.phases.sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));
        }
        if (!entry.designation && m.Designation) entry.designation = m.Designation;
        if (!entry.email && m.EmailID) entry.email = m.EmailID;
      });
    });
    return map;
  }, [hallZoneMasterRows]);

  // Onsite Agency per hall, from result set 0's OnsiteAgency (JSON from the
  // SP: the agency's hall managers mapped to the hall, plus its team that is
  // not mapped to any hall). hall key -> [{ vendorID, company, contact...,
  // hallManagers: [member], team: [member] }], one entry per agency.
  const onsiteAgencyByHall = useMemo(() => {
    const map = new Map();
    const PHASE_ORDER = ["PreSetup", "Event", "Dismantling"];
    (hallZoneMasterRows || []).forEach((h) => {
      let list = h.OnsiteAgency;
      if (typeof list === "string") {
        try { list = JSON.parse(list); } catch (e) { list = null; }
      }
      if (!Array.isArray(list) || list.length === 0) return;
      const agencies = [];
      list.forEach((m) => {
        let agency = agencies.find((a) => a.vendorID === m.VendorID);
        if (!agency) {
          agency = {
            vendorID: m.VendorID,
            company: m.CompanyName || "Onsite Agency",
            contactPerson: m.ContactPerson,
            contactMobile: m.ContactMobile,
            contactEmail: m.ContactEmail,
            hallManagers: [],
            team: [],
          };
          agencies.push(agency);
        }
        const bucket = m.IsHallManager === true || m.IsHallManager === 1 ? agency.hallManagers : agency.team;
        // One entry per person, carrying every phase they work in.
        const person = String(m.PersonName || "").trim().toLowerCase();
        const uid = [person, String(m.MobileNumber || "").trim() || String(m.EmailID || "").trim().toLowerCase()].join("|");
        let entry = bucket.find((x) => x.uid === uid);
        if (!entry) {
          entry = { uid, name: m.PersonName, designation: m.Designation, mobile: m.MobileNumber, email: m.EmailID, phases: [] };
          bucket.push(entry);
        }
        if (m.Phase && !entry.phases.includes(m.Phase)) {
          entry.phases.push(m.Phase);
          entry.phases.sort((a, b) => PHASE_ORDER.indexOf(a) - PHASE_ORDER.indexOf(b));
        }
        if (!entry.designation && m.Designation) entry.designation = m.Designation;
        if (!entry.email && m.EmailID) entry.email = m.EmailID;
      });
      map.set(hallNameKey(h.HallName), agencies);
    });
    return map;
  }, [hallZoneMasterRows]);

  const otherAreaNameOf = useCallback((hallName) => {
    const row = (hallZoneMasterRows || []).find((h) => hallNameKey(h.HallName) === hallNameKey(hallName));
    if (!row || !row.OtherAreaName) return "Other Area";
    return row.OtherAreaRoom ? `${row.OtherAreaName} · ${row.OtherAreaRoom}` : row.OtherAreaName;
  }, [hallZoneMasterRows]);
  // Only other areas in view (the picked hall, or every hall of this drawing):
  // Stall and Zone View have nothing to show, so the drawing is in Hall View.
  const hallsInView = filterHallId ? svgData.halls.filter((h) => h.HallID === filterHallId) : svgData.halls;
  const otherAreaOnly = hallsInView.length > 0 && hallsInView.every(isOtherAreaHall);
  const requestedViewFilter = viewFilter || internalViewFilter;
  const currentViewFilter =
    otherAreaOnly && (requestedViewFilter === "stall" || requestedViewFilter === "zone") ? "hall" : requestedViewFilter;
  // What the user picked in the Data dropdown - kept across views.
  const selectedDataFilters = dataFilters || internalDataFilters;
  // What the current view actually uses: the picked sources it allows
  // (VIEW_DATA_SOURCES), or its first task source when none of them is picked.
  const viewDataOptions = useMemo(() => {
    const allowed = VIEW_DATA_SOURCES[currentViewFilter];
    return allowed ? DATA_FILTER_OPTIONS.filter((o) => allowed.includes(o.key)) : DATA_FILTER_OPTIONS;
  }, [currentViewFilter]);
  const currentDataFilters = useMemo(() => {
    const kept = selectedDataFilters.filter((k) => viewDataOptions.some((o) => o.key === k));
    if (kept.length > 0) return kept;
    const first = viewDataOptions.find((o) => !o.exclusive);
    return first ? [first.key] : kept;
  }, [selectedDataFilters, viewDataOptions]);

  const handleViewFilterSelect = (newView) => {
    setInternalViewFilter(newView);
    if (onViewFilterChange) onViewFilterChange(newView);
  };

  const handleToggleDataFilter = (filterKey) => {
    let next;
    if (currentDataFilters.includes(filterKey)) {
      if (currentDataFilters.length === 1) return; // keep at least 1 active
      next = selectedDataFilters.filter((k) => k !== filterKey);
    } else if (EXCLUSIVE_DATA_FILTERS.includes(filterKey)) {
      // Single select: picking it replaces everything else.
      next = [filterKey];
    } else {
      // Picking a task source leaves single-select mode. Leaving it from a
      // view that offers only some sources (Stall View) brings back the full
      // task set, so Hall / Zone / Venue View are not left with just one.
      const base = selectedDataFilters.filter((k) => !EXCLUSIVE_DATA_FILTERS.includes(k));
      next = base.length === 0 && VIEW_DATA_SOURCES[currentViewFilter]
        ? DEFAULT_TASK_SOURCES.slice()
        : [...base, filterKey];
      if (!next.includes(filterKey)) next.push(filterKey);
    }
    setInternalDataFilters(next);
    if (onDataFilterChange) onDataFilterChange(next);
  };

  // Route state
  const [routeFromBoothId, setRouteFromBoothId] = useState("");
  const [routeToBoothId, setRouteToBoothId] = useState("");
  const [selectedBooth, setSelectedBooth] = useState(null);
  // The zone / hall cell clicked in Zone View / Hall View:
  // { kind: "zone" | "hall", hallName, zoneKey, zoneName }, or null.
  const [selectedArea, setSelectedArea] = useState(null);
  // The contact the Notify modal is open for, or null.
  const [notifyTarget, setNotifyTarget] = useState(null);
  const closeNotify = useCallback(() => setNotifyTarget(null), []);
  // Service partner team of the booth's hall, opened from the info icon by
  // Exhibitor Orders: { hallName, team }.
  const [teamModal, setTeamModal] = useState(null);
  const closeTeamModal = useCallback(() => setTeamModal(null), []);
  useEffect(() => {
    if (!teamModal) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeTeamModal(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [teamModal, closeTeamModal]);
  const handleSendNotification = useCallback((req) => {
    if (onSendNotification) {
      onSendNotification(req);
      return;
    }
    const via = req.channels.map((c) => (c === "sms" ? "SMS" : c === "whatsapp" ? "WhatsApp" : "Email")).join(", ");
    toast.info(`Notification to ${req.name || "contact"} via ${via} - sending is not connected yet.`);
  }, [onSendNotification]);
  const [routeError, setRouteError] = useState("");

  const boothNodesRef = useRef({});
  const navGridRef = useRef(null);
  const nodeBoothMapRef = useRef({});
  const clipIndexRef = useRef({ elementsByClip: new Map(), clipPathDefsById: new Map(), elementsByBoothName: new Map(), textElementsByContent: new Map() });

  const transform = useRef({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    isDragging: false,
    hasDragged: false,
    dragStart: { x: 0, y: 0 },
    dragInitial: { x: 0, y: 0 }
  });

  const zoomLabelRef = useRef(null);
  const willChangeTimeoutRef = useRef(null);
  // Bumped once the booth dots exist in the DOM, so the effect that colours
  // them for the service filter can run after they are created rather than
  // racing the parse.
  const [dotsVersion, setDotsVersion] = useState(0);
  const [isProcessingFilter, setIsProcessingFilter] = useState(false);
  const [filterMessage, setFilterMessage] = useState("");
  const [focusError, setFocusError] = useState("");

  const boothElementsRef = useRef(new Map());
  // Booth name -> the stall border found in the drawing, in root user space.
  const boothOutlinesRef = useRef(new Map());

  // Size the canvas to the drawing's own aspect ratio. Letterboxing a 1.29:1
  // plan inside a 3.3:1 box can only ever fill ~39% of it; matching the box to
  // the drawing means the map itself has no dead space at all.
  const canvasWrapRef = useRef(null);
  const [svgAspect, setSvgAspect] = useState(null);
  const [canvasBox, setCanvasBox] = useState({ width: null, height: 500 });

  // Full screen: the whole PAGE goes into the browser's fullscreen and this
  // component is laid over the viewport as a fixed layer (FULLSCREEN_BODY_CLASS).
  // Fullscreening only this component hid every popup attached to <body> -
  // the task screen, the manual forms and the dialogs they open themselves -
  // behind it. Popups already stack above the layer (see
  // FULLSCREEN_ROOT_STYLE); the map grows to fill it. Esc (or the button) leaves; the state follows the browser.
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const wantsFullscreenRef = useRef(false);
  useEffect(() => {
    const onChange = () => {
      // Every stacked layout hears this event; only the one that asked for
      // full screen owns the body class.
      const mine = wantsFullscreenRef.current;
      const on = mine && document.fullscreenElement === document.documentElement;
      if (!on) wantsFullscreenRef.current = false;
      if (mine) document.body.classList.toggle(FULLSCREEN_BODY_CLASS, on);
      setIsFullscreen(on);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      // Only the instance that went full screen undoes it - several layouts
      // are stacked, and the others unmount while the first stays full screen.
      if (wantsFullscreenRef.current) {
        document.body.classList.remove(FULLSCREEN_BODY_CLASS);
        if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      }
    };
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (document.documentElement.requestFullscreen) {
      wantsFullscreenRef.current = true;
      document.documentElement.requestFullscreen().catch(() => {
        wantsFullscreenRef.current = false;
      });
    }
  }, []);

  // Once the user pans or zooms, stop re-imposing the default view on them.
  const hasUserAdjustedRef = useRef(false);
  const defaultViewAppliedRef = useRef(false);

  const booths = svgData.booths;
  const halls = svgData.halls;
  const toolbarHalls = hallOptions || halls;
  const toolbarBoothList = boothOptions || booths;
  const shownHallId = toolbarHallId !== undefined ? toolbarHallId : filterHallId;
  const shownBoothId = toolbarBoothId !== undefined ? toolbarBoothId : filterBoothId;
  // Whether a hall / booth is in THIS drawing (only then does it zoom here).
  const ownsHall = (id) => halls.some((h) => h.HallID === id);
  const ownsBooth = (id) => booths.some((bt) => bt.BoothName === id);

  const isServicePartnerActive = currentDataFilters.includes("service_partner");
  const isExhibitorOrdersActive = currentDataFilters.includes("exhibitor_orders");
  const isDailyRoutineActive = currentDataFilters.includes("daily_routine");
  const isPossessionMode = currentDataFilters.includes("stall_possession");

  const normalizeKey = (str) => String(str || "").trim().toLowerCase();

  // Effective datasets combining explicit props and fallbacks
  const effectiveServicePartnerRows = (servicePartnerRows && servicePartnerRows.length > 0) ? servicePartnerRows : (exhibitorRows || NO_ROWS);
  const effectiveExhibitorOrderRows = exhibitorOrderRows || NO_ROWS;
  const effectiveDailyRoutineRows = dailyRoutineRows || NO_ROWS;

  // With a service picked, a row counts only if it is for that service. A row
  // with no ServiceID is not for any service, so it never passes.
  const matchesService = useCallback(
    (svcId) => !selectedServiceId || (svcId != null && String(svcId) === String(selectedServiceId)),
    [selectedServiceId]
  );

  // The tracker rows that belong to each drawn booth, joined within the booth's
  // own hall (the DB has "H3-C2" in Hall 3 and "H3C2" in Hall 1 as different
  // stalls, which a punctuation-stripped key alone would merge).
  const servicePartnerRowsByBooth = useMemo(
    () => attachRowsToBooths(booths, effectiveServicePartnerRows, (r) => r.StallName, (r) => r.HallName),
    [booths, effectiveServicePartnerRows]
  );
  const orderRowsByBooth = useMemo(
    () => attachRowsToBooths(booths, effectiveExhibitorOrderRows, (r) => r.Stall || r.StallName, (r) => r.Hall || r.HallName),
    [booths, effectiveExhibitorOrderRows]
  );

  const deliverableIndex = useMemo(() => buildDeliverableIndex(deliverableRows), [deliverableRows]);

  // Stall possession (data4), joined the same hall-scoped way. A stall with
  // several exhibitors reads as its least advanced one (rollupPossession).
  const possessionRows = stallPossessionRows || NO_ROWS;
  const possessionRowsByBooth = useMemo(
    () => attachRowsToBooths(booths, possessionRows, (r) => r.StallName, (r) => r.HallName),
    [booths, possessionRows]
  );
  const possessionByBooth = useMemo(() => {
    const map = new Map();
    possessionRowsByBooth.forEach((rows, boothName) => {
      const st = rollupPossession(rows.map((r) => r.PossessionStatus));
      if (st) map.set(boothName, st);
    });
    return map;
  }, [possessionRowsByBooth]);

  // 1. Status per stall (for Stall View), keyed by the drawing's BoothName.
  const { statusByBooth, stallStatsDetail } = useMemo(() => {
    const stallTasks = new Map();
    const tasksOf = (boothName) => {
      if (!stallTasks.has(boothName)) stallTasks.set(boothName, []);
      return stallTasks.get(boothName);
    };

    booths.forEach((b) => {
      if (!isServicePartnerActive) return;
      (servicePartnerRowsByBooth.get(b.BoothName) || []).forEach((r) => {
        const svcId = firstValue(r.ServiceID);
        if (!matchesService(svcId)) return;

        const statusStr = servicePartnerRowStatus(r);

        tasksOf(b.BoothName).push({
          status: statusStr,
          serviceId: svcId,
          source: "service_partner",
          overdue: r.Overdue,
          vendor: r.VendorCompanyName || r.vendorPersonName,
        });
      });
    });

    booths.forEach((b) => {
      if (!isExhibitorOrdersActive) return;
      (orderRowsByBooth.get(b.BoothName) || []).forEach((r) => {
        const svcId = r.ServiceID;
        if (!matchesService(svcId)) return;

        const statusStr = orderStatusOf(r);

        tasksOf(b.BoothName).push({
          status: statusStr,
          serviceId: svcId,
          source: "exhibitor_orders",
          serviceName: r.Service,
          vendor: r.VendorName,
        });
      });
    });

    // Daily routine checklists are kept per hall - the data has no stall - so
    // on its own it colours every booth of a hall with that hall's routine
    // status. Alongside a stall-level source the stall's own tasks decide;
    // a hall-wide checklist would otherwise wash every booth to one colour.
    if (isDailyRoutineActive && !isServicePartnerActive && !isExhibitorOrdersActive) {
      const routineByHall = dailyRoutineStatusesByHall(effectiveDailyRoutineRows);
      booths.forEach((b) => {
        (routineByHall.get(hallNameKey(b.HallName)) || []).forEach((st) => {
          tasksOf(b.BoothName).push({ status: st, source: "daily_routine" });
        });
      });
    }

    const map = new Map();
    stallTasks.forEach((tasks, key) => {
      if (!tasks || tasks.length === 0) {
        map.set(key, null);
        return;
      }
      // Delayed > InProgress > Pending > Completed (rollupTaskStatus).
      map.set(key, rollupTaskStatus(tasks.map((t) => t.status)));
    });

    return { statusByBooth: map, stallStatsDetail: stallTasks };
  }, [booths, isServicePartnerActive, isExhibitorOrdersActive, isDailyRoutineActive, effectiveDailyRoutineRows, servicePartnerRowsByBooth, orderRowsByBooth, matchesService]);

  // Zone names repeat in every hall ("Zone 1"), so a zone is always keyed by
  // hall and name together (zoneKeyOf) - never by name alone.
  const masterZones = useMemo(() => expandHallZoneMaster(hallZoneMasterRows), [hallZoneMasterRows]);
  const selectedHallName = (halls.find((h) => h.HallID === filterHallId) || {}).HallName || "";
  // The zone API's rows name their hall; fall back to the selected one.
  const zoneHallOf = useCallback(
    (z) => z.HallName || z.Hall || selectedHallName,
    [selectedHallName]
  );
  // Zones of the selected hall only (all halls when none is picked). The zone
  // API is preferred, the hall master list is the fallback, and either is
  // filtered by hall - a row that names another hall is never offered.
  const zonesInScope = useMemo(() => {
    const source = zones && zones.length > 0 ? zones : masterZones;
    if (!selectedHallName) return source;
    const hk = hallNameKey(selectedHallName);
    return source.filter((z) => hallNameKey(zoneHallOf(z)) === hk);
  }, [zones, masterZones, selectedHallName, zoneHallOf]);

  // The Zone dropdown: this hall's zones, one entry per name (the master can
  // list one zone name under two mapping ids, e.g. Hall 1's "Zone 2").
  const zoneOptions = useMemo(() => {
    if (!selectedHallName) return [];
    const seen = new Set();
    return zonesInScope.filter((z) => {
      const k = hallNameKey(z.Zone || z.ZoneName);
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [zonesInScope, selectedHallName]);

  // Hall View and Zone View work with one hall or with All Halls - there is
  // no forced default hall any more.

  // "hall::zone" -> loose keys of the stalls in it. A stall is in exactly ONE
  // zone of its hall. The sources do not always agree, and taking their union
  // put a stall into every zone any source named - so the first, most specific
  // source to place a stall wins: the zone API (the hall's own zone mapping),
  // then data2's stall master, then orders, then tasks.
  const zoneMembers = useMemo(() => {
    const zoneOfStall = new Map(); // "hall|stallKey" -> zoneKey
    const place = (hall, zone, stall) => {
      const zk = hallNameKey(hall) ? zoneKeyOf(hall, zone) : "";
      const bk = normalizeBoothKey(stall);
      if (!zk || !bk) return;
      const id = `${hallNameKey(hall)}|${bk}`;
      if (!zoneOfStall.has(id)) zoneOfStall.set(id, zk);
    };

    const zoneNameById = new Map();
    (zones || []).forEach((z) => zoneNameById.set(String(z.HallZoneMappingID), z.Zone || z.ZoneName));
    (zoneStalls || []).forEach((s) =>
      place(
        selectedHallName,
        s.Zone || s.ZoneName || zoneNameById.get(String(s.HallZoneMappingID)),
        s.StallName || s.Stall
      )
    );
    (stallHomes || new Map()).forEach((home, stall) => place(home.hall, home.zone, stall));
    effectiveExhibitorOrderRows.forEach((r) =>
      place(r.Hall || r.HallName, r.Zone || r.ZoneName, r.Stall || r.StallName)
    );
    effectiveServicePartnerRows.forEach((r) =>
      place(r.HallName, firstValue(r.ZoneName) || firstValue(r.Zone), r.StallName)
    );

    const members = new Map();
    zoneOfStall.forEach((zk, id) => {
      const bk = id.slice(id.indexOf("|") + 1);
      if (!members.has(zk)) members.set(zk, new Set());
      members.get(zk).add(bk);
    });
    return members;
  }, [zones, zoneStalls, selectedHallName, stallHomes, effectiveServicePartnerRows, effectiveExhibitorOrderRows]);

  // 2. Status per Zone (for Zone Status View), keyed by zoneKeyOf(hall, zone)
  const trackerZoneStatusMap = useMemo(() => {
    const zoneTasks = new Map();
    const push = (hall, zone, stat) => {
      const key = hallNameKey(hall) ? zoneKeyOf(hall, zone) : "";
      if (!key) return;
      if (!zoneTasks.has(key)) zoneTasks.set(key, []);
      zoneTasks.get(key).push(stat);
    };

    // Each task's own status - not the SP's precomputed ZoneStatus, which
    // rolls up differently (it never says Delayed) - so a zone follows the
    // same worst-task rule as its booths.
    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        if (!matchesService(firstValue(r.ServiceID))) return;
        push(r.HallName, firstValue(r.ZoneName) || firstValue(r.Zone), servicePartnerRowStatus(r));
      });
    }

    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        if (!matchesService(r.ServiceID)) return;
        push(r.Hall || r.HallName, r.Zone || r.ZoneName, orderStatusOf(r));
      });
    }

    const map = new Map();
    masterZones.concat(zones || []).forEach((z) => {
      const k = zoneKeyOf(zoneHallOf(z), z.Zone || z.ZoneName);
      if (k) map.set(k, "Pending");
    });

    // Daily routines are per hall, so each of a hall's routines votes in every
    // zone of that hall - the same rows Hall View rolls up, and the ones the
    // zone's detail panel lists. Leaving them out whenever another source was
    // on showed a zone Completed while its own panel counted an InProgress
    // routine.
    if (isDailyRoutineActive) {
      const routineByHall = dailyRoutineStatusesByHall(effectiveDailyRoutineRows);
      map.forEach((_, key) => {
        const [hallKey, zoneName] = key.split("::");
        (routineByHall.get(hallKey) || []).forEach((st) => push(hallKey, zoneName, st));
      });
    }

    zoneTasks.forEach((stats, key) => {
      if (!stats || stats.length === 0) return;
      map.set(key, rollupTaskStatus(stats));
    });

    return map;
  }, [isServicePartnerActive, isExhibitorOrdersActive, isDailyRoutineActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, effectiveDailyRoutineRows, masterZones, zones, zoneHallOf, matchesService]);

  // 3. Status per Hall (for Hall Status View)
  const { hallStatusMap: trackerHallStatusMap, hallDailyRoutineSummary } = useMemo(() => {
    const hallStats = new Map();
    const dailySummary = new Map();

    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        const hName = r.HallName || r.Hall;
        if (!hName) return;
        const key = normalizeKey(hName);
        if (!matchesService(firstValue(r.ServiceID))) return;

        // Each task's own status (not the SP's HallStatus) - see the zone map.
        const stat = servicePartnerRowStatus(r);
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);
      });
    }

    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        const hName = r.Hall || r.HallName;
        if (!hName) return;
        const key = normalizeKey(hName);
        if (!matchesService(r.ServiceID)) return;

        const stat = orderStatusOf(r);
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);
      });
    }

    if (isDailyRoutineActive) {
      effectiveDailyRoutineRows.forEach((r) => {
        const hName = r.HallName || r.Hall;
        if (!hName) return;
        const key = normalizeKey(hName);
        const stat = dailyRoutineRowStatus(r);
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);

        if (!dailySummary.has(key)) {
          dailySummary.set(key, { total: 0, completed: 0, inProgress: 0, programs: [] });
        }
        const summ = dailySummary.get(key);
        summ.total += Number(r.TotalCount || 0);
        summ.completed += Number(r.TotalCompCount || 0);
        summ.inProgress += Number(r.TotalInPCount || 0);
        summ.programs.push({
          title: r.ProgramTitle,
          times: r.ScheduleTimes,
          responsible: r.ResponsiblePerson,
          status: stat,
        });
      });
    }

    const map = new Map();
    (halls || []).forEach((h) => {
      const k = normalizeKey(h.HallName);
      if (k) map.set(k, "Pending");
    });

    hallStats.forEach((stats, key) => {
      if (!stats || stats.length === 0) return;
      map.set(key, rollupTaskStatus(stats));
    });

    return { hallStatusMap: map, hallDailyRoutineSummary: dailySummary };
  }, [isServicePartnerActive, isExhibitorOrdersActive, isDailyRoutineActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, effectiveDailyRoutineRows, halls, matchesService]);

  // Area-level service partner tasks (no stall) per other-area hall, for its
  // Hall View card.
  const areaTaskCountByHall = useMemo(() => {
    const counts = new Map();
    effectiveServicePartnerRows.forEach((r) => {
      if (r.StallName || !matchesService(firstValue(r.ServiceID))) return;
      const k = normalizeKey(r.HallName);
      if (!areaHallKeys.has(hallNameKey(r.HallName))) return;
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    return counts;
  }, [effectiveServicePartnerRows, matchesService, areaHallKeys]);

  // Possession status per zone and per hall, rolled up from data4's own
  // HallName / Zone columns. Every known zone and hall is pre-filled with
  // "No Record" so nothing falls through to a task status like "Pending".
  const { possessionZoneStatusMap, possessionHallStatusMap } = useMemo(() => {
    const byZone = new Map();
    const byHall = new Map();
    const push = (map, key, st) => {
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(st);
    };
    possessionRows.forEach((r) => {
      push(byZone, hallNameKey(r.HallName) ? zoneKeyOf(r.HallName, r.Zone) : "", r.PossessionStatus);
      push(byHall, normalizeKey(r.HallName), r.PossessionStatus);
    });
    const zoneMap = new Map();
    masterZones.concat(zones || []).forEach((z) => {
      const k = zoneKeyOf(zoneHallOf(z), z.Zone || z.ZoneName);
      if (k) zoneMap.set(k, NO_POSSESSION_RECORD);
    });
    byZone.forEach((list, k) => zoneMap.set(k, rollupPossession(list) || NO_POSSESSION_RECORD));
    const hallMap = new Map();
    (halls || []).forEach((h) => hallMap.set(normalizeKey(h.HallName), NO_POSSESSION_RECORD));
    byHall.forEach((list, k) => hallMap.set(k, rollupPossession(list) || NO_POSSESSION_RECORD));
    return { possessionZoneStatusMap: zoneMap, possessionHallStatusMap: hallMap };
  }, [possessionRows, masterZones, zones, zoneHallOf, halls]);

  const zoneStatusMap = isPossessionMode ? possessionZoneStatusMap : trackerZoneStatusMap;
  const hallStatusMap = isPossessionMode ? possessionHallStatusMap : trackerHallStatusMap;

  // ─── Venue zones (Venue Zone View) ───
  // The SP puts each service partner task and daily routine row in a venue
  // zone (VenueZone 1 / 2, by the hall's position on the venue drawing). A
  // hall's venue zone is read off those rows; orders and possession, which do
  // not carry the column, follow their hall.
  const hallVenueZone = useMemo(() => {
    const map = new Map();
    [...effectiveServicePartnerRows, ...effectiveDailyRoutineRows].forEach((r) => {
      const vz = firstValue(r.VenueZone);
      const hk = hallNameKey(r.HallName || r.Hall);
      if (vz == null || vz === "" || !hk || map.has(hk)) return;
      map.set(hk, String(vz));
    });
    // A hall with no such rows (no tasks under the current filters) still
    // sits in its venue zone - the same fixed mapping the SP uses.
    VENUE_HALLS.forEach((h) => {
      const hk = hallNameKey(h.hallName);
      if (h.venueZone && !map.has(hk)) map.set(hk, h.venueZone);
    });
    return map;
  }, [effectiveServicePartnerRows, effectiveDailyRoutineRows]);
  const venueZoneOfRow = useCallback(
    (r, hallName) => {
      const own = firstValue(r.VenueZone);
      return own != null && own !== "" ? String(own) : hallVenueZone.get(hallNameKey(hallName)) || null;
    },
    [hallVenueZone]
  );

  // Status per venue zone - the worst task decides, as for zones and halls.
  const venueZoneStatusMap = useMemo(() => {
    const byZone = new Map();
    const push = (vz, st) => {
      if (!vz || !st) return;
      if (!byZone.has(vz)) byZone.set(vz, []);
      byZone.get(vz).push(st);
    };
    const map = new Map();
    new Set(hallVenueZone.values()).forEach((vz) => map.set(vz, isPossessionMode ? NO_POSSESSION_RECORD : "Pending"));
    if (isPossessionMode) {
      possessionRows.forEach((r) => push(venueZoneOfRow(r, r.HallName), r.PossessionStatus));
      byZone.forEach((list, vz) => map.set(vz, rollupPossession(list) || NO_POSSESSION_RECORD));
      return map;
    }
    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        if (matchesService(firstValue(r.ServiceID))) push(venueZoneOfRow(r, r.HallName), servicePartnerRowStatus(r));
      });
    }
    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        if (matchesService(r.ServiceID)) push(venueZoneOfRow(r, r.Hall || r.HallName), orderStatusOf(r));
      });
    }
    if (isDailyRoutineActive) {
      effectiveDailyRoutineRows.forEach((r) => push(venueZoneOfRow(r, r.HallName || r.Hall), dailyRoutineRowStatus(r)));
    }
    byZone.forEach((list, vz) => map.set(vz, rollupTaskStatus(list)));
    return map;
  }, [hallVenueZone, venueZoneOfRow, isPossessionMode, possessionRows, isServicePartnerActive, isExhibitorOrdersActive, isDailyRoutineActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, effectiveDailyRoutineRows, matchesService]);

  // Each venue zone drawn as the box around its halls on the venue drawing.
  const venueZones = useMemo(() => {
    const PAD = VENUE_VIEWBOX.width * 0.0065; // a margin round the halls, ~8 units on the current drawing
    return Array.from(new Set(hallVenueZone.values()))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((vz) => {
        const zoneHalls = VENUE_HALLS.filter((h) => hallVenueZone.get(hallNameKey(h.hallName)) === vz);
        if (zoneHalls.length === 0) return null;
        const x0 = Math.min(...zoneHalls.map((h) => h.x)) - PAD;
        const y0 = Math.min(...zoneHalls.map((h) => h.y)) - PAD;
        const x1 = Math.max(...zoneHalls.map((h) => h.x + h.w)) + PAD;
        const y1 = Math.max(...zoneHalls.map((h) => h.y + h.h)) + PAD;
        return {
          id: vz,
          label: `Venue Zone ${vz}`,
          box: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
          // What is drawn: the halls' own outline, not the box round them.
          shape: venueZoneShape(zoneHalls, PAD),
          status: venueZoneStatusMap.get(vz) || null,
          hallCount: zoneHalls.length,
        };
      })
      .filter(Boolean);
  }, [hallVenueZone, venueZoneStatusMap]);

  // What a booth should read as right now. A stall without tasks in active filters is unallocated.
  // `label` is what a tooltip shows: the status, or for a booth with none the
  // legend's own word for that - "Unallocated", or "No Record" in possession.
  const boothDisplayStatus = useCallback((booth) => {
    if (!booth) return { status: null, dimmed: false, label: "" };
    const status = isPossessionMode
      ? possessionByBooth.get(booth.BoothName)
      : statusByBooth.get(booth.BoothName);
    return status
      ? { status, dimmed: false, label: status }
      : { status: null, dimmed: true, label: isPossessionMode ? NO_POSSESSION_RECORD : "Unallocated" };
  }, [statusByBooth, isPossessionMode, possessionByBooth]);

  const boothDisplayStatusRef = useRef(boothDisplayStatus);
  useEffect(() => {
    boothDisplayStatusRef.current = boothDisplayStatus;
  }, [boothDisplayStatus]);

  // A zone / hall panel belongs to the view and hall it was opened in.
  useEffect(() => {
    setSelectedArea(null);
  }, [currentViewFilter, filterHallId]);

  const serviceNameById = useMemo(() => {
    const map = new Map();
    (services || []).forEach((svc) => {
      if (svc && svc.ServiceID != null) {
        map.set(String(svc.ServiceID), svc.Name || svc.DisplayName);
      }
    });
    return map;
  }, [services]);

  const serviceNameOf = (row) =>
    row.name ||
    (row.ServiceID != null ? serviceNameById.get(String(row.ServiceID)) : null) ||
    null;

  // Stall codes belonging to the selected zone.
  const zoneBoothKeys = useMemo(() => {
    if (!selectedZoneId) return null;
    const set = new Set();
    const byId = z => String(z.HallZoneMappingID) === String(selectedZoneId);
    const picked = (zones || []).find(byId) || masterZones.find(byId);
    // Only the picked hall's zone of that name: "Zone 1" exists in every hall.
    const wantedKey = picked ? zoneKeyOf(zoneHallOf(picked), picked.Zone || picked.ZoneName) : "";
    const fromTracker = (wantedKey && zoneMembers.get(wantedKey)) || new Set();
    fromTracker.forEach(k => set.add(k));

    try {
      window.__zoneFocus = {
        selectedZoneId,
        zoneName: (picked && picked.Zone) || null,
        zoneKey: wantedKey || null,
        fromZoneApi: (zoneStalls || []).filter(byId).length,
        zoneApiRowsTotal: (zoneStalls || []).length,
        fromTracker: fromTracker.size,
        trackerNamesZones: effectiveServicePartnerRows.some(
          r => firstValue(r.ZoneName) || firstValue(r.Zone)
        ),
        stalls: Array.from(set),
      };
    } catch (e) { }

    return set;
  }, [zoneStalls, selectedZoneId, zones, masterZones, zoneHallOf, zoneMembers, effectiveServicePartnerRows]);

  const boothMap = useMemo(() => {
    const map = {};
    booths.forEach(b => { map[b.BoothName] = b; });
    return map;
  }, [booths]);

  // Dynamic legend counts according to viewFilter:
  const dynamicStatusCounts = useMemo(() => {
    const counts = isPossessionMode
      ? Object.fromEntries(POSSESSION_LEGEND.map((i) => [i.status, 0]))
      : { Completed: 0, InProgress: 0, Pending: 0, Delayed: 0 };
    if (currentViewFilter === "stall") {
      const seen = new Set();
      booths.forEach((b) => {
        if (seen.has(b.BoothName)) return;
        seen.add(b.BoothName);
        const st = isPossessionMode
          ? possessionByBooth.get(b.BoothName) || NO_POSSESSION_RECORD
          : statusByBooth.get(b.BoothName);
        if (st && counts[st] != null) counts[st] += 1;
      });
    } else if (currentViewFilter === "zone") {
      // Only the zones the map draws: in a hall on the plan, with a drawn stall.
      const drawnByHall = new Map();
      booths.forEach((b) => {
        if (filterHallId && b.HallID !== filterHallId) return;
        const hk = hallNameKey(b.HallName);
        if (!drawnByHall.has(hk)) drawnByHall.set(hk, new Set());
        drawnByHall.get(hk).add(normalizeBoothKey(b.BoothName));
      });
      const seen = new Set();
      zonesInScope.forEach((z) => {
        const k = zoneKeyOf(zoneHallOf(z), z.Zone || z.ZoneName);
        if (!k || seen.has(k)) return;
        const drawn = drawnByHall.get(hallNameKey(zoneHallOf(z)));
        const members = zoneMembers.get(k);
        if (!drawn || !members || !Array.from(members).some((m) => drawn.has(m))) return;
        seen.add(k);
        const st = zoneStatusMap.get(k) || "Pending";
        if (counts[st] != null) counts[st] += 1;
      });
    } else if (currentViewFilter === "venueZone") {
      venueZones.forEach((z) => {
        if (z.status && counts[z.status] != null) counts[z.status] += 1;
      });
    } else if (currentViewFilter === "venue") {
      VENUE_HALLS.forEach((v) => {
        const st = hallStatusMap.get(normalizeKey(v.hallName)) || (isPossessionMode ? NO_POSSESSION_RECORD : null);
        if (st && counts[st] != null) counts[st] += 1;
      });
    } else if (currentViewFilter === "hall") {
      // Hall Status shows one hall, so the legend counts that one hall.
      const shown = filterHallId ? (halls || []).filter(h => h.HallID === filterHallId) : [];
      const allHalls = shown.length > 0 ? shown : halls && halls.length > 0 ? halls : Array.from(hallStatusMap.keys()).map(h => ({ HallName: h }));
      const seen = new Set();
      allHalls.forEach((h) => {
        const k = normalizeKey(h.HallName);
        if (seen.has(k)) return;
        seen.add(k);
        const st = hallStatusMap.get(k) || "Pending";
        if (counts[st] != null) counts[st] += 1;
      });
    }
    return counts;
  }, [currentViewFilter, statusByBooth, zoneStatusMap, hallStatusMap, venueZones, booths, zonesInScope, zoneHallOf, zoneMembers, halls, filterHallId, isPossessionMode, possessionByBooth]);

  // Stacked layouts: report this layout's counts up (null once it unmounts);
  // the legend shows the parent's sum when it is passed in.
  const statusCountsCbRef = useRef(onStatusCountsChange);
  statusCountsCbRef.current = onStatusCountsChange;
  useEffect(() => {
    if (statusCountsCbRef.current) statusCountsCbRef.current(dynamicStatusCounts);
  }, [dynamicStatusCounts]);
  useEffect(() => () => {
    if (statusCountsCbRef.current) statusCountsCbRef.current(null);
  }, []);
  const shownStatusCounts = legendCounts || dynamicStatusCounts;

  // Keep the plan covering the viewport: pin the offsets so an edge can never
  // be dragged inward to reveal background, and centre an axis that genuinely
  // is smaller than the box.
  const clampTransform = useCallback(() => {
    const container = containerRef.current;
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!container || !svgElement) return;

    const bounds = container.getBoundingClientRect();
    const rectW = bounds.width;
    const rectH = bounds.height;
    if (!rectW || !rectH) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    const pxPerUnit = Math.min(rectW / vbW, rectH / vbH);
    const padX = (rectW - vbW * pxPerUnit) / 2;
    const padY = (rectH - vbH * pxPerUnit) / 2;

    const { scale } = transform.current;
    const drawnW = vbW * pxPerUnit * scale;
    const drawnH = vbH * pxPerUnit * scale;

    const clampAxis = (offset, pad, drawn, viewport) => {
      const lead = pad * scale;
      if (drawn <= viewport) return (viewport - drawn) / 2 - lead;
      return Math.min(-lead, Math.max(viewport - drawn - lead, offset));
    };

    transform.current.offsetX = clampAxis(transform.current.offsetX, padX, drawnW, rectW);
    transform.current.offsetY = clampAxis(transform.current.offsetY, padY, drawnH, rectH);
  }, []);

  const updateTransformStyle = useCallback((animate = false) => {
    if (!svgRef.current) return;
    clampTransform();
    const { scale, offsetX, offsetY } = transform.current;

    svgRef.current.style.willChange = "transform";
    if (willChangeTimeoutRef.current) clearTimeout(willChangeTimeoutRef.current);
    willChangeTimeoutRef.current = setTimeout(() => {
      if (svgRef.current) svgRef.current.style.willChange = "auto";
    }, animate ? 400 : 200);

    if (animate) {
      svgRef.current.style.transition = "transform 0.35s ease-in-out";
      setTimeout(() => {
        if (svgRef.current) svgRef.current.style.transition = "none";
      }, 350);
    } else {
      svgRef.current.style.transition = "none";
    }

    svgRef.current.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;

    if (zoomLabelRef.current) {
      zoomLabelRef.current.innerText = `${Math.round(scale * 100)}%`;
    }
  }, [clampTransform]);

  // Focus box for a booth code, in SVG root user space. Multiple occurrences of
  // one code are unioned, unless that union is implausibly large (the same code
  // printed in two unrelated places), in which case the first wins.
  // Unpadded label extent. This is what routing treats as the booth footprint.
  const getBoothRawBox = useCallback((boothName) => {
    // The drawn border when we found it - it is the stall, not an estimate of one.
    const outline = boothOutlinesRef.current.get(boothName);
    if (outline) return outline;

    // The single occurrence the outline pass settled on, not a union of
    // repeated captions that can sit far apart.
    const chosenLabel = boothOutlinesRef.current.labels && boothOutlinesRef.current.labels.get(boothName);
    if (chosenLabel) return chosenLabel;

    const els = boothElementsRef.current.get(boothName) || [];
    if (els.length === 0) return null;
    const boxes = els.map(elementBoxInSvgSpace).filter(Boolean);
    if (boxes.length === 0) return null;

    const merged = unionBoxes(boxes);
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    const vw = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.width) || 792;
    const vh = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.height) || 612;
    if (merged && (merged.width > vw * 0.25 || merged.height > vh * 0.25)) {
      return boxes[0];
    }
    return merged;
  }, []);

  // Box for the focus ring and hit target. A real border is used as drawn; only
  // the label-derived fallback is padded out to stall size.
  // The traced outline of one or more booths as a single SVG path. Unioning is
  // just concatenating the cell sets before tracing - the shared edge between
  // two touching stalls appears twice and cancels, so a zone comes back as one
  // region with its own outer boundary rather than as a pile of rectangles.
  const getShapePath = useCallback((boothNames, club) => {
    const store = boothOutlinesRef.current;
    if (!store || !store.cells || !store.grid) return null;
    const grid = store.grid;

    // A booth without a traced border - the fallback padded label, or one
    // rebuilt from its printed size - has no entry in store.cells at all. Left
    // out here, it left a literal hole in the middle of the zone: the shape
    // only ever covered the booths that happened to trace cleanly. Rasterising
    // its own box onto the same grid gives it cells too, so every selected
    // booth is represented before clubbing runs.
    const boxToCells = (box) => {
      if (!box) return [];
      const c0 = Math.max(0, Math.floor((box.x - grid.ox) / grid.cell));
      const r0 = Math.max(0, Math.floor((box.y - grid.oy) / grid.cell));
      const c1 = Math.min(grid.cols - 1, Math.ceil((box.x + box.width - grid.ox) / grid.cell) - 1);
      const r1 = Math.min(grid.rows - 1, Math.ceil((box.y + box.height - grid.oy) / grid.cell) - 1);
      const cells = [];
      for (let r = r0; r <= r1; r += 1) {
        for (let c = c0; c <= c1; c += 1) cells.push(r * grid.cols + c);
      }
      return cells;
    };

    const all = [];
    // Same geometry the stall fills use: a rectangular stall is its box snapped
    // to the drawn line; any other shape is its traced pocket grown back out to
    // the line (the pocket itself stops OUTLINE_DILATE cells short of it).
    boothNames.forEach((name) => {
      const traced = store.get(name);
      const cells = store.cells.get(name);
      if (cells && cells.length > 0 && traced && traced.fill < OUTLINE_RECT_FILL) {
        all.push(...growCells(cells, grid, OUTLINE_DILATE));
        return;
      }
      all.push(...boxToCells(getBoothBox(name)));
    });
    if (all.length === 0) return null;
    let cells = Array.from(new Set(all));
    // A zone is one thing, so it is drawn as one thing: the stalls are closed
    // together across the aisles between them and traced as a single boundary,
    // instead of as a scatter of separate outlines.
    if (club && cells.length > 0) cells = clubCells(cells, grid, ZONE_CLUB_MAX_RADIUS);
    return cellsToPath(cells, grid);
    // getBoothBox is declared just below and is itself referentially stable
    // (empty dep array) - reordering the two would only churn this callback's
    // identity for no behavioural change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBoothBox = useCallback((boothName) => {
    if (boothOutlinesRef.current.has(boothName)) return getBoothRawBox(boothName);
    return padBox(getBoothRawBox(boothName));
  }, [getBoothRawBox]);

  // Projects a point from SVG user space into "world" space: container pixels
  // as they would be at scale 1 / zero offset. getScreenCTM() already folds in
  // the wrapper's CSS transform, so we divide it back out.
  const svgPointToWorld = useCallback((x, y, ctm, containerRect) => {
    const { scale, offsetX, offsetY } = transform.current;
    const sx = ctm ? ctm.a * x + ctm.c * y + ctm.e : x;
    const sy = ctm ? ctm.b * x + ctm.d * y + ctm.f : y;
    return {
      x: (sx - containerRect.left - offsetX) / scale,
      y: (sy - containerRect.top - offsetY) / scale,
    };
  }, []);

  // Frames an SVG-user-space box in the viewport.
  const zoomToSvgBox = useCallback((box) => {
    if (!box || !svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height) return false;

    const ctm = typeof svgElement.getScreenCTM === "function" ? svgElement.getScreenCTM() : null;
    const a = svgPointToWorld(box.x, box.y, ctm, containerRect);
    const b = svgPointToWorld(box.x + box.width, box.y + box.height, ctm, containerRect);

    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);

    const width = Math.max(maxX - minX, 1);
    const height = Math.max(maxY - minY, 1);

    const padding = 0.12;
    // No cover floor here: a hall is tall and narrow, and forcing it up to the
    // fit-width scale would frame only part of it. clampTransform still stops
    // the view drifting off the plan.
    const newScale = Math.max(0.4, Math.min(8, Math.min(
      (containerRect.width * (1 - padding * 2)) / width,
      (containerRect.height * (1 - padding * 2)) / height
    )));

    transform.current.scale = newScale;
    transform.current.offsetX = containerRect.width / 2 - ((minX + maxX) / 2) * newScale;
    transform.current.offsetY = containerRect.height / 2 - ((minY + maxY) / 2) * newScale;

    updateTransformStyle(true);
    return true;
  }, [svgPointToWorld, updateTransformStyle]);

  // Spotlight overlay. Takes a LIST of boxes and punches one hole per booth, so
  // a zone lights up only its own stalls - a single hole over the zone's bounding
  // box would leave every unrelated booth inside that rectangle lit too.
  // items: [{ box, path }] - one entry per selected stall, `path` being that
  // stall's own traced border (or null to fall back to its box). Each stall
  // gets its own mask hole and its own ring, so a multi-stall selection (a
  // hall, a zone) reads as that many individually bordered stalls rather than
  // one shape whose interior boundaries have cancelled out into a blob.
  const drawFocusOverlay = useCallback((items, label) => {
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!svgElement) return;

    let g = svgElement.querySelector("g#booth-focus");
    if (!g) {
      g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.setAttribute("id", "booth-focus");
      g.setAttribute("pointer-events", "none");
    }
    // Always re-append so the overlay stays on top of later-added groups.
    svgElement.appendChild(g);
    g.innerHTML = "";

    const list = (Array.isArray(items) ? items : [items]).filter((i) => i && i.box);
    if (list.length === 0) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vx = (vb && vb.x) || 0;
    const vy = (vb && vb.y) || 0;
    const vw = (vb && vb.width) || 792;
    const vh = (vb && vb.height) || 612;

    const svgNs = "http://www.w3.org/2000/svg";
    const maskId = "booth-focus-mask";

    const shapeEl = ({ box, path }) => {
      const el = document.createElementNS(svgNs, path ? "path" : "rect");
      if (path) {
        el.setAttribute("d", path);
        el.setAttribute("fill-rule", "evenodd");
      } else {
        el.setAttribute("x", box.x); el.setAttribute("y", box.y);
        el.setAttribute("width", box.width); el.setAttribute("height", box.height);
      }
      return el;
    };

    const mask = document.createElementNS(svgNs, "mask");
    mask.setAttribute("id", maskId);
    const maskShow = document.createElementNS(svgNs, "rect");
    maskShow.setAttribute("x", vx); maskShow.setAttribute("y", vy);
    maskShow.setAttribute("width", vw); maskShow.setAttribute("height", vh);
    maskShow.setAttribute("fill", "#fff");
    mask.appendChild(maskShow);
    // One hole PER STALL - not one hole covering their union - so a stall
    // sitting between two selected ones (a different zone, say) stays dimmed,
    // and two touching selected stalls each still show as their own cutout.
    list.forEach((item) => {
      const hole = shapeEl(item);
      hole.setAttribute("fill", "#000");
      mask.appendChild(hole);
    });
    g.appendChild(mask);

    const dim = document.createElementNS(svgNs, "rect");
    dim.setAttribute("x", vx); dim.setAttribute("y", vy);
    dim.setAttribute("width", vw); dim.setAttribute("height", vh);
    dim.setAttribute("fill", "#0f172a");
    dim.setAttribute("opacity", "0.55");
    dim.setAttribute("mask", `url(#${maskId})`);
    g.appendChild(dim);

    // No ring of our own: the spotlight (the dim with one hole per stall) is
    // the focus, and each stall keeps its own status colour and border - an
    // extra outline in another colour read as a status it does not have.

    if (label) {
      const union = unionBoxes(list.map((i) => i.box));
      const text = document.createElementNS(svgNs, "text");
      text.setAttribute("x", union.x + union.width / 2);
      text.setAttribute("y", union.y - 3);
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("font-size", "7");
      text.setAttribute("font-weight", "bold");
      text.setAttribute("fill", "#1f2937");
      text.setAttribute("data-overlay", "1");
      text.textContent = label;
      g.appendChild(text);
    }
  }, []);

  // Opening view: the plan at DEFAULT_ZOOM (the toolbar's percentage / 100),
  // centred. Whatever overflows the box is panned through.
  const applyDefaultView = useCallback((animate = false) => {
    if (!svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbX = (vb && vb.x) || 0;
    const vbY = (vb && vb.y) || 0;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    // preserveAspectRatio="xMidYMid meet": uniform scale, centred, so this is
    // the px-per-unit and padding the SVG viewport itself applies at scale 1.
    const pxPerUnit = Math.min(rect.width / vbW, rect.height / vbH);
    const padX = (rect.width - vbW * pxPerUnit) / 2;
    const padY = (rect.height - vbH * pxPerUnit) / 2;

    // Opening zoom per layout: DEFAULT_ZOOM at most, but never more than what
    // fits all of this layout's booths in the box - a layout with more or
    // bigger halls would otherwise open zoomed into its middle. Centred on
    // the booths, not on the sheet (a drawing's margins are not the plan).
    const content = unionBoxes(booths.map((b) => getBoothBox(b.BoothName)));
    let scale = DEFAULT_ZOOM;
    let centreX = vbX + vbW / 2;
    let centreY = vbY + vbH / 2;
    if (content && content.width > 0 && content.height > 0) {
      const fit = Math.min(rect.width / (content.width * pxPerUnit), rect.height / (content.height * pxPerUnit));
      scale = Math.max(1, Math.min(DEFAULT_ZOOM, fit * DEFAULT_FIT_MARGIN));
      centreX = content.x + content.width / 2;
      centreY = content.y + content.height / 2;
    }

    const centreBaseX = (centreX - vbX) * pxPerUnit + padX;
    const centreBaseY = (centreY - vbY) * pxPerUnit + padY;

    transform.current.scale = scale;
    transform.current.offsetX = rect.width / 2 - centreBaseX * scale;
    transform.current.offsetY = rect.height / 2 - centreBaseY * scale;
    updateTransformStyle(animate);
    return true;
  }, [updateTransformStyle, booths, getBoothBox]);

  // Keep the canvas box locked to the drawing's aspect ratio, capped so a tall
  // plan can't push the rest of the page off screen.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    const stage = stageRef.current;
    if (!wrap) return;

    const measure = () => {
      // The wrap is display:none in Venue View; the stage around it is not.
      const available = wrap.clientWidth || (stage && stage.clientWidth);
      if (!available) return;
      // Full width always. Height is capped at the plan's own height once its
      // width fits, so a narrow screen can never letterbox top and bottom.
      // In full screen the map takes all the height below the toolbar.
      const aspect = svgAspect || 792 / 612;
      const height = isFullscreen && stage
        ? Math.max(300, Math.floor(window.innerHeight - stage.getBoundingClientRect().top - 14))
        : Math.max(
          300,
          Math.min(Math.round(window.innerHeight * 0.72), Math.round(available / aspect))
        );
      setCanvasBox((prev) => (prev.width === available && prev.height === height ? prev : { width: available, height }));
    };

    measure();

    if (typeof window.ResizeObserver !== "function") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new window.ResizeObserver(measure);
    ro.observe(wrap);
    if (stage) ro.observe(stage);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [svgAspect, isFullscreen]);

  // Apply the opening view once the drawing has been measured, and again if the
  // box is resized - but never after the user has panned or zoomed themselves.
  useEffect(() => {
    if (hasUserAdjustedRef.current) return;
    if (filterHallId || filterBoothId) return;
    if (booths.length === 0 && halls.length === 0) return;

    const timer = setTimeout(() => {
      if (hasUserAdjustedRef.current) return;
      if (applyDefaultView(defaultViewAppliedRef.current)) {
        defaultViewAppliedRef.current = true;
      }
    }, 40);
    return () => clearTimeout(timer);
  }, [booths, halls, canvasBox, filterHallId, filterBoothId, applyDefaultView]);

  // A new drawing gets a fresh opening view.
  useEffect(() => {
    hasUserAdjustedRef.current = false;
    defaultViewAppliedRef.current = false;
  }, [layout.svgContent]);

  useEffect(() => {
    updateTransformStyle();
  }, [updateTransformStyle]);

  const zoomAtPoint = useCallback((newScale, pointX, pointY) => {
    const clampedScale = Math.max(0.4, Math.min(8, newScale));
    const { scale, offsetX, offsetY } = transform.current;
    if (clampedScale === scale) return;

    const worldX = (pointX - offsetX) / scale;
    const worldY = (pointY - offsetY) / scale;

    transform.current.scale = clampedScale;
    transform.current.offsetX = pointX - worldX * clampedScale;
    transform.current.offsetY = pointY - worldY * clampedScale;
  }, []);


  // ─── Build index, paint status colours, wire interaction ──────────────
  // Keyed to the SVG itself: a filter change must never rebuild the graph.
  // ─── Read halls & booths out of the drawing ───────────────────────────
  // Runs once per SVG. Everything downstream keys off what this finds.
  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      clipIndexRef.current = buildSvgIndex(svgElement);

      // The exported SVG carries width="792" height="612", so it renders at its
      // intrinsic size and sits marooned in a wider container. Let it scale to
      // whatever box we give it instead. All our geometry goes through
      // getScreenCTM / getCTM, so this does not disturb focus or routing.
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      const vbox = svgElement.viewBox && svgElement.viewBox.baseVal;
      if (vbox && vbox.width > 0 && vbox.height > 0) {
        setSvgAspect(vbox.width / vbox.height);
      }
      if (!svgElement.getAttribute("preserveAspectRatio")) {
        svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
      }

      const { halls: drawnHalls, booths: svgBooths, elementsByBooth } = extractSvgLayoutData(svgElement);
      // Other-area halls have no booth labels to be found by: add their outlines.
      const areaHalls = areaHallsForLayout(layout).filter((a) => !drawnHalls.some((h) => h.HallID === a.HallID));
      const svgHalls = [...drawnHalls, ...areaHalls].sort((a, b) => naturalCompare(a.HallID, b.HallID));
      boothElementsRef.current = elementsByBooth;
      boothOutlinesRef.current = findBoothOutlines(svgElement, elementsByBooth);
      setSvgData({ halls: svgHalls, booths: svgBooths });
      if (onSvgDataExtracted) onSvgDataExtracted({ halls: svgHalls, booths: svgBooths });
    }, 60);

    return () => clearTimeout(timer);
    // onSvgDataExtracted intentionally omitted: parents commonly pass an inline
    // callback, which would re-run the parse on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.svgContent]);

  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    let cleanupListeners = null;

    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      // Color injection (fast)
      let styleTag = svgElement.querySelector('style[data-booth-colors]');
      if (!styleTag) {
        styleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleTag.setAttribute('data-booth-colors', 'true');
        styleTag.setAttribute('type', 'text/css');
        svgElement.insertBefore(styleTag, svgElement.firstChild);
      }

      let cssRules = '';
      // Booth colours are no longer painted from getHallLayout's StatusColor.
      // Status comes from the tracker now, and it is carried by the node dots -
      // painting a second, differently-sourced colour underneath them meant the
      // drawing could contradict the popup.

      // Booth label nodes are fill-opacity:0, so they are never "painted" and
      // the default visiblePainted hit-testing ignores them entirely.
      cssRules += `
        [data-booth-name] { pointer-events: all; cursor: pointer; }
        #booth-hits [data-booth-name] { fill: transparent; pointer-events: all; cursor: pointer; }
        #booth-nodes circle.booth-node-dot { cursor: pointer; transition: r 0.15s ease, filter 0.15s ease; }
        #booth-nodes circle.booth-node-dot:hover { r: 4.5; filter: drop-shadow(0 0 3px rgba(79,70,229,0.9)); }
        #booth-areas .booth-area { transition: fill-opacity 0.15s ease, stroke-width 0.15s ease; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
        #booth-areas .booth-area:hover { fill-opacity: 0.4 !important; stroke-width: 2.2 !important; }
        #booth-focus .booth-focus-ring { animation: boothFocusPulse 1.6s ease-in-out infinite; }
        @keyframes boothFocusPulse {
          0%, 100% { stroke-opacity: 1; stroke-width: 1.8; }
          50%      { stroke-opacity: .45; stroke-width: 2.8; }
        }
      `;
      styleTag.textContent = cssRules;

      // ─── Booth nodes from the label geometry ───
      const nodes = {};
      const nodeBoothMap = {};
      const boothBoxes = {};
      booths.forEach(booth => {
        const raw = getBoothRawBox(booth.BoothName);
        if (!raw) return;
        const box = boothOutlinesRef.current.has(booth.BoothName) ? raw : padBox(raw);
        const id = booth.BoothName;
        nodes[id] = { id, x: box.x + box.width / 2, y: box.y + box.height / 2 };
        nodeBoothMap[id] = booth;
        boothBoxes[id] = box;
      });

      boothNodesRef.current = nodes;
      nodeBoothMapRef.current = nodeBoothMap;

      /* The stall itself carries the status. Appended before the hit targets and
         the dots so it sits underneath them, and underneath the drawing's own
         booth number and dimension text */
      let areasGroup = svgElement.querySelector('g#booth-areas');
      if (!areasGroup) {
        areasGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        areasGroup.setAttribute('id', 'booth-areas');
        svgElement.appendChild(areasGroup);
      }
      areasGroup.innerHTML = '';
      // A combined stand carries several booth codes inside one border, so the
      // same box/path comes back for each of them. The fill is semi-transparent, and
      // painting it once per code stacks the opacity until the stand reads far
      // darker than a single stall - so each distinct area is painted once.
      const paintedAreas = new Set();
      Object.entries(boothBoxes).forEach(([id, box]) => {
        const areaKey = `${Math.round(box.x * 2) / 2}:${Math.round(box.y * 2) / 2}:${Math.round(box.width * 2) / 2}:${Math.round(box.height * 2) / 2}`;
        if (paintedAreas.has(areaKey)) return;
        paintedAreas.add(areaKey);

        // A rectangular stall is its snapped box. An L, a pentagon or a stall
        // around a pillar is drawn as the shape it traced to, so the highlight
        // follows its border instead of spilling over the bounding box.
        const store = boothOutlinesRef.current;
        const traced = store.get(id);
        const d = traced && traced.cells && traced.fill < OUTLINE_RECT_FILL && store.grid
          ? cellsToPath(growCells(traced.cells, store.grid, OUTLINE_DILATE), store.grid)
          : null;
        let area;
        if (d) {
          area = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          area.setAttribute('d', d);
          area.setAttribute('fill-rule', 'evenodd');
        } else {
          area = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          area.setAttribute('x', box.x);
          area.setAttribute('y', box.y);
          area.setAttribute('width', box.width);
          area.setAttribute('height', box.height);
        }
        area.setAttribute('class', 'booth-area');
        area.setAttribute('data-node-id', id);
        const st = boothDisplayStatusRef.current(nodeBoothMap[id]);
        paintBoothArea(area, st.status, st.dimmed);
        areasGroup.appendChild(area);
      });

      // Generous transparent hit targets - the label glyphs alone are tiny.
      let hitsGroup = svgElement.querySelector('g#booth-hits');
      if (!hitsGroup) {
        hitsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        hitsGroup.setAttribute('id', 'booth-hits');
        svgElement.appendChild(hitsGroup);
      }
      hitsGroup.innerHTML = '';
      Object.entries(boothBoxes).forEach(([id, box]) => {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', box.x);
        rect.setAttribute('y', box.y);
        rect.setAttribute('width', box.width);
        rect.setAttribute('height', box.height);
        rect.setAttribute('fill', 'transparent');
        rect.setAttribute('data-booth-name', id);
        hitsGroup.appendChild(rect);
      });

      // Draw dots
      let dotsGroup = svgElement.querySelector('g#booth-nodes');
      if (!dotsGroup) {
        dotsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        dotsGroup.setAttribute('id', 'booth-nodes');
        svgElement.appendChild(dotsGroup);
      }
      dotsGroup.innerHTML = '';

      Object.values(nodes).forEach(node => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', node.x);
        circle.setAttribute('cy', node.y);
        circle.setAttribute('r', '2.5');
        // The drawing has no fillable booth shape, so the dot carries status.
        // Painted with the booth's own status here; the service-filter effect
        // below repaints once the dots exist, and owns it from then on.
        {
          // Same source as the popup, so a dot can never disagree with the
          // status shown when the booth is opened.
          const st = boothDisplayStatusRef.current(nodeBoothMap[node.id]);
          paintBoothDot(circle, st.status, st.dimmed);
        }
        circle.setAttribute('stroke', '#fff');
        circle.setAttribute('stroke-width', '1');
        circle.setAttribute('class', 'booth-node-dot');
        /* The stall carries the colour now, so the dot is no longer drawn. It
           stays in the DOM because the route finder and the service filter both
           address it by node id */
        circle.style.display = 'none';
        circle.setAttribute('data-node-id', node.id);
        circle.style.pointerEvents = 'all';

        // Node click -> open popup with that node's booth data
        circle.addEventListener('click', (e) => {
          e.stopPropagation();
          if (transform.current.hasDragged) return;
          const booth = nodeBoothMap[node.id];
          if (booth) setSelectedBooth(booth);
        });

        // Node hover -> reuse existing tooltip
        circle.addEventListener('mouseover', () => {
          const booth = nodeBoothMap[node.id];
          if (booth && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current && svgContainer) {
            const rect = circle.getBoundingClientRect();
            const { status, label } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText = `${booth.BoothName} - ${label}`;
            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }
            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        });
        circle.addEventListener('mouseout', () => {
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
        });

        dotsGroup.appendChild(circle);

        /* With the dot hidden the stall is what the pointer meets, so it takes
           the click and hover the dot used to carry */
        const area = areasGroup.querySelector('[data-node-id="' + node.id.replace(/"/g, '\\"') + '"]');
        if (area) {
          area.style.cursor = 'pointer';
          area.style.pointerEvents = 'all';
          area.addEventListener('click', (e) => {
            e.stopPropagation();
            if (transform.current.hasDragged) return;
            const booth = nodeBoothMap[node.id];
            if (booth) setSelectedBooth(booth);
          });
          area.addEventListener('mouseover', () => {
            const booth = nodeBoothMap[node.id];
            if (booth && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current && svgContainer) {
              const rect = area.getBoundingClientRect();
              const { status, label } = boothDisplayStatusRef.current(booth);
              tooltipContentRef.current.innerText = `${booth.BoothName} - ${label}`;
              if (status) {
                tooltipDotRef.current.style.display = 'inline-block';
                tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
              } else {
                tooltipDotRef.current.style.display = 'none';
              }
              tooltipRef.current.style.left = rect.left + 'px';
              tooltipRef.current.style.top = (rect.top - 10) + 'px';
              tooltipRef.current.style.display = 'block';
            }
          });
          area.addEventListener('mouseout', () => {
            if (tooltipRef.current) tooltipRef.current.style.display = 'none';
          });
        }
      });
      setDotsVersion(v => v + 1);

      // Rasterise the hall once per layout. Booth footprints become walls; what
      // survives is the aisle network every route is confined to.
      const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
      const viewBox = {
        x: (vb && vb.x) || 0,
        y: (vb && vb.y) || 0,
        width: (vb && vb.width) || 792,
        height: (vb && vb.height) || 612,
      };
      navGridRef.current = buildNavGrid(svgElement, viewBox);

      let routeGroup = svgElement.querySelector('g#route-path');
      if (!routeGroup) {
        routeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        routeGroup.setAttribute('id', 'route-path');
        svgElement.appendChild(routeGroup);
      }

      // Event Delegation for hover
      const handleMouseOver = (e) => {
        const areaTarget = e.target.closest('[data-area-kind]');
        if (areaTarget && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
          const rect = areaTarget.getBoundingClientRect();
          tooltipContentRef.current.innerText = `${areaTarget.getAttribute('data-area-label')} - click for details`;
          tooltipDotRef.current.style.display = 'inline-block';
          tooltipDotRef.current.style.backgroundColor = areaTarget.getAttribute('stroke') || '#9CA3AF';
          tooltipRef.current.style.left = `${rect.left + rect.width / 2}px`;
          tooltipRef.current.style.top = `${rect.top + 20}px`;
          tooltipRef.current.style.display = 'block';
          return;
        }
        const target = e.target.closest('[data-booth-name]');
        if (target && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            const booth = boothMap[boothName];
            const rect = target.getBoundingClientRect();

            const { status, label } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText = `${booth.BoothName} - ${label}`;

            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }

            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        }
      };

      const handleMouseOut = (e) => {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      };

      const handleClick = (e) => {
        if (transform.current.hasDragged) {
          console.log("Click ignored due to dragging");
          return;
        }
        const areaTarget = e.target.closest('[data-area-kind]');
        if (areaTarget) {
          setSelectedBooth(null);
          setSelectedArea({
            kind: areaTarget.getAttribute('data-area-kind'),
            hallName: areaTarget.getAttribute('data-hall-name') || "",
            zoneKey: areaTarget.getAttribute('data-zone-key') || "",
            zoneName: areaTarget.getAttribute('data-zone-name') || "",
          });
          return;
        }
        const target = e.target.closest('[data-booth-name]');
        console.log("SVG Clicked! Target found:", target ? target.getAttribute('data-booth-name') : "None");
        if (target) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            console.log("Setting selected booth:", boothMap[boothName]);
            setSelectedArea(null);
            setSelectedBooth(boothMap[boothName]);
          }
        } else {
          setSelectedBooth(null);
          setSelectedArea(null);
        }
      };

      let cursorStyleTag = svgElement.querySelector('style[data-booth-cursor]');
      if (!cursorStyleTag) {
        cursorStyleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        cursorStyleTag.setAttribute('data-booth-cursor', 'true');
        cursorStyleTag.setAttribute('type', 'text/css');
        cursorStyleTag.textContent = `[data-booth-name] { cursor: pointer; }`;
        svgElement.insertBefore(cursorStyleTag, svgElement.firstChild);
      }

      svgContainer.addEventListener('mouseover', handleMouseOver);
      svgContainer.addEventListener('mouseout', handleMouseOut);
      svgContainer.addEventListener('click', handleClick);

      cleanupListeners = () => {
        svgContainer.removeEventListener('mouseover', handleMouseOver);
        svgContainer.removeEventListener('mouseout', handleMouseOut);
        svgContainer.removeEventListener('click', handleClick);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      // Returning this from inside the setTimeout would discard it and leak a
      // fresh set of listeners on every re-run.
      if (cleanupListeners) cleanupListeners();
    };
  }, [layout.svgContent, layout.booths, booths, boothMap, getBoothRawBox]);

  // ─── Multi-Mode Layer Rendering (Stall View / Zone Status / Hall Status) ──
  useEffect(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;

    const nodeBoothMap = nodeBoothMapRef.current || {};

    // 1. Group containers for zone and hall status overlays
    let zoneLayer = svgElement.querySelector('g#zone-status-layer');
    if (!zoneLayer) {
      zoneLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      zoneLayer.setAttribute('id', 'zone-status-layer');
      zoneLayer.setAttribute('pointer-events', 'all');
      svgElement.appendChild(zoneLayer);
    }

    let hallLayer = svgElement.querySelector('g#hall-status-layer');
    if (!hallLayer) {
      hallLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      hallLayer.setAttribute('id', 'hall-status-layer');
      hallLayer.setAttribute('pointer-events', 'all');
      svgElement.appendChild(hallLayer);
    }

    const areas = svgElement.querySelectorAll('#booth-areas .booth-area');
    const hits = svgElement.querySelectorAll('#booth-hits [data-booth-name]');
    const dots = svgElement.querySelectorAll('#booth-nodes circle.booth-node-dot');

    if (currentViewFilter === "stall") {
      // With a zone picked, only its stalls keep their status colour; every
      // other stall is dimmed, so the zone reads as exactly its own stalls.
      const outsideZone = (booth) =>
        Boolean(zoneBoothKeys) && !!booth &&
        !(booth.HallID === filterHallId && zoneBoothKeys.has(normalizeBoothKey(booth.BoothName)));
      // Show stall status fills & hit targets
      Array.from(areas).forEach((area) => {
        area.style.display = "";
        const booth = nodeBoothMap[area.getAttribute('data-node-id')];
        const { status, dimmed } = boothDisplayStatus(booth);
        paintBoothArea(area, status, dimmed || outsideZone(booth));
      });
      Array.from(hits).forEach((hit) => {
        hit.style.display = "";
      });
      Array.from(dots).forEach((circle) => {
        circle.style.display = "none";
        const booth = nodeBoothMap[circle.getAttribute('data-node-id')];
        const { status, dimmed } = boothDisplayStatus(booth);
        paintBoothDot(circle, status, dimmed);
      });

      zoneLayer.style.display = "none";
      zoneLayer.innerHTML = "";
      hallLayer.style.display = "none";
      hallLayer.innerHTML = "";
    } else if (currentViewFilter === "zone") {
      // Zone Status shows the selected hall's zones - the sections a hall is
      // divided into - as one rectangle each in the zone's status colour.
      // Stall fills, dots and hit targets stay hidden: a click lands on the
      // zone cell and opens the zone's own details (AreaDetailPanel), whose
      // stall list still leads to any one stall.
      Array.from(areas).forEach((area) => { area.style.display = "none"; });
      Array.from(dots).forEach((circle) => { circle.style.display = "none"; });
      Array.from(hits).forEach((hit) => { hit.style.display = "none"; });

      hallLayer.style.display = "none";
      hallLayer.innerHTML = "";
      zoneLayer.style.display = "";
      zoneLayer.innerHTML = "";

      // A zone belongs to one hall - "Zone 1" in Hall 3 and "Zone 1" in Hall 1
      // are different zones that share a name - so zones are always laid out
      // INSIDE their own hall: the selected hall, or with All Halls every hall
      // on the drawing, each divided into its own zone cells.
      const pickedZone = selectedZoneId
        ? zonesInScope.find((z) => String(z.HallZoneMappingID) === String(selectedZoneId))
        : null;
      const pickedZoneKey = pickedZone ? zoneKeyOf(zoneHallOf(pickedZone), pickedZone.Zone || pickedZone.ZoneName) : "";
      const hallsShown = filterHallId ? (halls || []).filter((h) => h.HallID === filterHallId) : (halls || []);
      // Same aligned hall boxes as Hall View - including its 3-unit margin, so
      // the outer stalls' borders sit inside the zone cells (without it, the
      // cells' 1.5-unit inset left every edge stall poking out of its zone).
      const zoneHallBoxes = hallBoxesFor(halls, booths, getBoothBox, 3);

      hallsShown.forEach((hall) => {
        const hallKey = hallNameKey(hall.HallName);
        // One overlay per hall+zone (a hall can list the same zone name under
        // two mapping ids).
        const zonesToRender = new Map();
        zonesInScope
          .filter((z) => hallNameKey(zoneHallOf(z)) === hallKey)
          .forEach((z) => {
            const key = zoneKeyOf(zoneHallOf(z), z.Zone || z.ZoneName);
            if (key && !zonesToRender.has(key)) zonesToRender.set(key, z);
          });

        // The hall is cut into one equal cell per zone that has stalls on the
        // drawing (layoutZoneCells), and each zone takes the cell holding most of its
        // stalls - a clean, non-overlapping division instead of boxes around
        // each zone's stalls, which overlap wherever a big stand straddles the
        // hall's midline. The cells are laid out over ALL of the hall's zones,
        // so a picked zone keeps its place when the others are hidden.
        const hallBox = zoneHallBoxes.get(hall.HallID);
        const placedZones = [];
        zonesToRender.forEach((zone, zoneKey) => {
          const zoneHall = hallNameKey(zoneHallOf(zone));
          // The booths belonging to this zone (one zone per stall - zoneMembers).
          const stallKeys = zoneMembers.get(zoneKey) || new Set();
          // Only this hall's booths: the loose key would otherwise pull in a
          // same-coded stall drawn in another hall.
          const boxes = booths
            .filter(
              (b) =>
                b.HallID === hall.HallID &&
                hallNameKey(b.HallName) === zoneHall &&
                stallKeys.has(normalizeBoothKey(b.BoothName))
            )
            .map((b) => getBoothBox(b.BoothName))
            .filter(Boolean);
          if (boxes.length === 0) return;
          // Area-weighted centre: where most of the zone's floor space is.
          let sx = 0, sy = 0, sa = 0;
          boxes.forEach((bx) => {
            const a = Math.max(bx.width * bx.height, 1);
            sx += (bx.x + bx.width / 2) * a;
            sy += (bx.y + bx.height / 2) * a;
            sa += a;
          });
          placedZones.push({ zone, zoneKey, boxes, centre: { x: sx / sa, y: sy / sa } });
        });
        const zoneCells = hallBox && placedZones.length > 0
          ? layoutZoneCells(hallBox, placedZones)
          : [];

        placedZones.forEach(({ zone, zoneKey }, zi) => {
          const zoneNameStr = zone.Zone || zone.ZoneName || "";
          const zStat = zoneStatusMap.get(zoneKey) || "Pending";
          const color = getStatusColor(zStat);
          const cell = zoneCells[zi];

          // With a zone picked, only that zone is drawn.
          if (pickedZoneKey && zoneKey !== pickedZoneKey) return;

          if (!cell) return;

          // A small inset leaves a visible gutter between neighbouring cells.
          const inset = 1.5;
          const inner = {
            x: cell.x + inset,
            y: cell.y + inset,
            width: Math.max(cell.width - inset * 2, 1),
            height: Math.max(cell.height - inset * 2, 1),
          };
          // A cell reaching into a corner cut out of the hall (an L-shaped
          // hall) is drawn without that corner, keeping the same gutter.
          const cellPath = rectMinusNotchesPath(inner, ((hallBox && hallBox.notches) || []).map((n) => ({
            x: n.x - inset, y: n.y - inset, width: n.width + inset * 2, height: n.height + inset * 2,
          })));
          const rectEl = document.createElementNS('http://www.w3.org/2000/svg', cellPath ? 'path' : 'rect');
          if (cellPath) {
            rectEl.setAttribute('d', cellPath);
          } else {
            rectEl.setAttribute('x', inner.x);
            rectEl.setAttribute('y', inner.y);
            rectEl.setAttribute('width', inner.width);
            rectEl.setAttribute('height', inner.height);
            rectEl.setAttribute('rx', '3');
          }
          rectEl.setAttribute('fill', color);
          rectEl.setAttribute('fill-opacity', '0.25');
          rectEl.setAttribute('stroke', color);
          rectEl.setAttribute('stroke-width', '2');
          rectEl.setAttribute('class', 'zone-status-overlay');
          rectEl.setAttribute('pointer-events', 'all');
          rectEl.setAttribute('data-area-kind', 'zone');
          rectEl.setAttribute('data-hall-name', zoneHallOf(zone) || hall.HallName || '');
          rectEl.setAttribute('data-zone-key', zoneKey);
          rectEl.setAttribute('data-zone-name', zoneNameStr);
          rectEl.setAttribute('data-area-label', `${zoneNameStr} · ${hall.HallName} - ${zStat}`);
          rectEl.style.cursor = 'pointer';
          zoneLayer.appendChild(rectEl);

          // Badge at the cell's centre - zone name over status, sized to fit
          // inside the cell so it never spills into the next zone.
          const cx = cell.x + cell.width / 2;
          const cy = cell.y + cell.height / 2;
          const longest = Math.max(zoneNameStr.length, zStat.length, 4);
          const fs = Math.max(4, Math.min(9.5, (cell.width * 0.85 - 8) / (longest * 0.62)));
          const badgeW = longest * fs * 0.62 + 8;
          const badgeH = fs * 2.9;

          const badgeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
          badgeGroup.setAttribute('class', 'zone-badge');
          badgeGroup.setAttribute('pointer-events', 'none');

          const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          bg.setAttribute('x', cx - badgeW / 2);
          bg.setAttribute('y', cy - badgeH / 2);
          bg.setAttribute('width', badgeW);
          bg.setAttribute('height', badgeH);
          bg.setAttribute('rx', '4');
          bg.setAttribute('fill', '#ffffff');
          bg.setAttribute('fill-opacity', '0.94');
          bg.setAttribute('stroke', color);
          bg.setAttribute('stroke-width', '1.5');
          badgeGroup.appendChild(bg);

          [[zoneNameStr, cy - fs * 0.25, 'bold', '#1f2937'], [zStat, cy + fs * 1.05, 'normal', '#374151']].forEach(
            ([text, y, weight, fill]) => {
              const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
              txt.setAttribute('x', cx);
              txt.setAttribute('y', y);
              txt.setAttribute('text-anchor', 'middle');
              txt.setAttribute('font-size', fs);
              txt.setAttribute('font-weight', weight);
              txt.setAttribute('fill', fill);
              txt.textContent = text;
              badgeGroup.appendChild(txt);
            }
          );

          zoneLayer.appendChild(badgeGroup);
        });
      });
    } else if (currentViewFilter === "hall") {
      // Hall Status shows each hall in scope (the selected one, or all of them)
      // as a single rectangle in its status colour. As in Zone View the stall
      // hit targets stay hidden, so a click opens the hall's own details.
      Array.from(areas).forEach((area) => { area.style.display = "none"; });
      Array.from(dots).forEach((circle) => { circle.style.display = "none"; });
      Array.from(hits).forEach((hit) => { hit.style.display = "none"; });

      zoneLayer.style.display = "none";
      zoneLayer.innerHTML = "";
      hallLayer.style.display = "";
      hallLayer.innerHTML = "";

      // The selected hall, or with All Halls every hall on the drawing. Boxes
      // are aligned over ALL halls, so a hall looks the same picked or not.
      const hallsToRender = filterHallId ? (halls || []).filter((h) => h.HallID === filterHallId) : (halls || []);
      // A little margin (3 units) so a rectangle's edge does not sit on the
      // outer stalls' own borders - applied inside hallBoxesFor, so halls that
      // meet still meet edge to edge instead of overlapping by the margin.
      const hallBoxes = hallBoxesFor(halls, booths, getBoothBox, 3);

      hallsToRender.forEach((hall) => {
        const hKey = normalizeKey(hall.HallName);
        const hStat = hallStatusMap.get(hKey) || "Pending";
        const color = getStatusColor(hStat);
        const hallBooths = booths.filter((b) => b.HallID === hall.HallID);

        // An other area has no stalls: it is drawn from its own outline.
        const isArea = isOtherAreaHall(hall);
        const hallBox = hall.areaBox || hallBoxes.get(hall.HallID);
        if ((hallBooths.length > 0 || hall.areaBox) && hallBox) {
          {
            // An L-shaped hall (empty corner cut out) is drawn as its outline.
            const rectEl = document.createElementNS('http://www.w3.org/2000/svg', hallBox.path ? 'path' : 'rect');
            if (hallBox.path) {
              rectEl.setAttribute('d', hallBox.path);
              rectEl.setAttribute('stroke-linejoin', 'round');
            } else {
              rectEl.setAttribute('x', hallBox.x);
              rectEl.setAttribute('y', hallBox.y);
              rectEl.setAttribute('width', hallBox.width);
              rectEl.setAttribute('height', hallBox.height);
              rectEl.setAttribute('rx', '3');
            }
            rectEl.setAttribute('fill', color);
            rectEl.setAttribute('fill-opacity', '0.25');
            rectEl.setAttribute('stroke', color);
            rectEl.setAttribute('stroke-width', '2.5');
            rectEl.setAttribute('class', 'hall-status-overlay');
            rectEl.setAttribute('pointer-events', 'all');
            rectEl.setAttribute('data-area-kind', 'hall');
            rectEl.setAttribute('data-hall-name', hall.HallName || '');
            rectEl.setAttribute('data-area-label', `${hall.HallName} - ${hStat}`);
            rectEl.style.cursor = 'pointer';
            hallLayer.appendChild(rectEl);
          }

          // Floating card at the hall's centre.
          {
            const cx = hallBox.x + hallBox.width / 2;
            const cy = hallBox.y + hallBox.height / 2;
            const badgeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            badgeGroup.setAttribute('class', 'hall-badge');
            badgeGroup.setAttribute('pointer-events', 'none');

            const daily = hallDailyRoutineSummary.get(hKey);
            const dailyText = (isDailyRoutineActive && daily && daily.total > 0)
              ? ` · ${daily.completed}/${daily.total} Routines`
              : "";
            const areaTasks = isArea && isServicePartnerActive ? (areaTaskCountByHall.get(hKey) || 0) : 0;
            const subtext = isArea
              ? `${otherAreaNameOf(hall.HallName)}${isServicePartnerActive ? ` · ${areaTasks} Tasks` : ""}${dailyText}`
              : `${hallBooths.length} Stalls${dailyText}`;

            // Wide enough for the longer line (an other area's names run long).
            const badgeW = Math.max(120, Math.max(`${hall.HallName} (${hStat})`.length * 6.2, subtext.length * 4.6) + 16);
            const badgeH = 34;

            const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            bg.setAttribute('x', cx - badgeW / 2);
            bg.setAttribute('y', cy - badgeH / 2);
            bg.setAttribute('width', badgeW);
            bg.setAttribute('height', badgeH);
            bg.setAttribute('rx', '5');
            bg.setAttribute('fill', '#ffffff');
            bg.setAttribute('fill-opacity', '0.95');
            bg.setAttribute('stroke', color);
            bg.setAttribute('stroke-width', '2');
            badgeGroup.appendChild(bg);

            const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            title.setAttribute('x', cx);
            title.setAttribute('y', cy - 2);
            title.setAttribute('text-anchor', 'middle');
            title.setAttribute('font-size', '11');
            title.setAttribute('font-weight', 'bold');
            title.setAttribute('fill', '#111827');
            title.textContent = `${hall.HallName} (${hStat})`;
            badgeGroup.appendChild(title);

            const subtitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            subtitle.setAttribute('x', cx);
            subtitle.setAttribute('y', cy + 11);
            subtitle.setAttribute('text-anchor', 'middle');
            subtitle.setAttribute('font-size', '8.5');
            subtitle.setAttribute('font-weight', '500');
            subtitle.setAttribute('fill', '#6b7280');
            subtitle.textContent = subtext;
            badgeGroup.appendChild(subtitle);

            hallLayer.appendChild(badgeGroup);
          }
        }
      });
    }
  }, [
    currentViewFilter,
    currentDataFilters,
    boothDisplayStatus,
    dotsVersion,
    zoneStatusMap,
    hallStatusMap,
    hallDailyRoutineSummary,
    isDailyRoutineActive,
    filterHallId,
    selectedHallName,
    selectedZoneId,
    zoneBoothKeys,
    booths,
    boothMap,
    halls,
    zonesInScope,
    zoneHallOf,
    zoneMembers,
    zoneStalls,
    getShapePath,
    getBoothBox,
    onZoneChange,
    onZoneStallFocus,
    onFilterHallChange,
    isOtherAreaHall,
    otherAreaNameOf,
    areaTaskCountByHall,
    isServicePartnerActive
  ]);

  // ─── Focus + spotlight for the active Hall / Booth selection ──────────
  // One effect owns both the framing and the highlight so they can never
  // disagree about what is selected.
  useEffect(() => {
    if (!svgRef.current) return;
    if (booths.length === 0 && !halls.some((h) => h.areaBox)) return;

    // Venue View hides the hall-layout canvas; framing it would only measure a
    // zero-sized box and report the hall as "not located".
    if (isVenueLike(currentViewFilter) || (!filterHallId && !filterBoothId)) {
      drawFocusOverlay(null);
      setFocusError("");
      setIsProcessingFilter(false);
      return;
    }

    let finishTimer = null;
    const timer = setTimeout(() => {
      // One item per booth - its own box AND its own traced border - so the
      // spotlight lights exactly the selected stalls, each on its own drawn
      // line, and never as one shape merged across several of them (that
      // would show only the group's outer edge, hiding every border between
      // its own stalls - see the Zone/Hall status views below for the same
      // fix). A name with no box (nothing in the drawing) is dropped.
      const itemsFor = (names) => names
        .map((name) => {
          const box = getBoothBox(name);
          return box ? { box, path: getShapePath([name], false) } : null;
        })
        .filter(Boolean);

      let items = [];
      let label = "";
      let missing = "";

      const hallBooths = booths.filter(b => b.HallID === filterHallId);

      if (filterBoothId) {
        const booth = boothMap[filterBoothId];
        label = booth ? booth.BoothName : String(filterBoothId);
        items = itemsFor([label]);
        if (items.length === 0) missing = `Booth "${label}"`;
      } else if (zoneBoothKeys && zoneBoothKeys.size > 0) {
        // A zone has no outline of its own - it groups stalls, it is not a
        // region on the plan. So it is framed and spotlit exactly as a hall is,
        // just over the stalls that belong to it: one mask hole each, which
        // leaves every stall between them dimmed, and the view targeted on
        // their combined extent.
        const zone = (zones || []).find(
          z => String(z.HallZoneMappingID) === String(selectedZoneId)
        );
        label = (zone && zone.Zone) || "Zone";

        // Only the selected hall's stalls: another hall can draw a stall with
        // the same code, and lighting it would show stalls outside the zone.
        const inZone = hallBooths.filter(b =>
          zoneBoothKeys.has(normalizeBoothKey(b.BoothName))
        );
        items = itemsFor(inZone.map(b => b.BoothName));

        // Never leave the map unframed. A zone whose stalls are not in the
        // drawing falls back to the hall, and says so.
        if (items.length === 0) {
          missing = `None of zone "${label}"'s ${zoneBoothKeys.size} stalls`;
          items = itemsFor(hallBooths.map(b => b.BoothName));
        }
      } else if (zoneBoothKeys) {
        // Zone picked, but neither the zone API nor the tracker places a single
        // stall in it. Stay framed on the hall rather than blanking the map.
        const zone = (zones || []).find(
          z => String(z.HallZoneMappingID) === String(selectedZoneId)
        );
        const hall = halls.find(h => h.HallID === filterHallId);
        label = hall ? hall.HallName : String(filterHallId);
        missing = `Zone "${(zone && zone.Zone) || selectedZoneId}" has no stalls mapped to it`;
        items = itemsFor(hallBooths.map(b => b.BoothName));
      } else {
        const hall = halls.find(h => h.HallID === filterHallId);
        label = hall ? hall.HallName : String(filterHallId);
        // An other area has no stalls; its outline frames it.
        items = hall && hall.areaBox ? [{ box: hall.areaBox, path: null }] : itemsFor(hallBooths.map(b => b.BoothName));
        if (items.length === 0) missing = `Hall "${label}"`;
      }

      // The dim-background + ring + text-label "spotlight" is Stall View's own
      // highlight. Zone Status and Hall Status already draw their own colored
      // regions and badges for this exact selection (the view-repaint effect
      // above) - stacking the spotlight's full-canvas dim on top of that drew
      // two conflicting highlights for the same pick ("ui gets disturbed").
      // The pan/zoom below is still shared: switching hall/zone should frame
      // it in every view, just without the extra chrome in these other two.
      drawFocusOverlay(currentViewFilter === "stall" ? items : null, items.length > 0 ? label : "");
      const union = unionBoxes(items.map((i) => i.box));
      const zoomed = union ? zoomToSvgBox(union) : false;

      // Failing silently here is what made this look broken - say so instead.
      setFocusError(
        !zoomed
          ? `${missing || label} could not be located in the layout drawing, so the view was not moved.`
          : missing
            ? `${missing} could be found in the drawing - showing the hall instead.`
            : ""
      );

      finishTimer = setTimeout(() => setIsProcessingFilter(false), 450);
    }, 120);

    return () => {
      clearTimeout(timer);
      if (finishTimer) clearTimeout(finishTimer);
    };
  }, [filterHallId, filterBoothId, currentViewFilter, zoneBoothKeys, selectedZoneId, zones, booths, halls, boothMap, getBoothBox, getShapePath, drawFocusOverlay, zoomToSvgBox]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const touchState = {
      isPinching: false,
      isPanning: false,
      startDistance: 0,
      startScale: 1,
      startMidpoint: { x: 0, y: 0 },
      startOffset: { x: 0, y: 0 },
      lastPan: { x: 0, y: 0 },
    };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const getMidpoint = (touches, rect) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
      y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
    });

    // A touch that starts inside the popup belongs to the popup.
    const fromPopup = (e) => {
      const t = e.target;
      return !!(t && t.closest && t.closest('[data-booth-popup]'));
    };

    const onTouchStart = (e) => {
      if (fromPopup(e)) return;
      const rect = container.getBoundingClientRect();
      if (e.touches.length === 2) {
        e.preventDefault();
        touchState.isPinching = true;
        touchState.isPanning = false;
        touchState.startDistance = getDistance(e.touches) || 1;
        touchState.startScale = transform.current.scale;
        touchState.startMidpoint = getMidpoint(e.touches, rect);
        touchState.startOffset = { x: transform.current.offsetX, y: transform.current.offsetY };
      } else if (e.touches.length === 1) {
        touchState.isPanning = true;
        touchState.isPinching = false;
        transform.current.hasDragged = false;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e) => {
      if (fromPopup(e)) return;
      if (touchState.isPinching && e.touches.length === 2) {
        e.preventDefault();
        hasUserAdjustedRef.current = true;
        const rect = container.getBoundingClientRect();
        const newDistance = getDistance(e.touches) || 1;
        const scaleFactor = newDistance / touchState.startDistance;
        const newScale = Math.max(0.4, Math.min(8, touchState.startScale * scaleFactor));

        const worldX = (touchState.startMidpoint.x - touchState.startOffset.x) / touchState.startScale;
        const worldY = (touchState.startMidpoint.y - touchState.startOffset.y) / touchState.startScale;

        transform.current.scale = newScale;
        transform.current.offsetX = touchState.startMidpoint.x - worldX * newScale;
        transform.current.offsetY = touchState.startMidpoint.y - worldY * newScale;
        updateTransformStyle();
      } else if (touchState.isPanning && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - touchState.lastPan.x;
        const dy = e.touches[0].clientY - touchState.lastPan.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          transform.current.hasDragged = true;
          hasUserAdjustedRef.current = true;
        }
        transform.current.offsetX += dx;
        transform.current.offsetY += dy;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        updateTransformStyle();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        touchState.isPinching = false;
        touchState.isPanning = false;
      } else if (e.touches.length === 1) {
        touchState.isPinching = false;
        touchState.isPanning = true;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [updateTransformStyle]);

  // ─── Route Path Rendering ───────────────────────────────────────────
  useEffect(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;
    const routeGroup = svgElement.querySelector('g#route-path');
    if (!routeGroup) return;

    routeGroup.innerHTML = '';

    if (!routeFromBoothId || !routeToBoothId) {
      setRouteError("");
      return;
    }

    if (routeFromBoothId === routeToBoothId) {
      setRouteError("");
      return;
    }

    setIsProcessingFilter(true);
    setFilterMessage("Routing optimal path...");
    setRouteError("");

    const timer = setTimeout(() => {
      const startId = String(routeFromBoothId);
      const endId = String(routeToBoothId);

      const startNode = boothNodesRef.current[startId];
      const endNode = boothNodesRef.current[endId];
      const grid = navGridRef.current;

      if (!startNode || !endNode || !grid) {
        setRouteError(
          !endNode
            ? `Destination "${endId}" does not exist on this layout.`
            : !startNode
              ? `Origin "${startId}" does not exist on this layout.`
              : "The walkable area could not be derived from this layout."
        );
        setIsProcessingFilter(false);
        return;
      }

      // Each stall is a sealed pocket, so step out to the nearest cell that is
      // actually on the aisle network - that is the stall's opening.
      const startCell = nearestAisleCell(grid, startNode.x, startNode.y);
      const endCell = nearestAisleCell(grid, endNode.x, endNode.y);

      if (!startCell || !endCell) {
        setRouteError(`No aisle could be found next to "${!startCell ? startId : endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      const cells = dijkstraOnGrid(grid, startCell, endCell);
      if (!cells || cells.length === 0) {
        setRouteError(`No walkable route connects "${startId}" and "${endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      // Anchor the drawn line on the booths themselves, not the aisle mouths.
      const waypoints = simplifyCells(cells).map(c => cellToPoint(grid, c));
      const points = [
        `${startNode.x},${startNode.y}`,
        ...waypoints.map(p => `${p.x},${p.y}`),
        `${endNode.x},${endNode.y}`,
      ].join(' ');

      const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      polyline.setAttribute('points', points);
      polyline.setAttribute('fill', 'none');
      polyline.setAttribute('stroke', '#ef4444'); // red route
      polyline.setAttribute('stroke-width', '2');
      polyline.setAttribute('stroke-dasharray', '6, 5');
      polyline.setAttribute('stroke-linejoin', 'round');
      polyline.setAttribute('stroke-linecap', 'round');
      polyline.setAttribute('pointer-events', 'none');

      const animate = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      animate.textContent = `
        @keyframes dash {
          to { stroke-dashoffset: -16; }
        }
        #route-path polyline {
          animation: dash 1s linear infinite;
        }
        @keyframes routePulse {
          0% { r: 5; opacity: 1; }
          50% { r: 9; opacity: 0.35; }
          100% { r: 5; opacity: 1; }
        }
        #route-path .route-pulse {
          animation: routePulse 1.4s ease-in-out infinite;
          pointer-events: none;
        }
      `;
      routeGroup.appendChild(animate);
      routeGroup.appendChild(polyline);

      // ─── "You are here" marker at the start node ───
      const startPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startPulse.setAttribute('cx', startNode.x);
      startPulse.setAttribute('cy', startNode.y);
      startPulse.setAttribute('r', '5');
      startPulse.setAttribute('fill', '#22c55e');
      startPulse.setAttribute('fill-opacity', '0.35');
      startPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(startPulse);

      const startDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startDot.setAttribute('cx', startNode.x);
      startDot.setAttribute('cy', startNode.y);
      startDot.setAttribute('r', '4');
      startDot.setAttribute('fill', '#22c55e');
      startDot.setAttribute('stroke', '#fff');
      startDot.setAttribute('stroke-width', '1.5');
      startDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startDot);

      const startLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const startLabelText = "You are here";
      startLabelBg.setAttribute('x', startNode.x - 30);
      startLabelBg.setAttribute('y', startNode.y - 22);
      startLabelBg.setAttribute('width', '60');
      startLabelBg.setAttribute('height', '14');
      startLabelBg.setAttribute('rx', '3');
      startLabelBg.setAttribute('fill', '#22c55e');
      startLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startLabelBg);

      const startLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      startLabel.setAttribute('x', startNode.x);
      startLabel.setAttribute('y', startNode.y - 12);
      startLabel.setAttribute('text-anchor', 'middle');
      startLabel.setAttribute('font-size', '7');
      startLabel.setAttribute('fill', '#fff');
      startLabel.setAttribute('font-weight', 'bold');
      startLabel.setAttribute('pointer-events', 'none');
      startLabel.textContent = startLabelText;
      routeGroup.appendChild(startLabel);

      // ─── "Destination" marker at the end node ───
      const endPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endPulse.setAttribute('cx', endNode.x);
      endPulse.setAttribute('cy', endNode.y);
      endPulse.setAttribute('r', '5');
      endPulse.setAttribute('fill', '#ef4444');
      endPulse.setAttribute('fill-opacity', '0.35');
      endPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(endPulse);

      const endDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endDot.setAttribute('cx', endNode.x);
      endDot.setAttribute('cy', endNode.y);
      endDot.setAttribute('r', '4');
      endDot.setAttribute('fill', '#ef4444');
      endDot.setAttribute('stroke', '#fff');
      endDot.setAttribute('stroke-width', '1.5');
      endDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endDot);

      const endLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const endLabelText = "Destination";
      endLabelBg.setAttribute('x', endNode.x - 27);
      endLabelBg.setAttribute('y', endNode.y - 22);
      endLabelBg.setAttribute('width', '54');
      endLabelBg.setAttribute('height', '14');
      endLabelBg.setAttribute('rx', '3');
      endLabelBg.setAttribute('fill', '#ef4444');
      endLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endLabelBg);

      const endLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      endLabel.setAttribute('x', endNode.x);
      endLabel.setAttribute('y', endNode.y - 12);
      endLabel.setAttribute('text-anchor', 'middle');
      endLabel.setAttribute('font-size', '7');
      endLabel.setAttribute('fill', '#fff');
      endLabel.setAttribute('font-weight', 'bold');
      endLabel.setAttribute('pointer-events', 'none');
      endLabel.textContent = endLabelText;
      routeGroup.appendChild(endLabel);

      // Auto-zoom to the whole corridor the route occupies.
      const routePts = [startNode, ...waypoints, endNode];
      const rx = routePts.map(p => p.x);
      const ry = routePts.map(p => p.y);
      const margin = 12;
      zoomToSvgBox({
        x: Math.min(...rx) - margin,
        y: Math.min(...ry) - margin,
        width: (Math.max(...rx) - Math.min(...rx)) + margin * 2,
        height: (Math.max(...ry) - Math.min(...ry)) + margin * 2,
      });
      setIsProcessingFilter(false);
    }, 50); // Small delay to allow the loading UI to render

    return () => clearTimeout(timer);
  }, [routeFromBoothId, routeToBoothId, booths, zoomToSvgBox]);


  if (!layout || !layout.svgContent) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="ri-alert-line me-2"></i>
        No SVG content available for this layout.
      </div>
    );
  }

  // Hall -> Zone -> Booth. With a zone chosen, only its stalls are offered.
  const hallBooths = shownHallId ? toolbarBoothList.filter(b => b.HallID === shownHallId) : [];
  const filteredBooths = zoneBoothKeys
    ? hallBooths.filter(b => zoneBoothKeys.has(normalizeBoothKey(b.BoothName)))
    : hallBooths;

  const zoneStallCount = zoneBoothKeys ? zoneBoothKeys.size : 0;
  // Zone is a convenience, not a step: "All Zones" offers the whole hall.
  // Booth selection is disabled when not in Stall View or when no hall is selected.
  const boothSelectDisabled = currentViewFilter !== "stall" || !shownHallId;
  const zoneSelectDisabled = currentViewFilter === "hall" || isVenueLike(currentViewFilter) || !shownHallId || isZoneLoading || zoneOptions.length === 0;

  // Everything the popup can truthfully say about a booth.
  const boothDetail = (() => {
    const empty = { zone: null, tasks: [], exhibitors: [], boothExhibitors: [], vendors: [], orders: [], dailyRoutines: [], possession: [], exhibitorByService: new Map() };
    if (!selectedBooth) return empty;

    const key = normalizeBoothKey(selectedBooth.BoothName);
    // This booth's own rows, hall-scoped (see attachRowsToBooths).
    const boothServiceRows = servicePartnerRowsByBooth.get(selectedBooth.BoothName) || [];
    const boothOrderRows = orderRowsByBooth.get(selectedBooth.BoothName) || [];
    const ownRows = new Set([...boothServiceRows, ...boothOrderRows]);

    const trackerZoneRow = boothServiceRows.find(
      (r) => firstValue(r.ZoneName) || firstValue(r.Zone)
    ) || boothOrderRows.find((r) => r.Zone || r.ZoneName);

    const zoneRow = (zoneStalls || []).find((s) => normalizeBoothKey(s.StallName) === key);
    const zoneName =
      (trackerZoneRow && (firstValue(trackerZoneRow.ZoneName) || firstValue(trackerZoneRow.Zone) || trackerZoneRow.Zone)) ||
      (zoneRow && zoneRow.Zone) ||
      null;

    const hallKey = String(selectedBooth.HallName || "").trim().toLowerCase();
    if (!hallKey) return { ...empty, zone: zoneName };

    const inHall = (r) => String(r.HallName || r.Hall || "").trim().toLowerCase() === hallKey;
    const isThisBooth = (r) => ownRows.has(r);

    const boothExhibitorRow = boothServiceRows[0] || boothOrderRows[0];

    // Exhibitors list
    const byExhibitor = new Map();
    const byService = new Map();
    const boothExhibitorIds = new Set();
    // One entry per exhibitor, filled from EVERY row that names it: a blank
    // on one row (an order row with no company, say) is taken from another
    // (the task or possession row) instead of sticking.
    const blank = (v) => v == null || (typeof v === "string" && v.trim() === "");
    const upsertExhibitor = (id, info) => {
      if (!byExhibitor.has(id)) byExhibitor.set(id, { id, services: new Set() });
      const ex = byExhibitor.get(id);
      Object.keys(info).forEach((k) => { if (blank(ex[k]) && !blank(info[k])) ex[k] = info[k]; });
      return ex;
    };

    effectiveServicePartnerRows.filter(inHall).forEach((r) => {
      if (isThisBooth(r) && r.ExhRegistrationID != null) {
        boothExhibitorIds.add(r.ExhRegistrationID);
      }
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      upsertExhibitor(id, {
        company: exhibitorField(r, "ExhibitorCompanyName", "CompanyName"),
        regNo: r.ExhRegistrationNo,
        email: exhibitorField(r, "ExhibitorEmail", "Email"),
        mobile: exhibitorField(r, "ExhibitorMobileNo", "MobileNo"),
        stallName: r.StallName || null,
        zone: firstValue(r.ZoneName) || firstValue(r.Zone) || null,
        stallType: r.StallTypeName,
        stallArea: r.StallArea,
      });
      const rowServiceId = firstValue(r.ServiceID);
      const svcName =
        r.name ||
        (rowServiceId != null ? serviceNameById.get(String(rowServiceId)) : null);
      if (svcName) {
        byExhibitor.get(id).services.add(svcName);
        if (!byService.has(svcName)) {
          byService.set(svcName, { id, company: byExhibitor.get(id).company });
        }
      }
    });

    effectiveExhibitorOrderRows.filter(inHall).forEach((r) => {
      if (isThisBooth(r) && r.ExhRegistrationID != null) {
        boothExhibitorIds.add(r.ExhRegistrationID);
      }
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      // Exhibitor fields only - VendorEmail / VendorContactNo are the service
      // partner's, and were shown as the exhibitor's contact before.
      upsertExhibitor(id, {
        company: r.Exhibitor || r.ExhibitorCompanyName,
        regNo: r.ExhRegistrationNo !== "NA" ? r.ExhRegistrationNo : null,
        email: r.ExhibitorEmail,
        mobile: r.ExhibitorMobileNo,
        stallName: r.Stall || r.StallName || null,
        zone: r.Zone || r.ZoneName || null,
        stallType: r.StallTypeName,
        stallArea: r.StallArea,
      });
      const svcName = r.Service || (r.ServiceID != null ? serviceNameById.get(String(r.ServiceID)) : null);
      if (svcName) {
        byExhibitor.get(id).services.add(svcName);
        if (!byService.has(svcName)) {
          byService.set(svcName, { id, company: byExhibitor.get(id).company });
        }
      }
    });

    // This booth's possession records also carry the exhibitor's name and
    // contact - the only source of them for a booth with orders only.
    (possessionRowsByBooth.get(selectedBooth.BoothName) || []).forEach((r) => {
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      boothExhibitorIds.add(id);
      upsertExhibitor(id, {
        company: r.ExhibitorCompanyName || r.ExhibitorName,
        regNo: r.ExhRegistrationNo,
        email: r.ExhibitorEmail,
        mobile: r.ExhibitorContactNumber,
        stallName: r.StallName || null,
        zone: r.Zone || null,
        stallType: r["Stall Type"],
      });
    });

    // Service partners on THIS stall (from servicePartnerRows)
    const vendors = Array.from(
      boothServiceRows.reduce((acc, r) => {
        const id =
          r.VendorID != null
            ? r.VendorID
            : vendorField(r, "VendorCompanyName", "CompanyName");
        if (id == null) return acc;
        if (!acc.has(id)) {
          acc.set(id, {
            id,
            company: vendorField(r, "VendorCompanyName", "CompanyName"),
            person: r.vendorPersonName || null,
            mobile: vendorField(r, "VendorMobileNo", "MobileNo"),
            email: r.VendorPrimaryEmail || r.PrimaryEmail || null,
            services: new Set(),
            serviceStatus: new Map(),
            overdue: 0,
          });
        }
        const svc = serviceNameOf(r);
        if (svc) {
          acc.get(id).services.add(svc);
          // The task's own status - the one the booth colour is built from.
          const raw = firstValue(r.StallTaskStatus);
          const st = (typeof raw === "string" && raw) || (typeof raw === "number" && STALL_TASK_STATUS[raw]) || "Pending";
          acc.get(id).serviceStatus.set(svc, st);
        }
        if (r.Overdue) acc.get(id).overdue += 1;
        return acc;
      }, new Map()).values()
    );

    // Exhibitor Orders on THIS stall (from exhibitorOrderRows)
    const orders = boothOrderRows.map((r) => {
      // Same rule as the map, so the badge matches the booth's colour.
      const status = orderStatusOf(r);

      return {
        id: r.HMWorkupdationID || `${r.ServiceID}_${r.ExhRegistrationID}`,
        serviceName: r.Service || serviceNameOf(r) || "Service",
        serviceId: r.ServiceID,
        vendorName: r.VendorName || "--",
        contactPerson: r.VendorContactPerson,
        email: r.VendorEmail,
        contactNo: r.VendorContactNo,
        status: status,
        possessionStatus: r.Status || "NA",
        lastUpdatedBy: r.LastUpdatedBy,
        remark: r.Remark || r.LastUpdatedRemark,
        isVerifyByHallManager: r.IsVerifyByHallManager,
        isVerifyByOrganizer: r.IsVerifyByOrganizer,
        scheduledDate: r.ScheduledDate,
        exhRegistrationID: r.ExhRegistrationID,
        exhibitorName: r.Exhibitor,
      };
    });

    // Stall possession records for THIS stall (data4), one per exhibitor.
    const possession = (possessionRowsByBooth.get(selectedBooth.BoothName) || []).map((r) => ({
      id: `${r.ExhRegistrationID}_${r.StallName}`,
      exhibitor: r.ExhibitorCompanyName || r.ExhibitorName || "--",
      contactName: r.ExhibitorName,
      mobile: r.ExhibitorContactNumber,
      email: r.ExhibitorEmail,
      regNo: r.ExhRegistrationNo,
      status: r.PossessionStatus,
      checklist: r.PendingChecklist,
      scheduledDate: r.ScheduledDate,
      issuedBy: r.IssuedBy,
      takenBy: r.PossessionTakenBy,
      takenAt: r.PossessionDateTime,
      remark: r.Remark,
    }));

    // Daily routine checklist for this hall (from dailyRoutineRows)
    const dailyRoutines = effectiveDailyRoutineRows.filter((r) =>
      String(r.HallName || r.Hall || "").trim().toLowerCase() === hallKey
    );

    return {
      zone:
        zoneName ||
        (boothExhibitorRow
          ? firstValue(boothExhibitorRow.ZoneName) || firstValue(boothExhibitorRow.Zone)
          : null),
      tasks: (taskRows || []).filter(inHall),
      boothExhibitors: Array.from(byExhibitor.values()).filter((e) =>
        boothExhibitorIds.has(e.id)
      ),
      exhibitors: Array.from(byExhibitor.values()),
      exhibitorByService: byService,
      vendors,
      orders,
      dailyRoutines,
      possession,
    };
  })();

  const boothExhibitor = boothDetail.boothExhibitors[0] || null;

  // Everything recorded against the clicked zone / hall. Rows are picked by the
  // same rule the cell's colour is rolled up with (trackerZoneStatusMap /
  // trackerHallStatusMap): the row's own hall and zone, the active data
  // sources and the service filter - so the panel always explains the colour.
  const areaDetail = (() => {
    if (!selectedArea) return null;
    const { kind, hallName, zoneKey, venueZone } = selectedArea;
    const hallK = normalizeKey(hallName);
    // A venue zone takes every row of its halls (the row's own VenueZone, else
    // its hall's) - the same rule venueZoneStatusMap colours it by.
    const inAreaRow = (r, rowHall, rowZone) => {
      if (kind === "venueZone") return venueZoneOfRow(r, rowHall) === String(venueZone);
      if (kind === "hall") return normalizeKey(rowHall) === hallK;
      return zoneKeyOf(rowHall, rowZone) === zoneKey;
    };
    const byPriority = (a, b) => TASK_STATUS_PRIORITY.indexOf(a.status) - TASK_STATUS_PRIORITY.indexOf(b.status);

    const spRows = isServicePartnerActive
      ? effectiveServicePartnerRows.filter(
        (r) => inAreaRow(r, r.HallName, firstValue(r.ZoneName) || firstValue(r.Zone)) && matchesService(firstValue(r.ServiceID))
      )
      : [];
    const orderRows = isExhibitorOrdersActive
      ? effectiveExhibitorOrderRows.filter(
        (r) => inAreaRow(r, r.Hall || r.HallName, r.Zone || r.ZoneName) && matchesService(r.ServiceID)
      )
      : [];
    const possRows = isPossessionMode ? possessionRows.filter((r) => inAreaRow(r, r.HallName, r.Zone)) : [];
    // Routines are per hall: a zone or hall gets its hall's, a venue zone all of its halls'.
    const routineRows = isDailyRoutineActive
      ? effectiveDailyRoutineRows.filter((r) =>
        kind === "venueZone"
          ? venueZoneOfRow(r, r.HallName || r.Hall) === String(venueZone)
          : normalizeKey(r.HallName || r.Hall) === hallK)
      : [];

    // Every order, service partner task and possession record is filed under
    // its exhibitor (registration id; reg no when the id is missing).
    const exhibitorKeyOf = (r) =>
      String(r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo || "unassigned");
    const exhibitors = new Map();
    const exhibitorOf = (r, info) => {
      const key = exhibitorKeyOf(r);
      if (!exhibitors.has(key)) {
        exhibitors.set(key, {
          id: key,
          exhRegistrationID: r.ExhRegistrationID,
          stalls: new Set(),
          orders: [],
          possession: [],
        });
      }
      const ex = exhibitors.get(key);
      Object.keys(info).forEach((k) => { if (ex[k] == null && info[k] != null && info[k] !== "") ex[k] = info[k]; });
      return ex;
    };

    // Service partner tasks are filed under the service partner, not the
    // exhibitor; the exhibitor still gets its details from the task row.
    const partners = new Map();
    spRows.forEach((r, i) => {
      // An other area's tasks are for the area, not an exhibitor: no card for them.
      const ex = r.ExhRegistrationID != null || r.ExhRegistrationNo
        ? exhibitorOf(r, {
          company: exhibitorField(r, "ExhibitorCompanyName", "CompanyName"),
          regNo: r.ExhRegistrationNo,
          email: exhibitorField(r, "ExhibitorEmail", "Email"),
          mobile: exhibitorField(r, "ExhibitorMobileNo", "MobileNo"),
          stallType: r.StallTypeName,
          stallArea: r.StallArea,
        })
        : null;
      if (ex && r.StallName) ex.stalls.add(r.StallName);
      const company = vendorField(r, "VendorCompanyName", "CompanyName");
      const partnerKey = String(r.VendorID != null ? r.VendorID : company || "unassigned");
      if (!partners.has(partnerKey)) {
        partners.set(partnerKey, {
          id: partnerKey,
          company,
          person: r.vendorPersonName || null,
          mobile: vendorField(r, "VendorMobileNo", "MobileNo"),
          email: r.VendorPrimaryEmail || r.PrimaryEmail || null,
          services: new Set(),
          tasks: [],
        });
      }
      const partner = partners.get(partnerKey);
      const { planned, scope } = deliverablesForTask(
        deliverableIndex, r.StallName, r.HallName, firstValue(r.ServiceID), r.VendorID
      );
      const serviceName = serviceNameOf({ ...r, ServiceID: firstValue(r.ServiceID) });
      if (serviceName) partner.services.add(serviceName);
      partner.tasks.push({
        id: `sp_${i}`,
        deliverables: planned,
        scope,
        serviceName,
        stall: r.StallName,
        exhibitor: ex ? ex.company : null,
        exhRegistrationID: r.ExhRegistrationID,
        status: servicePartnerRowStatus(r),
        overdue: r.Overdue || null,
        overdueCount: r.OverdueCount || 0,
      });
    });

    orderRows.forEach((r, i) => {
      const ex = exhibitorOf(r, { company: r.Exhibitor, regNo: r.ExhRegistrationNo });
      const stall = r.Stall || r.StallName;
      if (stall) ex.stalls.add(stall);
      ex.orders.push({
        id: r.HMWorkupdationID || `ord_${i}`,
        serviceName: r.Service || serviceNameOf(r) || "Service",
        stall,
        vendorName: r.VendorName,
        contactPerson: r.VendorContactPerson,
        contactNo: r.VendorContactNo,
        status: orderStatusOf(r),
        possessionStatus: r.Status,
        scheduledDate: r.ScheduledDate,
        lastUpdatedBy: r.LastUpdatedBy,
        remark: r.Remark || r.LastUpdatedRemark,
      });
    });

    possRows.forEach((r, i) => {
      const ex = exhibitorOf(r, {
        company: r.ExhibitorCompanyName || r.ExhibitorName,
        contactName: r.ExhibitorName,
        regNo: r.ExhRegistrationNo,
        email: r.ExhibitorEmail,
        mobile: r.ExhibitorContactNumber,
        country: r.CountryName,
        stallType: r["Stall Type"],
      });
      if (r.StallName) ex.stalls.add(r.StallName);
      ex.possession.push({
        id: `ps_${i}`,
        stall: r.StallName,
        status: r.PossessionStatus,
        checklist: r.PendingChecklist,
        scheduledDate: r.ScheduledDate,
        issuedBy: r.IssuedBy,
        takenBy: r.PossessionTakenBy,
        takenAt: r.PossessionDateTime,
        remark: r.Remark,
      });
    });

    const possessionRank = (s) => POSSESSION_ORDER.indexOf(s);
    const exhibitorList = Array.from(exhibitors.values())
      .map((ex) => {
        ex.orders.sort(byPriority);
        ex.possession.sort((a, b) => possessionRank(a.status) - possessionRank(b.status));
        const status = isPossessionMode
          ? rollupPossession(ex.possession.map((p) => p.status))
          : rollupTaskStatus(ex.orders.map((t) => t.status));
        return { ...ex, stalls: Array.from(ex.stalls).sort(), status };
      })
      // Worst first, the order the colour legend reads in.
      .sort((a, b) => {
        const rank = (s) => (isPossessionMode ? possessionRank(s) : TASK_STATUS_PRIORITY.indexOf(s));
        const ra = rank(a.status), rb = rank(b.status);
        if (ra !== rb) return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
        return String(a.company || "").localeCompare(String(b.company || ""));
      });

    const routines = routineRows.map((r, i) => ({
      id: r.DailyRoutineChecklistHdrID || `dr_${i}`,
      title: r.ProgramTitle,
      times: r.ScheduleTimes,
      responsible: r.ResponsiblePerson,
      done: r.TotalCompCount,
      total: r.TotalCount,
      hall: r.HallName || r.Hall,
      status: dailyRoutineRowStatus(r),
      row: r, // the tracker row, to open its update screen
    })).sort(byPriority);

    // The drawn stalls of this zone / hall / venue zone, each with its own map status.
    const memberKeys = kind === "zone" ? zoneMembers.get(zoneKey) || new Set() : null;
    const boothInArea = (b) =>
      kind === "venueZone"
        ? hallVenueZone.get(hallNameKey(b.HallName)) === String(venueZone)
        : normalizeKey(b.HallName) === hallK && (!memberKeys || memberKeys.has(normalizeBoothKey(b.BoothName)));
    const stalls = booths
      .filter(boothInArea)
      .map((b) => {
        const { status, label } = boothDisplayStatus(b);
        return { name: b.BoothName, booth: b, hall: b.HallName, status, label };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

    // A venue zone's halls, each with its Hall View status.
    const hallList = kind === "venueZone"
      ? Array.from(hallVenueZone.entries())
        .filter(([, vz]) => vz === String(venueZone))
        .map(([hk]) => {
          const venueHall = VENUE_HALLS.find((h) => hallNameKey(h.hallName) === hk);
          const name = venueHall ? venueHall.hallName : hk.replace(/\b\w/g, (c) => c.toUpperCase());
          return {
            name,
            status: hallStatusMap.get(normalizeKey(name)) || null,
            stallCount: stalls.filter((st) => hallNameKey(st.hall) === hk).length,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      : [];

    const countOf = new Map();
    const countItem = (t) => countOf.set(t.status, (countOf.get(t.status) || 0) + 1);
    const partnerList = Array.from(partners.values())
      .map((sp) => {
        sp.tasks.sort(byPriority);
        return {
          ...sp,
          services: Array.from(sp.services).sort(),
          status: rollupTaskStatus(sp.tasks.map((t) => t.status)),
        };
      })
      .sort((a, b) => {
        const ra = TASK_STATUS_PRIORITY.indexOf(a.status), rb = TASK_STATUS_PRIORITY.indexOf(b.status);
        if (ra !== rb) return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
        return String(a.company || "").localeCompare(String(b.company || ""));
      });

    exhibitorList.forEach((ex) => (isPossessionMode ? ex.possession : ex.orders).forEach(countItem));
    if (!isPossessionMode) {
      partnerList.forEach((sp) => sp.tasks.forEach(countItem));
      routines.forEach(countItem);
    }
    const counts = (isPossessionMode ? POSSESSION_ORDER : TASK_STATUS_PRIORITY)
      .filter((s) => countOf.has(s))
      .map((s) => ({ status: s, count: countOf.get(s) }));

    const totals = {
      orders: exhibitorList.reduce((t, ex) => t + ex.orders.length, 0),
      tasks: partnerList.reduce((t, sp) => t + sp.tasks.length, 0),
      possession: exhibitorList.reduce((t, ex) => t + ex.possession.length, 0),
    };

    const status = kind === "venueZone"
      ? venueZoneStatusMap.get(String(venueZone))
      : kind === "zone" ? zoneStatusMap.get(zoneKey) : hallStatusMap.get(hallK);

    return { status, stalls, halls: hallList, exhibitors: exhibitorList, servicePartners: partnerList, routines, counts, totals };
  })();

  // "Notify" on any popup row that carries a phone or an email.
  // Hidden for now - sending is not wired to a backend yet. To bring it back,
  // restore the body below; the popup rows and NotifyModal are still in place.
  // eslint-disable-next-line no-unused-vars
  const renderNotifyButton = (target) => null;
  // const renderNotifyButton = (target) => {
  //   if (!target || (!target.mobile && !target.email)) return null;
  //   return (
  //     <button
  //       type="button"
  //       className="btn btn-sm btn-outline-success py-0 px-2 mt-1"
  //       style={{ fontSize: "11px" }}
  //       title={`Notify ${target.name || "this contact"}`}
  //       onClick={(e) => {
  //         e.stopPropagation();
  //         setNotifyTarget({ ...target, booth: selectedBooth ? selectedBooth.BoothName : "" });
  //       }}
  //     >
  //       <i className="ri-notification-3-line me-1"></i>Notify
  //     </button>
  //   );
  // };

  const selectedServiceName = selectedServiceId
    ? (() => {
      const s = (services || []).find((x) => String(x.ServiceID) === String(selectedServiceId));
      return s ? s.Name || s.DisplayName || "" : "";
    })()
    : "";

  const popupStatus = boothDisplayStatus(selectedBooth);

  const baseLegend = isPossessionMode ? POSSESSION_LEGEND : STATUS_LEGEND;
  const legendItems = baseLegend.concat(
    Object.keys(shownStatusCounts)
      .filter((s) => !baseLegend.some((i) => i.status === s))
      .map((s) => ({ status: s, color: getStatusColor(s) }))
  );

  // ─── Applied-filter summary (beside the legend, and in full screen) ───
  // What the colours on the map are built from right now, so nobody has to
  // reopen every dropdown to know what they are looking at.
  const VIEW_LABELS = { stall: "Stall View", zone: "Zone View", hall: "Hall View", venue: "Venue View", venueZone: "Venue Zone View" };
  const dataSourceLabels = currentDataFilters
    .map((k) => (DATA_FILTER_OPTIONS.find((o) => o.key === k) || {}).label)
    .filter(Boolean);
  const pickedZoneName = selectedZoneId
    ? ((zonesInScope.find((z) => String(z.HallZoneMappingID) === String(selectedZoneId)) || {}).Zone || "")
    : "";
  const pickedBoothName = filterBoothId ? ((boothMap[filterBoothId] || {}).BoothName || String(filterBoothId)) : "";
  const filterChips = [
    { label: "View", value: VIEW_LABELS[currentViewFilter] || currentViewFilter },
    { label: "Data", value: dataSourceLabels.length ? dataSourceLabels.join(", ") : "None" },
    { label: "Hall", value: selectedHallName || (hideAllHallsOption && !isVenueLike(currentViewFilter) ? layout.LayoutName : "All Halls") },
    currentViewFilter !== "hall" && !isVenueLike(currentViewFilter) && selectedHallName
      ? { label: "Zone", value: pickedZoneName || "All Zones" }
      : null,
    currentViewFilter === "stall" && pickedBoothName ? { label: "Booth", value: pickedBoothName } : null,
    !isPossessionMode ? { label: "Service", value: selectedServiceName || "All Services" } : null,
  ].filter(Boolean);

  const filterExplanation = (() => {
    const svc = selectedServiceName ? ` for ${selectedServiceName}` : "";
    if (isPossessionMode) {
      const subject = currentViewFilter === "stall" ? "Each booth" : currentViewFilter === "zone" ? "Each zone" : currentViewFilter === "venueZone" ? "Each venue zone" : "Each hall";
      return `${subject} is coloured by its stall possession status (least advanced exhibitor first).`;
    }
    const stallSources = [
      isServicePartnerActive && "Service Partner Tasks",
      isExhibitorOrdersActive && "Exhibitor Orders",
    ].filter(Boolean);
    if (stallSources.length === 0 && !isDailyRoutineActive) {
      return "No data source selected - every booth reads as Unallocated.";
    }
    if (currentViewFilter === "stall") {
      if (stallSources.length === 0) return "Each booth takes its hall's Daily Routine status (routines are recorded per hall).";
      return `Each booth is coloured by its ${stallSources.join(" + ")}${svc}.${isDailyRoutineActive ? " Daily Routine counts in Zone / Hall / Venue View." : ""}`;
    }
    const all = stallSources.concat(isDailyRoutineActive ? ["Daily Routine"] : []);
    if (currentViewFilter === "zone") {
      if (stallSources.length === 0) return "Each zone takes its hall's Daily Routine status.";
      return `Each zone is coloured by its stalls' ${stallSources.join(" + ")}${isDailyRoutineActive ? " + its hall's Daily Routine" : ""}${svc}${pickedZoneName ? ` - showing ${pickedZoneName} only` : ""}.`;
    }
    if (currentViewFilter === "venueZone") {
      return `The venue is split in two (VenueZone from the procedure); each venue zone is coloured by its halls' ${all.join(" + ")}${svc}.`;
    }
    return `Each hall is coloured by its ${all.join(" + ")}${svc}.`;
  })();

  const handleWheel = (e) => {
    e.preventDefault();
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    const pointX = e.clientX - rect.left;
    const pointY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAtPoint(transform.current.scale * delta, pointX, pointY);
    updateTransformStyle();
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      transform.current.isDragging = true;
      transform.current.hasDragged = false;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      transform.current.dragInitial = { x: e.clientX, y: e.clientY };
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (transform.current.isDragging) {
      const dxTotal = e.clientX - transform.current.dragInitial.x;
      const dyTotal = e.clientY - transform.current.dragInitial.y;
      if (Math.abs(dxTotal) > 5 || Math.abs(dyTotal) > 5) {
        transform.current.hasDragged = true;
        hasUserAdjustedRef.current = true;
      }

      const dx = e.clientX - transform.current.dragStart.x;
      const dy = e.clientY - transform.current.dragStart.y;
      transform.current.offsetX += dx;
      transform.current.offsetY += dy;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      updateTransformStyle();
    }
  };

  const handleMouseUp = () => {
    transform.current.isDragging = false;
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  };

  const zoomIn = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 1.2, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const zoomOut = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 0.8, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const handleReset = () => {
    setIsProcessingFilter(true);
    setFilterMessage("Resetting layout view & filters...");

    hasUserAdjustedRef.current = false;
    if (!applyDefaultView(true)) {
      transform.current.scale = 1;
      transform.current.offsetX = 0;
      transform.current.offsetY = 0;
      updateTransformStyle(true);
    }

    drawFocusOverlay(null);
    if (onFilterHallChange) onFilterHallChange("");
    if (onFilterBoothChange) onFilterBoothChange("");
    if (onZoneChange) onZoneChange("");
    if (onServiceChange) onServiceChange("");
    setRouteFromBoothId("");
    setRouteToBoothId("");
    setRouteError("");
    setFocusError("");

    setTimeout(() => {
      setIsProcessingFilter(false);
    }, 400);
  };

  const handleHallFilterChange = (e) => {
    const picked = e.target.value;
    // "all" (every layout) and this drawing's own "layout:<id>" both mean no
    // hall here - reset this map - but "all" still goes up as "all".
    const isAll = picked === "all";
    const hallId = isAll || picked === `layout:${layout.MstHallLayoutID}` ? "" : picked;
    // A hall of another stacked layout zooms THAT layout; waiting here would
    // leave this one's spinner on (its own filter never changes).
    if (!hallId || ownsHall(hallId) || filterHallId) {
      setIsProcessingFilter(true);
      setFilterMessage(hallId ? "Filtering & zooming to Hall..." : "Resetting hall filter...");
    }
    if (onFilterHallChange) onFilterHallChange(isAll ? "all" : hallId);
    if (onFilterBoothChange) onFilterBoothChange("");

    if (!hallId) {
      hasUserAdjustedRef.current = false;
      if (!applyDefaultView(true)) {
        transform.current.scale = 1;
        transform.current.offsetX = 0;
        transform.current.offsetY = 0;
        updateTransformStyle(true);
      }

      setTimeout(() => {
        setIsProcessingFilter(false);
      }, 400);
    }
  };

  // Venue -> Hall: a hall clicked on the venue map opens it in Hall View,
  // when the hall layout drawing has it.
  const handleVenueHallClick = (venueHall) => {
    const hall = (toolbarHalls || []).find((h) => hallNameKey(h.HallName) === hallNameKey(venueHall.hallName));
    if (!hall) {
      setFocusError(`${venueHall.label} is not in the hall layout drawing.`);
      return;
    }
    setFocusError("");
    handleViewFilterSelect("hall");
    if (onFilterHallChange) onFilterHallChange(hall.HallID);
  };

  const handleBoothFilterChange = (e) => {
    const boothId = e.target.value;
    if (ownsBooth(boothId) || filterBoothId || (!boothId && filterHallId)) {
      setIsProcessingFilter(true);
      setFilterMessage(boothId ? "Focusing on selected Booth..." : "Viewing all booths in Hall...");
    }
    if (onFilterBoothChange) onFilterBoothChange(boothId);
  };

  return (
    <div
      ref={rootRef}
      className="svg-canvas-container text-start"
      style={isFullscreen ? FULLSCREEN_ROOT_STYLE : undefined}
    >
      <NotifyModal target={notifyTarget} onClose={closeNotify} onSend={handleSendNotification} />
      {/* Service partner team of the booth's hall (info icon by Exhibitor
          Orders). Inside the root so it stays on top in Full Screen. */}
      {teamModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 2000, backgroundColor: "rgba(15, 23, 42, 0.45)" }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeTeamModal(); }}
          data-booth-popup
        >
          <div
            className="bg-white shadow rounded d-flex flex-column"
            style={{ width: "460px", maxWidth: "calc(100vw - 32px)", maxHeight: "80vh" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
              <span className="fw-semibold" style={{ fontSize: "14px", color: "#007062" }}>
                <i className={`${teamModal.kind === "agency" ? "ri-building-4-line" : "ri-team-line"} me-1`}></i>
                {teamModal.kind === "agency" ? "Onsite Agency Team" : "Service Partner Team"}
                {teamModal.hallName ? <span className="text-muted fw-normal" style={{ fontSize: "12px" }}> · {teamModal.hallName}</span> : null}
              </span>
              <button type="button" className="btn-close" style={{ fontSize: "10px" }} onClick={closeTeamModal} aria-label="Close" />
            </div>
            <div className="px-3 py-2" style={{ overflowY: "auto" }}>
              {teamModal.kind === "agency"
                ? renderOnsiteAgency(teamModal.agencies, teamModal.hallName)
                : renderTeamCards(teamModal.team, `No team members mapped to ${teamModal.hallName || "this hall"}.`)}
            </div>
          </div>
        </div>
      )}
      {compact && (
        <div className="d-flex align-items-center gap-2 mt-3 mb-1 px-1">
          <i className="ri-map-2-line" style={{ color: '#007062' }}></i>
          <span className="fw-semibold" style={{ color: '#007062', fontSize: '15px' }}>{layout.LayoutName}</span>
        </div>
      )}
      {!compact && (<>
      {/* ─── Title & Toolbar Card ─── */}
      <div className="card shadow-none border p-2 mb-2 bg-white" style={{ borderRadius: '2px', borderColor: '#e4e7eb' }}>
        <h6 className="mb-2 mt-0 fw-semibold text-start" style={{ color: '#007062', fontSize: '16px' }}>
          <i className="ri-map-2-line me-2"></i><span>Hall Layout View</span>
        </h6>
        <div className="svg-toolbar d-flex gap-2 flex-wrap align-items-center">
          <div className="btn-group" role="group">
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" onClick={zoomIn} title="Zoom In" style={{ color: '#4a4a4a', height: '32px' }}>
              <i className="ri-zoom-in-line"></i>
            </button>
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" disabled ref={zoomLabelRef} style={{ color: '#4a4a4a', height: '32px' }}>
              100%
            </button>
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" onClick={zoomOut} title="Zoom Out" style={{ color: '#4a4a4a', height: '32px' }}>
              <i className="ri-zoom-out-line"></i>
            </button>
          </div>

          {/* ─── 1. View Filter Dropdown (Main Filter) ──────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm rounded-0 fw-semibold shadow-none"
              style={{ ...TOOLBAR_SELECT_STYLE, minWidth: '140px', borderColor: '#007062', color: '#007062', backgroundColor: '#f0fdfa' }}
              value={currentViewFilter}
              onChange={(e) => handleViewFilterSelect(e.target.value)}
              title={otherAreaOnly ? "Other areas have no stalls or zones - Hall View only" : "Select View Mode"}
            >
              <option value="stall" disabled={otherAreaOnly}>1 - Stall View</option>
              <option value="zone" disabled={otherAreaOnly}>2 - Zone View</option>
              <option value="hall">3 - Hall View</option>
              <option value="venue">4 - Venue View</option>
              <option value="venueZone">5 - Venue Zone View</option>
            </select>
          </div>

          {otherAreaOnly && (
            <span
              className="badge rounded-pill border d-inline-flex align-items-center gap-1"
              style={{ backgroundColor: '#fff7ed', color: '#9a3412', borderColor: '#fdba74', fontSize: '11px', height: '24px' }}
              title="Other areas (banquet / conference / food court) have no stalls or zones, so they are shown in Hall View only"
            >
              <i className="ri-information-line"></i>Other area · Hall View only
            </span>
          )}

          {/* ─── 2. Data Filter Multi-Select Checkbox Dropdown ───────── */}
          <div ref={dataFilterRef} className="position-relative d-inline-block">
            <button
              type="button"
              className="btn btn-sm bg-white border rounded-0 d-flex align-items-center gap-2 px-2 shadow-none"
              style={{ height: '32px', fontSize: '13px', color: '#1f2937', minWidth: '140px', justifyContent: 'space-between', borderColor: isDataFilterOpen ? '#007062' : undefined }}
              onClick={() => setIsDataFilterOpen(!isDataFilterOpen)}
              title="Select Data Filters"
              aria-haspopup="true"
              aria-expanded={isDataFilterOpen}
            >
              <span className="d-flex align-items-center gap-1 text-truncate">
                <i className="ri-filter-3-line" style={{ color: '#007062' }}></i>
                <span>Data</span>
                <span className="badge rounded-pill" style={{ backgroundColor: '#007062', fontSize: '11px' }}>
                  {currentDataFilters.length}
                </span>
              </span>
              <i className={`ri-arrow-${isDataFilterOpen ? 'up' : 'down'}-s-line text-muted`}></i>
            </button>

            {isDataFilterOpen && (
              <div
                className="shadow position-absolute bg-white py-1"
                role="menu"
                style={{
                  top: 'calc(100% + 4px)',
                  left: 0,
                  zIndex: 1060,
                  minWidth: '230px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center px-3 py-1 mb-1 border-bottom">
                  <span className="fw-semibold text-dark" style={{ fontSize: '12px' }}>Show data from</span>
                  <span className="text-muted" style={{ fontSize: '11px' }}>{currentDataFilters.length} of {viewDataOptions.length}</span>
                </div>
                {/* Only the sources this view uses (VIEW_DATA_SOURCES). */}
                {viewDataOptions.map((opt, idx) => {
                  const checked = currentDataFilters.includes(opt.key);
                  const firstExclusive = opt.exclusive && !(viewDataOptions[idx - 1] || {}).exclusive;
                  // The last active source cannot be switched off (handleToggleDataFilter).
                  const locked = checked && currentDataFilters.length === 1;
                  return (
                    <React.Fragment key={opt.key}>
                    {firstExclusive && (
                      <div className="border-top mt-1 pt-1 px-3 text-muted" style={{ fontSize: '11px' }}>
                        Single select
                      </div>
                    )}
                    <label
                      className="d-flex align-items-center gap-2 px-3 py-2 mb-0 w-100"
                      style={{
                        cursor: locked ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        backgroundColor: checked ? '#f0fdfa' : 'transparent',
                        color: '#1f2937',
                      }}
                      title={
                        locked
                          ? opt.exclusive
                            ? 'Pick another data source to leave Stall Possession'
                            : 'At least one data source must stay selected'
                          : undefined
                      }
                    >
                      <input
                        type={opt.exclusive ? "radio" : "checkbox"}
                        className="form-check-input shadow-none flex-shrink-0"
                        // .form-check-input carries margin-left:-1.5em (and app CSS may
                        // float it), which pushed the box out past the menu edge.
                        style={{
                          position: 'static',
                          float: 'none',
                          margin: 0,
                          width: '16px',
                          height: '16px',
                          ...(checked ? { backgroundColor: '#007062', borderColor: '#007062' } : null),
                        }}
                        checked={checked}
                        disabled={locked}
                        onChange={() => handleToggleDataFilter(opt.key)}
                      />
                      <i className={`${opt.icon} text-muted`} style={{ fontSize: '14px' }}></i>
                      <span className="flex-grow-1" style={{ fontSize: '13px', lineHeight: 1.3 }}>{opt.label}</span>
                    </label>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Hall Filter Dropdown ─────────────────────────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm rounded-0 shadow-none"
              style={TOOLBAR_SELECT_STYLE}
              value={shownHallId || ""}
              onChange={handleHallFilterChange}
              title="Select Hall to zoom"
            >
              {!hideAllHallsOption && <option value="">All Halls</option>}
              {toolbarHalls.map((hall) => (
                <option key={hall.HallID} value={hall.HallID}>
                  {hall.HallName}{hall.BoothCount != null ? ` (${hall.BoothCount})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Zone Filter Dropdown (cascades from Hall) ────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm rounded-0 shadow-none"
              style={TOOLBAR_SELECT_STYLE}
              value={selectedZoneId || ""}
              onChange={(e) => onZoneChange && onZoneChange(e.target.value)}
              disabled={zoneSelectDisabled}
              title={
                currentViewFilter === "hall" || isVenueLike(currentViewFilter) ? "Zone filter disabled in Hall and Venue view"
                  : !shownHallId ? "Select a Hall first"
                    : isZoneLoading ? "Loading zones..."
                      : zoneOptions.length === 0 ? "No zones for this hall"
                        : "Select Zone"
              }
            >
              <option value="">
                {currentViewFilter === "hall" ? "N/A in Hall View" : isVenueLike(currentViewFilter) ? "N/A in Venue View" : isZoneLoading ? "Loading zones..." : zoneOptions.length === 0 ? "No Zones" : "All Zones"}
              </option>
              {zoneOptions.map((zone) => (
                <option key={zone.HallZoneMappingID} value={zone.HallZoneMappingID}>
                  {zone.Zone}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Booth Filter Dropdown (cascades from Zone) ───────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm rounded-0 shadow-none"
              style={TOOLBAR_SELECT_STYLE}
              value={shownBoothId || ""}
              onChange={handleBoothFilterChange}
              disabled={boothSelectDisabled}
              title={
                currentViewFilter !== "stall" ? "Booth filter available in Stall View only"
                  : !shownHallId ? "Select a Hall first"
                    : "Select Booth to zoom"
              }
            >
              <option value="">{currentViewFilter !== "stall" ? "N/A in Zone/Hall" : "All Booths"}</option>
              {filteredBooths.map((booth) => (
                <option key={booth.BoothName} value={booth.BoothName}>
                  {booth.BoothName}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Service Filter ───────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-select form-select-sm rounded-0 shadow-none"
              style={TOOLBAR_SELECT_STYLE}
              value={selectedServiceId || ""}
              onChange={(e) => onServiceChange && onServiceChange(e.target.value)}
              disabled={services.length === 0}
              title={services.length === 0 ? "No services available" : "Filter by Service"}
            >
              <option value="">All Services</option>
              {services.map((svc) => (
                <option key={svc.ServiceID} value={svc.ServiceID}>
                  {svc.Name || svc.DisplayName}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-sm bg-white d-flex align-items-center justify-content-center flex-shrink-0 rounded-0"
            style={{ border: '1px solid #007062', color: '#007062', height: '32px', fontSize: '13px' }}
            onClick={() => onOpenTaskDetail && onOpenTaskDetail()}
            title="Open the task list for the selected Hall and Service"
          >
            <i className="ri-file-text-line me-1"></i>
            Tasks
          </button>

          <button
            type="button"
            className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center"
            style={{ color: '#4a4a4a', height: '32px', fontSize: '13px' }}
            onClick={handleReset}
            title="Reset View & Filters"
          >
            <i className="ri-refresh-line me-1"></i>
            Reset
          </button>

          <button
            type="button"
            className="btn btn-sm bg-white rounded-0 d-flex align-items-center justify-content-center"
            style={{ border: '1px solid #007062', color: '#007062', height: '32px', fontSize: '13px' }}
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit full screen (Esc)" : "Show the layout in full screen"}
          >
            <i className={`${isFullscreen ? "ri-fullscreen-exit-line" : "ri-fullscreen-line"} me-1`}></i>
            {isFullscreen ? "Exit Full Screen" : "Full Screen"}
          </button>

          {isProcessingFilter && (
            <div className="d-flex align-items-center gap-2 text-primary fw-semibold bg-primary-subtle border border-primary-subtle px-2 py-1 rounded">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <small>{filterMessage || "Processing filter..."}</small>
            </div>
          )}
        </div>
      </div>
      </>)}

      {/* ─── Focus (hall / booth) warning banner ─── */}
      {focusError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-crosshair-line me-2"></i>
          <span className="small fw-semibold">{focusError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setFocusError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {/* ─── Route error banner ─── */}
      {routeError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-error-warning-line me-2"></i>
          <span className="small fw-semibold">{routeError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setRouteError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {!compact && (<>
      {/* ─── Status legend: one compact row, sits directly above the map ─── */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2" style={{ fontSize: '14px' }}>
        <span className="fw-semibold text-dark">
          <i className="ri-palette-line me-1"></i>
          {isPossessionMode ? "Stall Possession" : currentViewFilter === "stall" ? "Stall Status" : currentViewFilter === "zone" ? "Zone Status" : currentViewFilter === "venue" ? "Venue - Hall Status" : currentViewFilter === "venueZone" ? "Venue Zone Status" : "Hall Status"}:
        </span>
        {legendItems.map((item) => (
          <span key={item.status} className="d-inline-flex align-items-center gap-1">
            <span
              style={{
                width: '14px',
                height: '14px',
                borderRadius: '3px',
                backgroundColor: item.color,
                border: '1px solid #d1d5db',
                display: 'inline-block',
              }}
            />
            {item.status}
            {!isStatusLoading && (shownStatusCounts[item.status] || 0) > 0 && (
              <strong className="text-dark">{shownStatusCounts[item.status]}</strong>
            )}
          </span>
        ))}
        {isStatusLoading && (
          <span className="d-inline-flex align-items-center gap-1">
            <span className="spinner-border spinner-border-sm" role="status" style={{ width: '0.9rem', height: '0.9rem' }}></span>
          </span>
        )}

        {/* Applied filters, inline right after the legend: what the colours
            are built from right now. The full explanation is on the (i). */}
        <span
          className="d-inline-flex align-items-center gap-2 ps-3"
          style={{ borderLeft: '1px solid #d1d5db', fontSize: '12.5px' }}
        >
          <span className="fw-semibold" style={{ color: '#007062' }}>
            <i className="ri-filter-3-line me-1"></i>Applied:
          </span>
          {filterChips.map((chip, i) => (
            <span key={chip.label} className="text-nowrap">
              {i > 0 && <span className="text-muted me-2">·</span>}
              <span className="text-muted">{chip.label}:</span> <span className="fw-semibold text-dark">{chip.value}</span>
            </span>
          ))}
          <i
            className="ri-information-line"
            style={{ color: '#007062', cursor: 'help', fontSize: '15px' }}
            title={filterExplanation}
            aria-label={filterExplanation}
          ></i>
        </span>
      </div>
      </>)}

      {/* SVG Canvas and Sidebar Container */}
      <div ref={stageRef} className="position-relative border border-secondary-subtle rounded overflow-hidden bg-white p-0">
        {/* SVG Canvas Area */}
        {currentViewFilter === "venue" && (
          <VenueView
            height={canvasBox.height || 500}
            statusOfHall={(name) =>
              hallStatusMap.get(normalizeKey(name)) || (isPossessionMode ? NO_POSSESSION_RECORD : null)
            }
            getStatusColor={getStatusColor}
            selectedHallName={selectedHallName}
            onHallClick={handleVenueHallClick}
          />
        )}
        {currentViewFilter === "venueZone" && (
          <VenueView
            height={canvasBox.height || 500}
            getStatusColor={getStatusColor}
            zones={venueZones}
            onZoneClick={(zone) => {
              setSelectedBooth(null);
              setSelectedArea({ kind: "venueZone", venueZone: zone.id, zoneName: zone.label, hallName: "", zoneKey: "" });
            }}
          />
        )}
        {/* Hidden, not unmounted, in Venue View: the booth index, outlines and
            nav grid were built against this DOM and would not be rebuilt. */}
        <div ref={canvasWrapRef} style={{ width: "100%", display: isVenueLike(currentViewFilter) ? "none" : undefined }}>
          <div
            ref={containerRef}
            className="canvas-container position-relative"
            style={{
              width: "100%",
              height: `${canvasBox.height}px`,
              overflow: "hidden",
              cursor: "grab",
              backgroundColor: "#f8f9fa",
              userSelect: "none",
              position: "relative",
              touchAction: "none",
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {isProcessingFilter && (
              <div
                className="position-absolute d-flex align-items-center gap-2 bg-white shadow-sm border rounded-pill px-3 py-2"
                style={{
                  top: "12px",
                  left: "12px",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              >
                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: "1rem", height: "1rem" }}>
                  <span className="visually-hidden">Processing...</span>
                </div>
                <small className="fw-semibold text-dark">{filterMessage || "Processing filter..."}</small>
              </div>
            )}

            <MemoizedSvg ref={svgRef} svgContent={layout.svgContent} />

          </div>
        </div>

        {/* Zone / hall details, opened by clicking a cell in Zone / Hall View */}
        {selectedArea && areaDetail && (
          <AreaDetailPanel
            key={`${selectedArea.kind}|${selectedArea.hallName}|${selectedArea.zoneKey}|${selectedArea.venueZone || ""}`}
            area={selectedArea}
            otherArea={selectedArea.kind === "hall" && isOtherAreaHall({ HallName: selectedArea.hallName }) ? otherAreaNameOf(selectedArea.hallName) : null}
            detail={areaDetail}
            flags={{
              servicePartner: isServicePartnerActive,
              orders: isExhibitorOrdersActive,
              dailyRoutine: isDailyRoutineActive,
              possession: isPossessionMode,
            }}
            isTaskLoading={isTaskLoading}
            onClose={() => setSelectedArea(null)}
            onOpenManualForm={onOpenManualForm}
            onOpenDailyRoutine={onOpenDailyRoutine}
            onSelectBooth={(booth) => {
              setSelectedArea(null);
              setSelectedBooth(booth);
            }}
            getStatusColor={getStatusColor}
            statusBadgeStyle={statusBadgeStyle}
          />
        )}

        {/* Sidebar Area for Selected Booth Details (Overlapping) */}
        {selectedBooth && (
          <div
            className="position-absolute shadow-lg bg-white d-flex flex-column border-end"
            style={{
              top: 0,
              left: 0,
              width: '390px',
              height: '100%',
              zIndex: 20
            }}
            onWheel={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center p-2 border-bottom sticky-top" style={{ backgroundColor: '#f3f4f6' }}>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-semibold d-flex align-items-center text-dark" style={{ fontSize: '14px' }}>
                  {selectedBooth.BoothName}
                </h6>
                <div className="d-flex align-items-center gap-1 rounded-pill px-2 py-1" style={{ backgroundColor: '#e2e8f0', fontSize: '11px', fontWeight: '500' }}>
                  <i className="ri-map-pin-line"></i>
                  <span>{withPrefix("Hall", selectedBooth.HallName || selectedBooth.HallID)}</span>
                  <span className="mx-1">·</span>
                  <span>{withPrefix("Zone", boothDetail.zone)}</span>
                </div>
                {/* Onsite Agency of the booth's hall; opens its team. */}
                {(() => {
                  const agencies = onsiteAgencyByHall.get(hallNameKey(selectedBooth.HallName)) || [];
                  const names = agencies.map((a) => a.company).join(", ");
                  return (
                    <div
                      role="button"
                      className="d-flex align-items-center gap-1 rounded-pill px-2 py-1 text-nowrap"
                      style={{ backgroundColor: '#ccfbf1', color: '#007062', fontSize: '11px', fontWeight: '500', cursor: 'pointer' }}
                      title={names ? `Onsite Agency: ${names}` : `No Onsite Agency mapped to ${selectedBooth.HallName || "this hall"}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTeamModal({ kind: "agency", hallName: selectedBooth.HallName, agencies });
                      }}
                    >
                      <span>Onsite Agency</span>
                      <i className="ri-information-line" style={{ fontSize: '13px' }}></i>
                    </div>
                  );
                })()}
              </div>
              <button
                type="button" style={{ fontSize: '10px' }}
                className="btn-close"
                aria-label="Close"
                onClick={() => setSelectedBooth(null)}
              ></button>
            </div>
            <div className="p-2 flex-grow-1 overflow-auto pb-3 pt-1">

              <div className="d-flex align-items-center gap-2 mb-2 p-1 bg-light rounded border">
                <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Status:
                </span>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    // The legend's swatch - Unallocated / No Record when dimmed.
                    backgroundColor: getStatusColor(
                      popupStatus.dimmed ? (isPossessionMode ? NO_POSSESSION_RECORD : 'Unallocated') : popupStatus.status
                    ),
                    border: '1px solid rgba(0, 0, 0, 0.15)'
                  }}
                  className="flex-shrink-0"
                />
                <span className={popupStatus.dimmed ? 'text-muted' : 'fw-semibold text-dark'} style={{ fontSize: '13px' }}>
                  {isPossessionMode
                    ? popupStatus.label
                    : popupStatus.dimmed
                      ? (boothDetail.vendors.length > 0 || boothDetail.orders.length > 0 ? 'Unallocated' : 'No tasks recorded')
                      : popupStatus.status || 'Unknown'}
                </span>
              </div>

              {/* Exhibitor Details Card */}
              {boothDetail.boothExhibitors.length > 0 && (
                <div className="card shadow-none border mb-2 p-1 pt-0" style={{ borderRadius: '4px' }}>
                  <div className="card-header bg-white p-1 border-bottom">
                    <span className="fw-bold" style={{ color: '#007062', fontSize: '11px', letterSpacing: '0.5px' }}>EXHIBITOR DETAILS</span>
                  </div>
                  <div className="card-body p-1">
                    {boothDetail.boothExhibitors.map((ex) => (
                      <div key={ex.id}>
                        <h6 className="fw-semibold text-dark mb-0 mt-1" style={{ fontSize: '13px' }}>{ex.company || "--"}</h6>
                        {ex.regNo && (
                          <div className="small text-muted mb-1"># {ex.regNo}</div>
                        )}
                        {(ex.stallType || ex.stallArea != null) && (
                          <div className="d-inline-flex align-items-center bg-light border rounded px-2 py-1 mb-2">
                            <i className="ri-layout-grid-line me-2 text-secondary"></i>
                            <span className="small">{ex.stallType || "Stall"}{ex.stallArea != null ? ` — ${ex.stallArea} sqm` : ""}</span>
                          </div>
                        )}

                        {(ex.mobile || ex.email) && (
                          <div className="row g-0 rounded p-0">
                            {ex.mobile && (
                              <div className="col-6">
                                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                                  <i className="ri-phone-line me-1"></i> Phone
                                </div>
                                <div className="small text-dark mt-0">{ex.mobile}</div>
                              </div>
                            )}
                            {ex.email && (
                              <div className="col-6">
                                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                                  <i className="ri-mail-line me-1"></i> Email
                                </div>
                                <div className="small text-dark mt-0 text-break">{ex.email}</div>
                              </div>
                            )}
                          </div>
                        )}
                        {renderNotifyButton({ name: ex.company, role: "Exhibitor", mobile: ex.mobile, email: ex.email })}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 2: Exhibitor Orders & Services (if isExhibitorOrdersActive) */}
              {isExhibitorOrdersActive && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-shopping-cart-line me-1 text-success"></i>Exhibitor Orders & Services
                      <span
                        role="button"
                        className="ms-1 text-primary"
                        style={{ cursor: "pointer", fontSize: "13px" }}
                        title={`Service partner team - ${selectedBooth.HallName || "this hall"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTeamModal({
                            hallName: selectedBooth.HallName,
                            team: teamByHall.get(hallNameKey(selectedBooth.HallName)) || [],
                          });
                        }}
                      >
                        <i className="ri-information-line"></i>
                      </span>
                    </span>
                    {boothDetail.orders.length > 0 && (
                      <span className="badge rounded-pill bg-success" style={{ fontSize: '10px' }}>
                        {boothDetail.orders.length}
                      </span>
                    )}
                  </div>

                  {boothDetail.orders.length === 0 ? (
                    <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                      No exhibitor orders recorded for {selectedBooth.BoothName}.
                    </div>
                  ) : (
                    boothDetail.orders.map((ord) => (
                      <div key={ord.id} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
                        <div className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="fw-semibold text-dark" style={{ fontSize: '12px' }}>
                              {ord.serviceName}
                              <span
                                role="button"
                                className="ms-1 text-primary"
                                style={{ cursor: "pointer", fontSize: "13px" }}
                                title={`Open ${ord.serviceName} Form`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenManualForm && onOpenManualForm(ord.serviceName, ord.exhRegistrationID, ord.exhibitorName);
                                }}
                              >
                                <i className="ri-information-line"></i>
                              </span>
                            </span>
                            <span
                              className="badge"
                              style={{ ...statusBadgeStyle(ord.status), fontSize: '10px' }}
                            >
                              {ord.status}
                            </span>
                          </div>

                          <div className="small text-muted" style={{ fontSize: '11px' }}>
                            <div><strong>Service Partner:</strong> {ord.vendorName}</div>
                            {ord.contactPerson && <div><strong>Contact:</strong> {ord.contactPerson}{ord.contactNo ? ` · ${ord.contactNo}` : ""}</div>}
                            {ord.possessionStatus && ord.possessionStatus !== "NA" && (
                              <div><strong>Possession:</strong> {ord.possessionStatus}</div>
                            )}
                            {ord.remark && <div><strong>Remark:</strong> {ord.remark}</div>}
                            {renderNotifyButton({ name: ord.contactPerson ? `${ord.contactPerson} (${ord.vendorName})` : ord.vendorName, role: `Service Partner - ${ord.serviceName}`, mobile: ord.contactNo, email: ord.email })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* SECTION 1: Service Partner Tasks (if isServicePartnerActive) */}
              {isServicePartnerActive && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-truck-line me-1 text-primary"></i>Service Partner Tasks
                    </span>
                    {boothDetail.vendors.length > 0 && (
                      <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>
                        {boothDetail.vendors.length}
                      </span>
                    )}
                  </div>

                  {isTaskLoading ? (
                    <div className="small text-muted p-2">
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>Loading...
                    </div>
                  ) : boothDetail.vendors.length === 0 ? (
                    <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                      No service partners recorded against {selectedBooth.BoothName}.
                    </div>
                  ) : (
                    boothDetail.vendors.map((v) => (
                      <div key={v.id} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
                        <div className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="fw-semibold text-dark mb-0" style={{ fontSize: '12.5px' }}>
                              {v.company || "--"}
                              {/* This row's tasks: the header Tasks popup, filtered to the
                                  booth's hall, this service partner and - when the row
                                  carries just one - its service. */}
                              {onOpenTaskDetail && (
                                <span
                                  role="button"
                                  className="ms-1 text-primary"
                                  style={{ cursor: "pointer", fontSize: "13px" }}
                                  title={`Tasks of ${v.company || "this service partner"} in ${selectedBooth.HallName || "this hall"}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenTaskDetail({
                                      hallName: selectedBooth.HallName || null,
                                      vendorCompany: v.company || null,
                                      serviceName: v.services.size === 1 ? Array.from(v.services)[0] : null,
                                    });
                                  }}
                                >
                                  <i className="ri-information-line"></i>
                                </span>
                              )}
                            </h6>
                            {v.overdue > 0 && (
                              <span className="badge flex-shrink-0" style={{ backgroundColor: '#fee2e2', color: '#ef4444', fontWeight: 600, fontSize: '9.5px' }}>
                                <i className="ri-error-warning-fill me-1"></i>{v.overdue} OVERDUE
                              </span>
                            )}
                          </div>

                          {v.services.size > 0 && (
                            <div className="mb-1">
                              {Array.from(v.services).map((svc) => (
                                <span key={svc} className="d-inline-flex align-items-center bg-light border rounded px-2 py-0.5 small me-1 mb-1" style={{ fontSize: '11px' }}>
                                  {svc}
                                  {v.serviceStatus.get(svc) && (
                                    <span className="badge ms-1" style={{ ...statusBadgeStyle(v.serviceStatus.get(svc)), fontSize: '9.5px' }}>
                                      {v.serviceStatus.get(svc)}
                                    </span>
                                  )}
                                  {(() => {
                                    const target = boothExhibitor || boothDetail.exhibitorByService.get(svc) || null;
                                    if (!target) return null;
                                    return (
                                      <span
                                        role="button"
                                        className="ms-1 text-primary"
                                        style={{ cursor: "pointer", fontSize: "13px", lineHeight: 1 }}
                                        title={`Open the ${svc} form for ${target.company || "this exhibitor"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onOpenManualForm && onOpenManualForm(svc, target.id, target.company);
                                        }}
                                      >
                                        <i className="ri-information-line"></i>
                                      </span>
                                    );
                                  })()}
                                </span>
                              ))}
                            </div>
                          )}

                          {(v.person || v.mobile || v.email) && (
                            <div className="small text-muted pt-1 border-top mt-1" style={{ fontSize: '11px' }}>
                              {v.person && <div><i className="ri-user-3-line me-1"></i>{v.person}</div>}
                              {v.mobile && <div><i className="ri-phone-line me-1"></i>{v.mobile}</div>}
                              {v.email && <div className="text-break"><i className="ri-mail-line me-1"></i>{v.email}</div>}
                              {renderNotifyButton({ name: v.person ? `${v.person} (${v.company || "Service Partner"})` : v.company, role: "Service Partner", mobile: v.mobile, email: v.email })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Service partner team for this booth's hall (result set 0's TeamMembers). */}
              {!isPossessionMode && (() => {
                const team = teamByHall.get(hallNameKey(selectedBooth.HallName)) || [];
                return (
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                      <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                        <i className="ri-team-line me-1 text-primary"></i>Service Partner Team
                      </span>
                      {team.length > 0 && (
                        <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>{team.length}</span>
                      )}
                    </div>
                    {renderTeamCards(team, `No team members mapped to ${selectedBooth.HallName || "this hall"}.`)}
                  </div>
                );
              })()}

              {/* SECTION 4: Stall Possession (single-select data filter) */}
              {isPossessionMode && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-key-2-line me-1 text-success"></i>Stall Possession
                    </span>
                    {boothDetail.possession.length > 0 && (
                      <span className="badge rounded-pill bg-success" style={{ fontSize: '10px' }}>
                        {boothDetail.possession.length}
                      </span>
                    )}
                  </div>

                  {boothDetail.possession.length === 0 ? (
                    <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                      No possession record for {selectedBooth.BoothName}.
                    </div>
                  ) : (
                    boothDetail.possession.map((ps) => (
                      <div key={ps.id} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
                        <div className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="fw-semibold text-dark" style={{ fontSize: '12px' }}>{ps.exhibitor}</span>
                            <span
                              className="badge"
                              style={{ ...statusBadgeStyle(ps.status), fontSize: '10px' }}
                            >
                              {ps.status}
                            </span>
                          </div>
                          <div className="small text-muted" style={{ fontSize: '11px' }}>
                            {ps.regNo && <div><strong>Reg No:</strong> {ps.regNo}</div>}
                            {ps.checklist && <div><strong>Pending checklist:</strong> {ps.checklist}</div>}
                            {ps.scheduledDate && (
                              <div><strong>Scheduled:</strong> {new Date(ps.scheduledDate).toLocaleDateString()}</div>
                            )}
                            {ps.issuedBy && <div><strong>NDC issued by:</strong> {ps.issuedBy}</div>}
                            {ps.takenBy && (
                              <div><strong>Taken by:</strong> {ps.takenBy}{ps.takenAt ? ` (${ps.takenAt})` : ""}</div>
                            )}
                            {ps.remark && <div><strong>Remark:</strong> {ps.remark}</div>}
                            {renderNotifyButton({ name: ps.contactName ? `${ps.contactName} (${ps.exhibitor})` : ps.exhibitor, role: "Exhibitor - Stall Possession", mobile: ps.mobile, email: ps.email })}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* SECTION 3: Daily Routine Task Summary (if isDailyRoutineActive) */}
              {isDailyRoutineActive && boothDetail.dailyRoutines.length > 0 && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-calendar-check-line me-1 text-info"></i>Hall Daily Routines ({boothDetail.dailyRoutines.length})
                    </span>
                  </div>
                  {boothDetail.dailyRoutines.map((dr, idx) => (
                    <div key={idx} className="card shadow-none border mb-1 p-2 bg-light" style={{ borderRadius: '4px', fontSize: '11px' }}>
                      <div className="d-flex justify-content-between">
                        <strong>
                          {dr.ProgramTitle}
                          {onOpenDailyRoutine && (
                            <span
                              role="button"
                              className="ms-1 text-primary fw-normal"
                              style={{ cursor: "pointer", fontSize: "13px" }}
                              title={`Update ${dr.ProgramTitle || "this routine"}`}
                              onClick={(e) => { e.stopPropagation(); onOpenDailyRoutine(dr); }}
                            >
                              <i className="ri-information-line"></i>
                            </span>
                          )}
                        </strong>
                        <span className="badge" style={statusBadgeStyle(dailyRoutineRowStatus(dr))}>
                          {dailyRoutineRowStatus(dr)}
                        </span>
                      </div>
                      <div className="text-muted mt-1">
                        <div>Time: {dr.ScheduleTimes}</div>
                        <div>Responsible: {dr.ResponsiblePerson}</div>
                        <div>Completed: {dr.TotalCompCount}/{dr.TotalCount}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>
      {/* Tooltip (DOM updated directly via ref) */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          display: "none",
          backgroundColor: "#1f2937",
          color: "white",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          pointerEvents: "none",
          zIndex: 1000,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          border: "1px solid #374151",
          maxWidth: "250px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          transform: "translateY(-100%)",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            ref={tooltipDotRef}
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
            }}
          />
          <span ref={tooltipContentRef}></span>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: "-5px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid #1f2937",
          }}
        />
      </div>

      {/* Info Section */}
      <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
        <div>
          <small className=" d-block">
            <strong>Layout Name:</strong> {layout.LayoutName}
          </small>
          <small className=" d-block">
            <strong>Halls in drawing:</strong> {halls.length}
            <span className="mx-2">|</span>
            <strong>Booths in drawing:</strong> {booths.length}
          </small>
        </div>

      </div>

      {children}
    </div>
  );
};

export default SvgCanvas;